import BemenuSettings, { omitBemenuMaintenanceCode } from './BemenuSettings';
import BefidAddonEntry from './BefidAddonEntry';
import { brandForLocation } from './productIdentity';
import { adminSettingsPatch, campaignAttempt, campaignComplete } from './loyaltyAdminRequests';
import WorkstationManager from './WorkstationManager';
import WorkstationSettings from './WorkstationSettings';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KioskExperienceSettings from './KioskExperienceSettings';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import AiAssistantWidget from './AiAssistantWidget';
import StripeConnectSettings, { stripeSetupAccountFromSearch } from './StripeConnectSettings';
import UberDirectSettings from './UberDirectSettings';
import StripeTerminalSettings from './StripeTerminalSettings';
import StripeAfterSalesSettings from './StripeAfterSalesSettings';
import { API_URL, buildKioskAuthHeaders, getDefaultBrandLogoUrl, resolveMediaUrl } from './api';
import { printDailySummary, printOrderTickets, printTestTicket } from './thermalPrinter';
const ADMIN_TAB_GROUPS = [
  { id: 'Exploitation', items: ['Imprimantes', 'Paiements', 'Historique'] },
  { id: 'Catalogue', items: ['Produits', 'Menus'] },
  { id: 'Matériel', items: ['BEGuest'] },
  { id: 'Clients', items: ['Fidelisation', 'Ecran fid'] },
  { id: 'Affichage', items: ['Affichage', 'Veille', 'Chatbot'] },
];
const PRODUCT_VISUAL_TAGS = [
  { key: 'isSpicy', label: 'Epicé' },
  { key: 'isVegetarian', label: 'Veggie' },
  { key: 'isHalal', label: 'Halal' },
  { key: 'isGlutenFree', label: 'Sans gluten' },
];
const PRODUCT_ALLERGENS = [
  { id: 'gluten', label: 'Céréales contenant du gluten' },
  { id: 'crustaceans', label: 'Crustacés' },
  { id: 'eggs', label: 'Œufs' },
  { id: 'fish', label: 'Poissons' },
  { id: 'peanuts', label: 'Arachides' },
  { id: 'soy', label: 'Soja' },
  { id: 'milk', label: 'Lait' },
  { id: 'nuts', label: 'Fruits à coque' },
  { id: 'celery', label: 'Céleri' },
  { id: 'mustard', label: 'Moutarde' },
  { id: 'sesame', label: 'Graines de sésame' },
  { id: 'sulphites', label: 'Anhydride sulfureux et sulfites' },
  { id: 'lupin', label: 'Lupin' },
  { id: 'molluscs', label: 'Mollusques' },
];

function readKioskAccountFeatures() {
  try {
    const account = JSON.parse(localStorage.getItem('kioskAccount') || '{}');
    return {
      caisseEnabled: !!account?.features?.caisseEnabled,
      borneEnabled: account?.features?.borneEnabled !== false,
      beguestEnabled: !!account?.features?.beguestEnabled,
    };
  } catch (error) {
    return { caisseEnabled: false, borneEnabled: true, beguestEnabled: false };
  }
}

const CAMPAIGN_RULE_FIELDS = [
  { value: 'totalOrders', label: 'Nombre de commandes' },
  { value: 'totalSpent', label: 'Volume d’achat (€)' },
  { value: 'points', label: 'Points fidélité' },
  { value: 'averageOrderValue', label: 'Panier moyen (€)' },
  { value: 'inactiveDays', label: 'Jours depuis dernière commande' },
  { value: 'singleOrder', label: 'Une seule commande' },
  { value: 'verified', label: 'Compte validé' },
  { value: 'hasEmail', label: 'Email renseigné' },
  { value: 'hasBirthDate', label: 'Date de naissance renseignée' },
];
const CAMPAIGN_NUMERIC_FIELDS = new Set([
  'totalOrders',
  'totalSpent',
  'points',
  'averageOrderValue',
  'inactiveDays',
]);
const WEEKLY_DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];
const PRINTER_TYPE_OPTIONS = [
  { value: 'epson', label: 'Epson (TM / ePOS)' },
  { value: 'star', label: 'Star (mC-Print / MCP31L)' },
  { value: 'generic', label: 'Generique ESC/POS' },
];

function resolvePrinterType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return PRINTER_TYPE_OPTIONS.some((option) => option.value === normalized) ? normalized : 'epson';
}

function inferPrinterTypeFromPrinter(printer = {}) {
  const haystack = [
    printer?.name,
    printer?.deviceId,
    printer?.networkHost,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .join(' ');

  if (haystack.includes('star') || haystack.includes('mc-print3') || haystack.includes('mcp31')) {
    return 'star';
  }
  if (haystack.includes('brother') || haystack.includes('ql-')) {
    return 'generic';
  }
  return 'epson';
}

function resolveDeviceIdFromPrinterType(printerType) {
  switch (resolvePrinterType(printerType)) {
    case 'star':
      return 'STAR_MCP31L';
    case 'generic':
      return 'ESC_POS_GENERIC';
    case 'epson':
    default:
      return 'XBVW019042';
  }
}

function resolveDefaultPort(connectionMode) {
  const mode = String(connectionMode || '').trim().toLowerCase();
  if (mode === 'epson_epos') {
    return 8008;
  }
  return 9100;
}
const FALLBACK_BRAND_LOGO_URL = getDefaultBrandLogoUrl({
  width: 420,
  height: 420,
  quality: 62,
  fit: 'contain',
});

function createDefaultCampaignRule() {
  return {
    id: Date.now() + Math.random(),
    mode: 'include',
    field: 'totalOrders',
    operator: 'gte',
    value: 1,
  };
}

function normalizeTimeString(value) {
  const raw = String(value ?? '').trim();
  return /^(\d{1,2}):(\d{2})$/.test(raw) ? raw : '';
}

function createUniformWeeklySchedule({
  openingTime = '',
  closingTime = '',
  breakStartTime = '',
  breakEndTime = '',
} = {}) {
  return WEEKLY_DAYS.reduce((acc, day) => {
    acc[day.key] = {
      enabled: true,
      openingTime: normalizeTimeString(openingTime),
      closingTime: normalizeTimeString(closingTime),
      breakStartTime: normalizeTimeString(breakStartTime),
      breakEndTime: normalizeTimeString(breakEndTime),
    };
    return acc;
  }, {});
}

function normalizeWeeklySchedule(rawSchedule, fallback = {}) {
  let parsed = rawSchedule;

  if (typeof rawSchedule === 'string') {
    const trimmed = rawSchedule.trim();
    if (!trimmed) {
      parsed = null;
    } else {
      try {
        parsed = JSON.parse(trimmed);
      } catch (error) {
        parsed = null;
      }
    }
  }

  const fallbackSchedule = createUniformWeeklySchedule({
    openingTime: fallback.openingTime,
    closingTime: fallback.closingTime,
    breakStartTime: fallback.breakStartTime,
    breakEndTime: fallback.breakEndTime,
  });

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return fallbackSchedule;
  }

  const normalized = {};
  WEEKLY_DAYS.forEach((day) => {
    const dayRaw = parsed[day.key] || {};
    const dayFallback = fallbackSchedule[day.key];
    normalized[day.key] = {
      enabled: dayRaw.enabled !== undefined ? !!dayRaw.enabled : !!dayFallback.enabled,
      openingTime: normalizeTimeString(dayRaw.openingTime ?? dayFallback.openingTime),
      closingTime: normalizeTimeString(dayRaw.closingTime ?? dayFallback.closingTime),
      breakStartTime: normalizeTimeString(dayRaw.breakStartTime ?? dayFallback.breakStartTime),
      breakEndTime: normalizeTimeString(dayRaw.breakEndTime ?? dayFallback.breakEndTime),
    };
  });

  return normalized;
}

function normalizeKdsScreens(value) {
  const fallback = {
    incoming: true,
    prep: true,
    ready: true,
    assembly: false,
    dispatch: false,
  };
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  return {
    incoming: value.incoming !== undefined ? !!value.incoming : fallback.incoming,
    prep: value.prep !== undefined ? !!value.prep : fallback.prep,
    ready: value.ready !== undefined ? !!value.ready : fallback.ready,
    assembly: value.assembly !== undefined ? !!value.assembly : fallback.assembly,
    dispatch: value.dispatch !== undefined ? !!value.dispatch : fallback.dispatch,
  };
}

function normalizeKdsScreenZoneMap(value) {
  const fallback = {
    incoming: '',
    prep: '',
    ready: '',
    assembly: '',
    dispatch: '',
  };
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  return {
    incoming: String(value.incoming || '').trim(),
    prep: String(value.prep || '').trim(),
    ready: String(value.ready || '').trim(),
    assembly: String(value.assembly || '').trim(),
    dispatch: String(value.dispatch || '').trim(),
  };
}

function formatDateTime(value) {
  return new Date(value).toLocaleString('fr-FR');
}

function formatPromoReward(promoCode) {
  if (!promoCode) {
    return '-';
  }

  if (promoCode.rewardType === 'discount_euro') {
    return `${Number(promoCode.rewardValue || 0).toFixed(2)} € de remise`;
  }

  if (promoCode.rewardType === 'discount_percent') {
    return `${Number(promoCode.rewardValue || 0).toFixed(0)} % de remise`;
  }

  if (promoCode.rewardType === 'product') {
    return `Produit offert: ${promoCode.Product?.name || promoCode.label || 'Produit'}`;
  }

  if (promoCode.rewardType === 'product_choice') {
    return promoCode.label || 'Produit offert au choix';
  }

  return promoCode.label || promoCode.rewardType || '-';
}

function downloadCsv(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function getAdminTabGroup(activeTab) {
  return ADMIN_TAB_GROUPS.find((group) => group.items.includes(activeTab)) || ADMIN_TAB_GROUPS[0];
}

function getAdminTabFromSearch(search) {
  const queryTab = new URLSearchParams(search || '').get('tab');
  const requestedTab = queryTab === 'payments' ? 'Paiements' : queryTab;
  return ADMIN_TAB_GROUPS.flatMap((group) => group.items).includes(requestedTab) ? requestedTab : '';
}

function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

function fetchWithAuth(url, options = {}) {
  const headers = buildKioskAuthHeaders(options.headers);
  return fetch(url, {
    ...options,
    headers,
  });
}

function ChatbotQrCard({ url, restaurantName }) {
  const canvasRef = useRef(null);
  const [qrError, setQrError] = useState('');

  useEffect(() => {
    let active = true;
    setQrError('');
    const canvas = canvasRef.current;
    if (!canvas || !url) {
      return undefined;
    }
    QRCode.toCanvas(canvas, url, {
      width: 240,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: {
        dark: '#0f3d3e',
        light: '#ffffff',
      },
    }).catch(() => {
      if (active) {
        setQrError('QR code impossible à générer.');
      }
    });
    return () => {
      active = false;
    };
  }, [url]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas || !url) {
      return;
    }
    const link = document.createElement('a');
    const safeName = String(restaurantName || 'restaurant')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'restaurant';
    link.download = `qr-chatbot-${safeName}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (!url) {
    return (
      <div className="admin-chatbot-qr-card is-disabled">
        <span>QR code indisponible</span>
        <p>Active le chatbot client dans MASTER pour obtenir l’URL publique.</p>
      </div>
    );
  }

  return (
    <div className="admin-chatbot-qr-card">
      <canvas ref={canvasRef} width="240" height="240" aria-label="QR code chatbot client" />
      {qrError ? <p className="admin-error-text">{qrError}</p> : null}
      <button type="button" className="admin-button admin-button-secondary" onClick={handleDownload}>
        Télécharger le QR
      </button>
    </div>
  );
}


function parseAvailabilityTime(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function isWithinAvailabilityWindow(record, now = new Date()) {
  if (!record) {
    return false;
  }

  if (record.isAvailable === false) {
    return false;
  }

  const fromMinutes = parseAvailabilityTime(record.availableFromTime);
  const toMinutes = parseAvailabilityTime(record.availableToTime);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  if (fromMinutes === null && toMinutes === null) {
    return true;
  }

  if (fromMinutes !== null && toMinutes === null) {
    return currentMinutes >= fromMinutes;
  }

  if (fromMinutes === null && toMinutes !== null) {
    return currentMinutes <= toMinutes;
  }

  if (fromMinutes === toMinutes) {
    return true;
  }

  if (fromMinutes < toMinutes) {
    return currentMinutes >= fromMinutes && currentMinutes <= toMinutes;
  }

  return currentMinutes >= fromMinutes || currentMinutes <= toMinutes;
}

function formatAvailabilityWindow(record) {
  const fromTime = String(record?.availableFromTime || '').trim();
  const toTime = String(record?.availableToTime || '').trim();

  if (!fromTime && !toTime) {
    return '';
  }

  if (fromTime && toTime) {
    return `${fromTime} - ${toTime}`;
  }

  return fromTime ? `dès ${fromTime}` : `jusqu'à ${toTime}`;
}

function getSectionRequiredQuantity(section) {
  return Math.max(1, parseInt(section?.MenuSections?.requiredQuantity || section?.requiredQuantity || 1, 10) || 1);
}

function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const stripeSetupAccount = stripeSetupAccountFromSearch(location.search);
  const [activeTab, setActiveTab] = useState(() => getAdminTabFromSearch(location.search) || 'Imprimantes');
  const [accountFeatures, setAccountFeatures] = useState(() => readKioskAccountFeatures());
  const [sections, setSections] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [supplementCategories, setSupplementCategories] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [products, setProducts] = useState([]);
  const [menus, setMenus] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [productionZones, setProductionZones] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPromoSource, setCustomerPromoSource] = useState('signup');
  const [customerPromoLoading, setCustomerPromoLoading] = useState(false);
  const [customerFilters, setCustomerFilters] = useState({
    search: '',
    verified: 'all',
    orderProfile: 'all',
    minOrders: '',
    maxOrders: '',
    minSpent: '',
    maxSpent: '',
    codeStatus: 'all',
  });
  const [customerInlineFormData, setCustomerInlineFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    email: '',
  });
  const [customerDirectMessage, setCustomerDirectMessage] = useState(
    'Bonjour {firstname}, merci pour ta fidélité chez nous.'
  );
  const [customerDirectMessageSending, setCustomerDirectMessageSending] = useState(false);
  const [customerDeleteLoading, setCustomerDeleteLoading] = useState(false);
  const [customerManualPromoLoading, setCustomerManualPromoLoading] = useState(false);
  const [customerManualPromo, setCustomerManualPromo] = useState({
    rewardType: 'discount_euro',
    rewardValue: 5,
    productId: '',
    sectionId: '',
    subcategoryId: '',
    label: 'Offre personnalisée',
    expiresDays: 30,
    sendSms: true,
    message: 'Ton code promo est {code}.',
  });
  const [campaignCriteria, setCampaignCriteria] = useState({
    joinOperator: 'and',
    rules: [createDefaultCampaignRule()],
  });
  const [campaignPreview, setCampaignPreview] = useState({
    totalCustomers: 0,
    targetedCustomers: 0,
    sample: [],
  });
  const [campaignMessage, setCampaignMessage] = useState(
    'Bonjour {firstname}, profitez de notre offre du moment avec le code {code}.'
  );
  const [campaignReward, setCampaignReward] = useState({
    enabled: false,
    rewardType: 'discount_euro',
    rewardValue: 5,
    targetType: 'product',
    productId: '',
    sectionId: '',
    subcategoryId: '',
    expiresDays: 30,
    label: 'Offre campagne SMS',
  });
  const [campaignPreviewLoading, setCampaignPreviewLoading] = useState(false);
  const [campaignSendLoading, setCampaignSendLoading] = useState(false);
  const campaignRequestRef = useRef(null);
  const campaignBusyRef = useRef(false);
  const printerBaselineRef = useRef(null);
  const [loyaltyConflict, setLoyaltyConflict] = useState(false);
  const acceptPrinterSettings = useCallback(value => {
    printerBaselineRef.current = value;
    setPrinterSettings(value);
    setLoyaltyConflict(false);
  }, []);
  const [printerSettings, setPrinterSettings] = useState({
    dailySummaryPrinterId: '',
    clientTicketZoneId: '',
    clientTicketCopies: 1,
    dailySummaryTime: '',
    lastDailySummaryPrintDate: '',
    weeklySchedule: createUniformWeeklySchedule({}),
    kdsScreenMode: 'single',
    kdsEnabledScreensJson: normalizeKdsScreens(null),
    kdsScreenZoneMapJson: normalizeKdsScreenZoneMap(null),
    kdsWarnThresholdSeconds: 180,
    kdsLateThresholdSeconds: 600,
    kdsSoundEnabled: true,
    fidReviewUrl: 'https://beresto.fr',
    fidPrinterId: '',
    fidLogoMedia: '',
    fidTicketQrUrl: '',
    fidDownloadQrText: 'Telecharge ta photo sous 24h ici',
    customerChatbotName: 'Assistant menu',
    customerChatbotLogoMedia: '',
    customerChatbotWelcomeMessage: 'Bonjour, je peux vous conseiller sur la carte, les recettes et les allergènes.',
  });
  const [sectionFormData, setSectionFormData] = useState({
    name: '',
    emoji: '',
    image: '',
    displayOrder: '0',
  });
  const [editingSection, setEditingSection] = useState(null);
  const [printerFormData, setPrinterFormData] = useState({
    connectionMode: 'epson_epos',
    printerType: 'epson',
    name: '',
    printerIp: '',
    networkHost: '',
    port: '8008',
    deviceId: 'XBVW019042',
    bridgeId: '',
    bridgeMacAddress: '',
    bridgeBrokerHost: '212.227.212.196',
    bridgeBrokerPort: '8883',
    bridgeUsername: '',
    bridgePassword: '',
    bridgeTimeoutMs: '15000',
    isMain: true,
  });
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [printerDiscoveryLoading, setPrinterDiscoveryLoading] = useState(false);
  const [printerDiscoveryError, setPrinterDiscoveryError] = useState('');
  const [printerDiscoveryResults, setPrinterDiscoveryResults] = useState([]);
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    sectionId: '',
    image: '',
    displayOrder: '0',
  });
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [productionZoneFormData, setProductionZoneFormData] = useState({
    name: '',
    printerId: '',
    ticketCopies: 1,
  });
  const [editingProductionZone, setEditingProductionZone] = useState(null);
  const [supplementCategoryFormData, setSupplementCategoryFormData] = useState({ name: '' });
  const [supplementFormData, setSupplementFormData] = useState({
    name: '',
    price: '',
    dineInPrice: '',
    image: '',
    supplementCategoryId: '',
  });
  const [editingSupplement, setEditingSupplement] = useState(null);
  const [supplementEditFormData, setSupplementEditFormData] = useState({
    name: '',
    price: '',
    image: '',
    supplementCategoryId: '',
  });
  const [selectedSupplementIdsForAutoImages, setSelectedSupplementIdsForAutoImages] = useState([]);
  const [productFormData, setProductFormData] = useState({
    name: '',
    kitchenName: '',
    price: '',
    taxRate: '10',
    sectionId: '',
    subcategoryId: '',
    productionZoneId: '',
    isDealOfDay: false,
    availableFromTime: '',
    availableToTime: '',
    supplementIds: [],
    image: '',
    description: '',
    isAvailable: true,
    isCaisseAvailable: true,
    isBorneAvailable: true,
    isSpicy: false,
    isVegetarian: false,
    isHalal: false,
    isGlutenFree: false,
    allergens: [],
  });
  const [productEditFormData, setProductEditFormData] = useState({
    name: '',
    kitchenName: '',
    price: '',
    dineInPrice: '',
    taxRate: '10',
    sectionId: '',
    subcategoryId: '',
    productionZoneId: '',
    isDealOfDay: false,
    availableFromTime: '',
    availableToTime: '',
    supplementIds: [],
    image: '',
    description: '',
    isAvailable: true,
    isCaisseAvailable: true,
    isBorneAvailable: true,
    isSpicy: false,
    isVegetarian: false,
    isHalal: false,
    isGlutenFree: false,
    allergens: [],
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [productEditAiInstruction, setProductEditAiInstruction] = useState('');
  const [selectedProductIdsForAutoImages, setSelectedProductIdsForAutoImages] = useState([]);
  const [productCatalogFilters, setProductCatalogFilters] = useState({
    sectionId: '',
    subcategoryId: '',
    availability: 'all',
  });
  const [productAvailabilityUpdating, setProductAvailabilityUpdating] = useState(false);
  const [productDealUpdatingId, setProductDealUpdatingId] = useState('');
  const [productInlinePriceDrafts, setProductInlinePriceDrafts] = useState({});
  const [productInlineDineInPriceDrafts, setProductInlineDineInPriceDrafts] = useState({});
  const [productInlineTaxRateDrafts, setProductInlineTaxRateDrafts] = useState({});
  const [productInlineZoneDrafts, setProductInlineZoneDrafts] = useState({});
  const [productPriceUpdatingId, setProductPriceUpdatingId] = useState('');
  const [productSalesOptionsUpdatingId, setProductSalesOptionsUpdatingId] = useState('');
  const [productZoneUpdatingId, setProductZoneUpdatingId] = useState('');
  const [menuFormData, setMenuFormData] = useState({
    name: '',
    price: '',
    description: '',
    image: '',
    availableFromTime: '',
    availableToTime: '',
    selectedSections: [],
    selectedProducts: [],
  });
  const [editingMenu, setEditingMenu] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    dateFrom: '',
    dateTo: '',
    productId: '',
    search: '',
  });
  const [dailySummary, setDailySummary] = useState(null);
  const [productImageUploading, setProductImageUploading] = useState(false);
  const [productEditImageUploading, setProductEditImageUploading] = useState(false);
  const [productAutoImageLoading, setProductAutoImageLoading] = useState(false);
  const [sectionImageUploading, setSectionImageUploading] = useState(false);
  const [subcategoryImageUploading, setSubcategoryImageUploading] = useState(false);
  const [supplementImageUploading, setSupplementImageUploading] = useState(false);
  const [supplementEditImageUploading, setSupplementEditImageUploading] = useState(false);
  const [supplementAutoImageLoading, setSupplementAutoImageLoading] = useState(false);
  const [menuImageUploading, setMenuImageUploading] = useState(false);
  const [attractUploading, setAttractUploading] = useState(false);
  const [clientBackgroundUploading, setClientBackgroundUploading] = useState(false);
  const [ticketLogoUploading, setTicketLogoUploading] = useState(false);
  const [ticketPreviewPrinting, setTicketPreviewPrinting] = useState(false);
  const [displayLogoUploading, setDisplayLogoUploading] = useState(false);
  const [fidLogoUploading, setFidLogoUploading] = useState(false);
  const [customerChatbotLogoUploading, setCustomerChatbotLogoUploading] = useState(false);
  const [uberImporting, setUberImporting] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [uberImportFormData, setUberImportFormData] = useState({
    url: '',
  });
  const [productAutoImageInstruction, setProductAutoImageInstruction] = useState('');
  const productCardRefs = useRef({});
  const productSelectAllRef = useRef(null);
  const supplementCardRefs = useRef({});
  const supplementSelectAllRef = useRef(null);

  useEffect(() => {
    const requestedTab = getAdminTabFromSearch(location.search);
    if (requestedTab) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  useEffect(() => {
    let active = true;

    const verifyAccess = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        if (active) {
          setAuthChecking(false);
        }
        navigate('/admin', { replace: true, state: { from: { pathname: location.pathname, search: location.search, hash: location.hash } } });
        return;
      }

      try {
        const response = await fetchWithAuth(`${API_URL}/api/admin/auto-login`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const payload = response.ok ? await response.json() : null;
        if (!active) {
          return;
        }
        if (response.status === 401 || response.status === 403 || !payload?.success) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('kioskAccount');
          if (active) {
            setAuthChecking(false);
          }
          navigate('/admin', { replace: true, state: { from: { pathname: location.pathname, search: location.search, hash: location.hash } } });
          return;
        }
        if (payload?.token) {
          localStorage.setItem('authToken', payload.token);
        }
        if (payload?.account) {
          localStorage.setItem('kioskAccount', JSON.stringify(omitBemenuMaintenanceCode(payload.account)));
          setAccountFeatures(readKioskAccountFeatures());
        }
      } catch (error) {
        if (!active) {
          return;
        }
        // Do not force logout on transient failures.
        setAuthChecking(false);
      }

      if (active) {
        setAuthChecking(false);
      }
    };

    verifyAccess();
    return () => {
      active = false;
    };
  }, [navigate, location.pathname, location.search, location.hash]);

  const fetchCoreData = useCallback(async () => {
    const [
      sectionsRes,
      subcategoriesRes,
      supplementCategoriesRes,
      supplementsRes,
      productsRes,
      menusRes,
      printersRes,
      productionZonesRes,
      printerSettingsRes,
    ] = await Promise.all([
      fetchWithAuth(`${API_URL}/api/sections`),
      fetchWithAuth(`${API_URL}/api/subcategories`),
      fetchWithAuth(`${API_URL}/api/supplement-categories`),
      fetchWithAuth(`${API_URL}/api/supplements`),
      fetchWithAuth(`${API_URL}/api/products`),
      fetchWithAuth(`${API_URL}/api/menus`),
      fetchWithAuth(`${API_URL}/api/printers`),
      fetchWithAuth(`${API_URL}/api/production-zones`),
      fetchWithAuth(`${API_URL}/api/printer-settings`),
    ]);

    setSections(ensureArray(await sectionsRes.json()).map((section) => ({
      ...section,
      image: resolveMediaUrl(section.image),
    })));
    setSubcategories(ensureArray(await subcategoriesRes.json()).map((subcategory) => ({
      ...subcategory,
      image: resolveMediaUrl(subcategory.image),
    })));
    setSupplementCategories(ensureArray(await supplementCategoriesRes.json()));
    setSupplements(ensureArray(await supplementsRes.json()).map((supplement) => ({
      ...supplement,
      image: resolveMediaUrl(supplement.image),
    })));
    setProducts(ensureArray(await productsRes.json()).map((product) => ({
      ...product,
      image: resolveMediaUrl(product.image),
      Supplements: Array.isArray(product.Supplements)
        ? product.Supplements.map((supplement) => ({
            ...supplement,
            image: resolveMediaUrl(supplement.image),
          }))
        : [],
    })));
    setMenus(ensureArray(await menusRes.json()).map((menu) => ({
      ...menu,
      image: resolveMediaUrl(menu.image),
    })));
    const printersData = await printersRes.json();
    setPrinters(ensureArray(printersData));
    setProductionZones(ensureArray(await productionZonesRes.json()));
    const printerSettingsData = await printerSettingsRes.json();
    const normalizedWeeklySchedule = normalizeWeeklySchedule(printerSettingsData.weeklySchedule, printerSettingsData);
    acceptPrinterSettings({
      ...printerSettingsData,
      weeklySchedule: normalizedWeeklySchedule,
      kdsScreenMode: ['single', 'dual', 'full'].includes(String(printerSettingsData.kdsScreenMode || '').trim())
        ? String(printerSettingsData.kdsScreenMode).trim()
        : 'single',
      kdsEnabledScreensJson: normalizeKdsScreens(printerSettingsData.kdsEnabledScreensJson),
      kdsScreenZoneMapJson: normalizeKdsScreenZoneMap(printerSettingsData.kdsScreenZoneMapJson),
      kdsWarnThresholdSeconds: Math.max(30, Number(printerSettingsData.kdsWarnThresholdSeconds || 180)),
      kdsLateThresholdSeconds: Math.max(60, Number(printerSettingsData.kdsLateThresholdSeconds || 600)),
      kdsSoundEnabled: printerSettingsData.kdsSoundEnabled !== false,
      attractBackgroundMedia: resolveMediaUrl(printerSettingsData.attractBackgroundMedia),
      closedAttractBackgroundMedia: resolveMediaUrl(printerSettingsData.closedAttractBackgroundMedia),
      clientBackgroundMedia: resolveMediaUrl(printerSettingsData.clientBackgroundMedia),
      displayLogoMedia: resolveMediaUrl(printerSettingsData.displayLogoMedia),
      ticketLogoMedia: resolveMediaUrl(printerSettingsData.ticketLogoMedia),
      fidLogoMedia: resolveMediaUrl(printerSettingsData.fidLogoMedia),
      customerChatbotName: printerSettingsData.customerChatbotName || 'Assistant menu',
      customerChatbotLogoMedia: resolveMediaUrl(printerSettingsData.customerChatbotLogoMedia),
      customerChatbotWelcomeMessage:
        printerSettingsData.customerChatbotWelcomeMessage ||
        'Bonjour, je peux vous conseiller sur la carte, les recettes et les allergènes.',
      uiSurfaceColorHex:
        typeof printerSettingsData.uiSurfaceColor === 'string' &&
        printerSettingsData.uiSurfaceColor.startsWith('#')
          ? printerSettingsData.uiSurfaceColor
          : '#fffaf3',
    });
  }, [acceptPrinterSettings]);

  const fetchOrders = useCallback(async () => {
    const searchParams = new URLSearchParams();
    Object.entries(historyFilters).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      }
    });
    const response = await fetchWithAuth(`${API_URL}/api/orders?${searchParams.toString()}`);
    setOrders(await response.json());
  }, [historyFilters]);

  const fetchCustomers = useCallback(async () => {
    const searchParams = new URLSearchParams();
    if (customerFilters.search) {
      searchParams.set('search', customerFilters.search);
    }
    searchParams.set('includePromoStats', '1');

    const response = await fetchWithAuth(`${API_URL}/api/customers?${searchParams.toString()}`);
    const payload = ensureArray(await response.json());
    setCustomers(payload);
    setSelectedCustomer((current) => {
      if (!payload.length) {
        return null;
      }

      if (!current?.id) {
        return null;
      }

      return payload.find((customer) => customer.id === current.id) || null;
    });
  }, [customerFilters.search]);

  const fetchCustomerDetails = useCallback(async (customerId) => {
    const response = await fetchWithAuth(`${API_URL}/api/customers/${customerId}`);
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || 'Impossible de charger le client.');
    }
    setSelectedCustomer(payload);
  }, []);

  const handleGenerateCustomerPromo = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    try {
      setCustomerPromoLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}/promo-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: customerPromoSource,
          sendSms: false,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de générer le code cadeau.');
      }
      window.alert(`Code généré: ${payload.code}`);
      await fetchCustomerDetails(selectedCustomer.id);
    } catch (error) {
      window.alert(error.message);
    } finally {
      setCustomerPromoLoading(false);
    }
  };

  const handleSendCustomerValidation = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}/send-validation`, {
        method: 'POST',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Envoi du lien impossible.');
      }

      window.alert(`Lien de validation envoyé.\n${payload.validationLink}`);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleSelectCustomer = async (customerId) => {
    try {
      const previewCustomer = customers.find((customer) => String(customer.id) === String(customerId));
      if (previewCustomer) {
        setSelectedCustomer(previewCustomer);
      }
      await fetchCustomerDetails(customerId);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleCustomerInlineSave = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerInlineFormData),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour client impossible.');
      }

      await fetchCustomers();
      await fetchCustomerDetails(selectedCustomer.id);
      window.alert('Compte client mis à jour.');
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleCustomerDelete = async () => {
    if (!selectedCustomer?.id) {
      return;
    }
    if (!window.confirm('Supprimer ce compte client ? Cette action est irréversible.')) {
      return;
    }

    try {
      setCustomerDeleteLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}`, {
        method: 'DELETE',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Suppression du client impossible.');
      }
      await fetchCustomers();
      setSelectedCustomer(null);
      window.alert('Compte client supprimé.');
    } catch (error) {
      window.alert(error.message);
    } finally {
      setCustomerDeleteLoading(false);
    }
  };

  const handleSendCustomerMessage = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    try {
      setCustomerDirectMessageSending(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}/send-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: customerDirectMessage }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Envoi du SMS impossible.');
      }
      window.alert('SMS envoyé.');
    } catch (error) {
      window.alert(error.message);
    } finally {
      setCustomerDirectMessageSending(false);
    }
  };

  const handleCreateCustomerManualPromo = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    try {
      setCustomerManualPromoLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/${selectedCustomer.id}/promo-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'manual',
          ...customerManualPromo,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Création du code promo impossible.');
      }

      await fetchCustomerDetails(selectedCustomer.id);
      await fetchCustomers();
      window.alert(`Code créé: ${payload.code}`);
    } catch (error) {
      window.alert(error.message);
    } finally {
      setCustomerManualPromoLoading(false);
    }
  };

  const handleCampaignRuleChange = (ruleId, updates) => {
    setCampaignCriteria((current) => ({
      ...current,
      rules: current.rules.map((rule) => (rule.id === ruleId ? { ...rule, ...updates } : rule)),
    }));
  };

  const handleCampaignAddRule = () => {
    setCampaignCriteria((current) => ({
      ...current,
      rules: [...current.rules, createDefaultCampaignRule()],
    }));
  };

  const handleCampaignRemoveRule = (ruleId) => {
    setCampaignCriteria((current) => {
      const nextRules = current.rules.filter((rule) => rule.id !== ruleId);
      return {
        ...current,
        rules: nextRules.length ? nextRules : [createDefaultCampaignRule()],
      };
    });
  };

  const handleCampaignPreview = async () => {
    try {
      setCampaignPreviewLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/sms-campaign/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          criteria: campaignCriteria,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de calculer le ciblage SMS.');
      }
      setCampaignPreview(payload);
    } catch (error) {
      window.alert(error.message);
    } finally {
      setCampaignPreviewLoading(false);
    }
  };

  const handleCampaignSend = async () => {
    if (campaignBusyRef.current) return;
    campaignBusyRef.current = true;
    try {
      const content = { criteria: campaignCriteria, message: campaignMessage, reward: campaignReward };
      campaignRequestRef.current = campaignAttempt(campaignRequestRef.current, content);
      setCampaignSendLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/customers/sms-campaign/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...content,
          requestId: campaignRequestRef.current.id,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Envoi de campagne SMS impossible.');
      }

      setCampaignPreview((current) => ({
        ...current,
        targetedCustomers: payload.totalTargeted,
      }));

      const complete = campaignComplete(payload);
      if (complete) campaignRequestRef.current = null;
      window.alert(
        `${complete ? 'Campagne terminée.' : 'Résultat partiel ou incertain. Réessayer reprend la même campagne.'}\nEn attente: ${payload.totalPending || 0}\nIncertains: ${payload.totalUncertain || 0}\nCiblés: ${payload.totalTargeted}\nEnvoyés: ${payload.totalSent}\nÉchecs: ${payload.totalFailed}\nCodes générés: ${payload.totalPromoCodesGenerated}`
      );
    } catch (error) {
      window.alert(error.message);
    } finally {
      campaignBusyRef.current = false;
      setCampaignSendLoading(false);
    }
  };

  const fetchDailySummary = useCallback(async (date) => {
    const response = await fetchWithAuth(`${API_URL}/api/reports/daily-summary?date=${date || ''}`);
    const summary = await response.json();
    setDailySummary(summary);
    return summary;
  }, []);

  useEffect(() => {
    fetchCoreData();
    fetchOrders();
    fetchCustomers();
    fetchDailySummary();
  }, [fetchCoreData, fetchOrders, fetchCustomers, fetchDailySummary]);

  useEffect(() => {
    setSelectedSupplementIdsForAutoImages((current) =>
      current.filter((supplementId) =>
        supplements.some((supplement) => String(supplement.id) === String(supplementId))
      )
    );
  }, [supplements]);

  useEffect(() => {
    setSelectedProductIdsForAutoImages((current) =>
      current.filter((productId) => products.some((product) => String(product.id) === String(productId)))
    );
  }, [products]);

  useEffect(() => {
    if (!supplementSelectAllRef.current) {
      return;
    }

    supplementSelectAllRef.current.indeterminate =
      selectedSupplementIdsForAutoImages.length > 0 &&
      selectedSupplementIdsForAutoImages.length < supplements.length;
  }, [selectedSupplementIdsForAutoImages, supplements.length]);

  useEffect(() => {
    if (!editingSupplement?.id) {
      return;
    }

    const supplementElement = supplementCardRefs.current[editingSupplement.id];
    if (!supplementElement) {
      return;
    }

    window.requestAnimationFrame(() => {
      supplementElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [editingSupplement?.id]);

  useEffect(() => {
    if (!editingSupplement?.id) {
      return;
    }

    const refreshedSupplement = supplements.find(
      (supplement) => String(supplement.id) === String(editingSupplement.id)
    );

    if (!refreshedSupplement) {
      return;
    }

    setEditingSupplement(refreshedSupplement);
    setSupplementEditFormData((current) => ({
      ...current,
      image: refreshedSupplement.image || current.image,
    }));
  }, [editingSupplement?.id, supplements]);

  useEffect(() => {
    if (!productSelectAllRef.current) {
      return;
    }

    const filteredIds = products
      .filter((product) => {
        if (
          productCatalogFilters.sectionId
          && String(product.sectionId) !== String(productCatalogFilters.sectionId)
        ) {
          return false;
        }

        if (
          productCatalogFilters.subcategoryId
          && String(product.subcategoryId || '') !== String(productCatalogFilters.subcategoryId)
        ) {
          return false;
        }

        return true;
      })
      .map((product) => String(product.id));
    const selectedInFilter = selectedProductIdsForAutoImages.filter((id) =>
      filteredIds.includes(String(id))
    );

    productSelectAllRef.current.indeterminate =
      selectedInFilter.length > 0 &&
      selectedInFilter.length < filteredIds.length;
  }, [selectedProductIdsForAutoImages, products, productCatalogFilters.sectionId, productCatalogFilters.subcategoryId]);

  useEffect(() => {
    if (!editingProduct?.id) {
      return;
    }

    const productElement = productCardRefs.current[editingProduct.id];
    if (!productElement) {
      return;
    }

    window.requestAnimationFrame(() => {
      productElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [editingProduct?.id]);

  useEffect(() => {
    if (!editingProduct?.id) {
      return;
    }

    const refreshedProduct = products.find((product) => String(product.id) === String(editingProduct.id));

    if (!refreshedProduct) {
      return;
    }

    setEditingProduct(refreshedProduct);
    setProductEditFormData((current) => ({
      ...current,
      image: refreshedProduct.image || current.image,
    }));
  }, [editingProduct?.id, products]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!selectedCustomer) {
      return;
    }

    setCustomerInlineFormData({
      firstName: selectedCustomer.firstName || '',
      lastName: selectedCustomer.lastName || '',
      birthDate: selectedCustomer.birthDate || '',
      email: selectedCustomer.email || '',
    });
    setCustomerManualPromo((current) => ({
      ...current,
      productId: '',
      sectionId: '',
      subcategoryId: '',
    }));
  }, [selectedCustomer]);

  useEffect(() => {
    if (activeTab === 'Fidelisation') {
      handleCampaignPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const intervalId = window.setInterval(async () => {
      const currentTime = new Date();
      const hh = String(currentTime.getHours()).padStart(2, '0');
      const mm = String(currentTime.getMinutes()).padStart(2, '0');
      const today = currentTime.toISOString().slice(0, 10);
      const scheduledTime = printerSettings.dailySummaryTime;
      const targetPrinter = printers.find(
        (printer) => String(printer.id) === String(printerSettings.dailySummaryPrinterId)
      );

      if (!scheduledTime || !targetPrinter) {
        return;
      }

      if (`${hh}:${mm}` !== scheduledTime) {
        return;
      }

      if (printerSettings.lastDailySummaryPrintDate === today) {
        return;
      }

      try {
        const summary = await fetchDailySummary(today);
        await printDailySummary(targetPrinter, summary);
        const response = await fetchWithAuth(`${API_URL}/api/printer-settings/mark-daily-summary-printed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: today }),
        });
        const payload = await response.json();
        setPrinterSettings((prev) => ({
          ...prev,
          lastDailySummaryPrintDate: payload.lastDailySummaryPrintDate,
        }));
      } catch (error) {
        console.error('Erreur impression automatique du récap:', error);
      }
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [printerSettings, printers, fetchDailySummary]);

  const availableSubcategories = useMemo(
    () =>
      subcategories.filter(
        (subcategory) => String(subcategory.sectionId) === String(productFormData.sectionId)
      ),
    [subcategories, productFormData.sectionId]
  );

  const subcategoryProductCounts = useMemo(() => {
    const counts = new Map();
    products.forEach((product) => {
      const subcategoryId = String(product.subcategoryId || '').trim();
      if (!subcategoryId) {
        return;
      }
      counts.set(subcategoryId, (counts.get(subcategoryId) || 0) + 1);
    });
    return counts;
  }, [products]);

  const availableEditingSubcategories = useMemo(
    () =>
      subcategories.filter(
        (subcategory) => String(subcategory.sectionId) === String(productEditFormData.sectionId)
      ),
    [subcategories, productEditFormData.sectionId]
  );

  const productCatalogSubcategories = useMemo(
    () =>
      productCatalogFilters.sectionId
        ? subcategories.filter(
            (subcategory) => String(subcategory.sectionId) === String(productCatalogFilters.sectionId)
          )
        : subcategories,
    [subcategories, productCatalogFilters.sectionId]
  );

  const filteredCatalogProducts = useMemo(
    () =>
      products.filter((product) => {
        if (
          productCatalogFilters.sectionId
          && String(product.sectionId) !== String(productCatalogFilters.sectionId)
        ) {
          return false;
        }

        if (
          productCatalogFilters.subcategoryId
          && String(product.subcategoryId || '') !== String(productCatalogFilters.subcategoryId)
        ) {
          return false;
        }

        if (productCatalogFilters.availability === 'available' && !isWithinAvailabilityWindow(product)) {
          return false;
        }

        if (productCatalogFilters.availability === 'unavailable' && isWithinAvailabilityWindow(product)) {
          return false;
        }

        return true;
      }),
    [
      products,
      productCatalogFilters.sectionId,
      productCatalogFilters.subcategoryId,
      productCatalogFilters.availability,
    ]
  );

  const filteredCustomers = useMemo(
    () =>
      customers.filter((customer) => {
        const normalizedSearch = String(customerFilters.search || '').trim().toLowerCase();
        if (normalizedSearch) {
          const text = [
            customer.phone || '',
            customer.firstName || '',
            customer.lastName || '',
            customer.email || '',
          ]
            .join(' ')
            .toLowerCase();
          if (!text.includes(normalizedSearch)) {
            return false;
          }
        }

        if (customerFilters.verified === 'verified' && !customer.loyaltyVerifiedAt) {
          return false;
        }
        if (customerFilters.verified === 'unverified' && customer.loyaltyVerifiedAt) {
          return false;
        }

        const totalOrders = Number(customer.totalOrders || 0);
        const totalSpent = Number(customer.totalSpent || 0);
        if (customerFilters.orderProfile === 'single' && totalOrders !== 1) {
          return false;
        }
        if (customerFilters.orderProfile === 'multiple' && totalOrders <= 1) {
          return false;
        }
        if (customerFilters.minOrders !== '' && totalOrders < Number(customerFilters.minOrders || 0)) {
          return false;
        }
        if (customerFilters.maxOrders !== '' && totalOrders > Number(customerFilters.maxOrders || 0)) {
          return false;
        }
        if (customerFilters.minSpent !== '' && totalSpent < Number(customerFilters.minSpent || 0)) {
          return false;
        }
        if (customerFilters.maxSpent !== '' && totalSpent > Number(customerFilters.maxSpent || 0)) {
          return false;
        }

        const unusedCodes = Number(customer.promoCodesUnused || 0);
        if (customerFilters.codeStatus === 'has_unused' && unusedCodes <= 0) {
          return false;
        }
        if (customerFilters.codeStatus === 'without_unused' && unusedCodes > 0) {
          return false;
        }

        return true;
      }),
    [customers, customerFilters]
  );

  const customerManualPromoSubcategories = useMemo(
    () =>
      customerManualPromo.sectionId
        ? subcategories.filter((subcategory) => String(subcategory.sectionId) === String(customerManualPromo.sectionId))
        : subcategories,
    [subcategories, customerManualPromo.sectionId]
  );

  const getDefaultProductionZoneId = useCallback(() => {
    const mainZone = productionZones.find((zone) => zone.Printer?.isMain) || productionZones[0];
    return mainZone ? String(mainZone.id) : '';
  }, [productionZones]);

  const resetPrinterForm = () => {
    setPrinterFormData({
      connectionMode: 'epson_epos',
      printerType: 'epson',
      name: '',
      printerIp: '',
      networkHost: '',
      port: '8008',
      deviceId: 'XBVW019042',
      bridgeId: '',
      bridgeMacAddress: '',
      bridgeBrokerHost: '212.227.212.196',
      bridgeBrokerPort: '8883',
      isMain: printers.length === 0,
    });
    setEditingPrinter(null);
  };

  const resetProductionZoneForm = () => {
    setProductionZoneFormData({
      name: '',
      printerId: '',
      ticketCopies: 1,
    });
    setEditingProductionZone(null);
  };

  const resetProductForm = () => {
    setProductFormData({
      name: '',
      kitchenName: '',
      price: '',
      dineInPrice: '',
      taxRate: '10',
      sectionId: '',
      subcategoryId: '',
      productionZoneId: getDefaultProductionZoneId(),
      isDealOfDay: false,
      availableFromTime: '',
      availableToTime: '',
      supplementIds: [],
      image: '',
      description: '',
      isAvailable: true,
      isCaisseAvailable: true,
      isBorneAvailable: true,
      isSpicy: false,
      isVegetarian: false,
      isHalal: false,
      isGlutenFree: false,
      allergens: [],
    });
  };

  const resetProductEditForm = () => {
    setProductEditFormData({
      name: '',
      kitchenName: '',
      price: '',
      dineInPrice: '',
      taxRate: '10',
      sectionId: '',
      subcategoryId: '',
      productionZoneId: getDefaultProductionZoneId(),
      isDealOfDay: false,
      availableFromTime: '',
      availableToTime: '',
      supplementIds: [],
      image: '',
      description: '',
      isAvailable: true,
      isCaisseAvailable: true,
      isBorneAvailable: true,
      isSpicy: false,
      isVegetarian: false,
      isHalal: false,
      isGlutenFree: false,
      allergens: [],
    });
    setProductEditAiInstruction('');
    setEditingProduct(null);
  };

  const resetSupplementForm = () => {
    setSupplementFormData({
      name: '',
      price: '',
      image: '',
      supplementCategoryId: '',
    });
  };

  const resetSupplementEditForm = () => {
    setSupplementEditFormData({
      name: '',
      price: '',
      image: '',
      supplementCategoryId: '',
    });
    setEditingSupplement(null);
  };

  const resetMenuForm = () => {
    setMenuFormData({
      name: '',
      price: '',
      description: '',
      image: '',
      availableFromTime: '',
      availableToTime: '',
      selectedSections: [],
      selectedProducts: [],
    });
    setEditingMenu(null);
  };

  const startPrinterEdit = (printer) => {
    setPrinterFormData({
      connectionMode: printer.connectionMode || 'epson_epos',
      printerType: inferPrinterTypeFromPrinter(printer),
      name: printer.name,
      printerIp: printer.printerIp,
      networkHost: printer.networkHost || '',
      port: String(printer.port),
      deviceId: printer.deviceId,
      bridgeId: printer.bridgeId || '',
      bridgeMacAddress: printer.bridgeMacAddress || '',
      bridgeBrokerHost: printer.bridgeBrokerHost || '212.227.212.196',
      bridgeBrokerPort: String(printer.bridgeBrokerPort === 1883 ? 8883 : (printer.bridgeBrokerPort || 8883)),
      bridgeUsername: printer.bridgeUsername || '',
      bridgePassword: printer.bridgePassword || '',
      bridgeTimeoutMs: String(printer.bridgeTimeoutMs || 15000),
      isMain: !!printer.isMain,
    });
    setEditingPrinter(printer);
  };

  const startProductionZoneEdit = (zone) => {
    setProductionZoneFormData({
      name: zone.name || '',
      printerId: String(zone.printerId || ''),
      ticketCopies: String(zone.ticketCopies || 1),
    });
    setEditingProductionZone(zone);
  };

  const startProductEdit = (product) => {
    setProductEditFormData({
      name: product.name,
      kitchenName: product.kitchenName || '',
      price: product.price,
      dineInPrice: product.dineInPrice ?? '',
      taxRate: String(product.taxRate ?? 10),
      sectionId: String(product.sectionId || ''),
      subcategoryId: String(product.subcategoryId || ''),
      productionZoneId: String(product.productionZoneId || product.ProductionZone?.id || ''),
      isDealOfDay: !!product.isDealOfDay,
      availableFromTime: String(product.availableFromTime || ''),
      availableToTime: String(product.availableToTime || ''),
      supplementIds: Array.isArray(product.Supplements)
        ? product.Supplements.map((supplement) => String(supplement.id))
        : [],
      image: product.image || '',
      description: product.description || '',
      isAvailable: product.isAvailable !== false,
      isCaisseAvailable: product.isCaisseAvailable !== false,
      isBorneAvailable: product.isBorneAvailable !== false,
      isSpicy: !!product.isSpicy,
      isVegetarian: !!product.isVegetarian,
      isHalal: !!product.isHalal,
      isGlutenFree: !!product.isGlutenFree,
      allergens: Array.isArray(product.allergens) ? product.allergens.map(String) : [],
    });
    setProductEditAiInstruction('');
    setEditingProduct(product);
  };

  const startSupplementEdit = (supplement) => {
    setSupplementEditFormData({
      name: supplement.name,
      price: supplement.price,
      image: supplement.image || '',
      supplementCategoryId: String(supplement.supplementCategoryId || ''),
    });
    setEditingSupplement(supplement);
  };

  const startMenuEdit = (menu) => {
    setMenuFormData({
      name: menu.name || '',
      price: String(menu.price ?? ''),
      description: menu.description || '',
      image: menu.image || '',
      availableFromTime: String(menu.availableFromTime || ''),
      availableToTime: String(menu.availableToTime || ''),
      selectedSections: Array.isArray(menu.Sections)
        ? menu.Sections.map((section) => ({
            id: String(section.id),
            requiredQuantity: String(getSectionRequiredQuantity(section)),
          }))
        : [],
      selectedProducts: Array.isArray(menu.Products)
        ? menu.Products.map((product) => ({
            id: String(product.id),
            additionalPrice: String(product.MenuProducts?.additionalPrice || 0),
          }))
        : [],
    });
    setEditingMenu(menu);
  };

  const handlePrinterSubmit = async (event) => {
    event.preventDefault();
    const method = editingPrinter ? 'PUT' : 'POST';
    const url = editingPrinter ? `${API_URL}/api/printers/${editingPrinter.id}` : `${API_URL}/api/printers`;
    const connectionMode = String(printerFormData.connectionMode || 'epson_epos').trim().toLowerCase();
    const payload = {
      name: printerFormData.name,
      printerIp: String(printerFormData.printerIp || '').trim(),
      networkHost: '',
      port: resolveDefaultPort(connectionMode),
      deviceId: resolveDeviceIdFromPrinterType(printerFormData.printerType),
      bridgeId: String(printerFormData.bridgeId || '').trim(),
      bridgeMacAddress: String(printerFormData.bridgeMacAddress || '').trim(),
      bridgeBrokerHost: String(printerFormData.bridgeBrokerHost || '').trim(),
      bridgeBrokerPort: parseInt(printerFormData.bridgeBrokerPort || 8883, 10),
      isMain: !!printerFormData.isMain,
      connectionMode,
    };

    await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    resetPrinterForm();
    fetchCoreData();
  };

  const handleProductionZoneSubmit = async (event) => {
    event.preventDefault();
    const method = editingProductionZone ? 'PUT' : 'POST';
    const url = editingProductionZone
      ? `${API_URL}/api/production-zones/${editingProductionZone.id}`
      : `${API_URL}/api/production-zones`;

    await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...productionZoneFormData,
        printerId: productionZoneFormData.printerId ? parseInt(productionZoneFormData.printerId, 10) : null,
        ticketCopies: parseInt(productionZoneFormData.ticketCopies || 1, 10),
      }),
    });

    resetProductionZoneForm();
    fetchCoreData();
  };

  const handleProductionZoneDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/production-zones/${id}`, { method: 'DELETE' });
    if (editingProductionZone?.id === id) {
      resetProductionZoneForm();
    }
    fetchCoreData();
  };

  const handlePrinterDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/printers/${id}`, { method: 'DELETE' });
    fetchCoreData();
  };

  const handlePrinterDiscovery = async () => {
    try {
      setPrinterDiscoveryLoading(true);
      setPrinterDiscoveryError('');
      const response = await fetchWithAuth(`${API_URL}/api/printers/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Découverte impossible.');
      }

      setPrinterDiscoveryResults(payload.discoveries || []);
    } catch (error) {
      setPrinterDiscoveryError(error.message || 'Découverte impossible.');
    } finally {
      setPrinterDiscoveryLoading(false);
    }
  };

  const applyDiscoveredPrinter = (candidate) => {
    setPrinterFormData((prev) => ({
      ...prev,
      printerIp: candidate.host,
      networkHost: candidate.host,
      port: String(candidate.port || prev.port || 9100),
      connectionMode: candidate.protocol || 'escpos_tcp',
    }));
  };

  const isBridgePrinterForm = String(printerFormData.connectionMode || '').toLowerCase() === 'esp32_bridge';

  const handlePrinterTest = async (printer) => {
    try {
      await printTestTicket(printer);
      window.alert(`Ticket test envoyé sur ${printer.name}.`);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleSectionSubmit = async (event) => {
    event.preventDefault();
    const isEditing = !!editingSection?.id;
    const endpoint = isEditing
      ? `${API_URL}/api/sections/${editingSection.id}`
      : `${API_URL}/api/sections`;
    const method = isEditing ? 'PUT' : 'POST';

    await fetchWithAuth(endpoint, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sectionFormData),
    });
    setSectionFormData({ name: '', emoji: '', image: '', displayOrder: '0' });
    setEditingSection(null);
    fetchCoreData();
  };

  const startSectionEdit = (section) => {
    setSectionFormData({
      name: section.name || '',
      emoji: section.emoji || '',
      image: section.image || '',
      displayOrder: String(section.displayOrder ?? 0),
    });
    setEditingSection(section);
  };

  const resetSectionForm = () => {
    setSectionFormData({ name: '', emoji: '', image: '', displayOrder: '0' });
    setEditingSection(null);
  };

  const handleSubcategorySubmit = async (event) => {
    event.preventDefault();
    const isEditing = !!editingSubcategory?.id;
    const endpoint = isEditing
      ? `${API_URL}/api/subcategories/${editingSubcategory.id}`
      : `${API_URL}/api/subcategories`;
    const method = isEditing ? 'PUT' : 'POST';

    await fetchWithAuth(endpoint, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subcategoryFormData),
    });

    setSubcategoryFormData({
      name: '',
      sectionId: '',
      image: '',
      displayOrder: '0',
    });
    setEditingSubcategory(null);
    fetchCoreData();
  };

  const startSubcategoryEdit = (subcategory) => {
    setSubcategoryFormData({
      name: subcategory.name || '',
      sectionId: String(subcategory.sectionId || ''),
      image: subcategory.image || '',
      displayOrder: String(subcategory.displayOrder ?? 0),
    });
    setEditingSubcategory(subcategory);
  };

  const resetSubcategoryForm = () => {
    setSubcategoryFormData({
      name: '',
      sectionId: '',
      image: '',
      displayOrder: '0',
    });
    setEditingSubcategory(null);
  };

  const handleSubcategoryDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/subcategories/${id}`, { method: 'DELETE' });
    fetchCoreData();
  };

  const handleSupplementCategorySubmit = async (event) => {
    event.preventDefault();
    await fetchWithAuth(`${API_URL}/api/supplement-categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplementCategoryFormData),
    });
    setSupplementCategoryFormData({ name: '' });
    fetchCoreData();
  };

  const handleSupplementCategoryDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/supplement-categories/${id}`, { method: 'DELETE' });
    fetchCoreData();
  };

  const handleProductSubmit = async (event) => {
    event.preventDefault();
    await fetchWithAuth(`${API_URL}/api/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(productFormData),
    });

    resetProductForm();
    fetchCoreData();
  };

  const handleProductEditSubmit = async (event) => {
    event.preventDefault();

    if (!editingProduct) {
      return;
    }

    await fetchWithAuth(`${API_URL}/api/products/${editingProduct.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(productEditFormData),
    });

    resetProductEditForm();
    fetchCoreData();
  };

  const handleProductDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/products/${id}`, { method: 'DELETE' });
    if (editingProduct?.id === id) {
      resetProductEditForm();
    }
    const draftKey = String(id);
    setProductInlinePriceDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, draftKey)) {
        return current;
      }
      const nextDrafts = { ...current };
      delete nextDrafts[draftKey];
      return nextDrafts;
    });
    setProductInlineTaxRateDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, draftKey)) {
        return current;
      }
      const nextDrafts = { ...current };
      delete nextDrafts[draftKey];
      return nextDrafts;
    });
    setSelectedProductIdsForAutoImages((current) => current.filter((productId) => String(productId) !== String(id)));
    fetchCoreData();
  };

  const handleSupplementSubmit = async (event) => {
    event.preventDefault();
    await fetchWithAuth(`${API_URL}/api/supplements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplementFormData),
    });

    resetSupplementForm();
    fetchCoreData();
  };

  const handleSupplementEditSubmit = async (event) => {
    event.preventDefault();

    if (!editingSupplement) {
      return;
    }

    await fetchWithAuth(`${API_URL}/api/supplements/${editingSupplement.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(supplementEditFormData),
    });

    resetSupplementEditForm();
    fetchCoreData();
  };

  const handleSupplementDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/supplements/${id}`, { method: 'DELETE' });
    if (editingSupplement?.id === id) {
      resetSupplementEditForm();
    }
    fetchCoreData();
  };

  const handleMenuSubmit = async (event) => {
    event.preventDefault();
    const method = editingMenu ? 'PUT' : 'POST';
    const url = editingMenu ? `${API_URL}/api/menus/${editingMenu.id}` : `${API_URL}/api/menus`;

    await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: menuFormData.name,
        price: parseFloat(menuFormData.price),
        description: menuFormData.description,
        image: menuFormData.image,
        availableFromTime: menuFormData.availableFromTime,
        availableToTime: menuFormData.availableToTime,
        sections: menuFormData.selectedSections.map((section) => ({
          sectionId: parseInt(section.id ?? section, 10),
          requiredQuantity: parseInt(section.requiredQuantity || 1, 10),
        })),
        products: menuFormData.selectedProducts.map((item) => ({
          id: parseInt(item.id, 10),
          additionalPrice: parseFloat(item.additionalPrice || 0),
        })),
      }),
    });

    resetMenuForm();
    fetchCoreData();
  };

  const handleMenuDelete = async (id) => {
    await fetchWithAuth(`${API_URL}/api/menus/${id}`, { method: 'DELETE' });

    if (editingMenu?.id === id) {
      resetMenuForm();
    }

    fetchCoreData();
  };

  const uploadFile = async (kind, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_URL}/api/uploads/${kind}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload impossible');
    }

    return response.json();
  };

  const uploadProductImage = async (file, onUploaded) => {
    setProductImageUploading(true);
    try {
      const uploaded = await uploadFile('product', file);
      onUploaded(resolveMediaUrl(uploaded.url));
    } finally {
      setProductImageUploading(false);
    }
  };

  const handleSectionImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setSectionImageUploading(true);
      const uploaded = await uploadFile('product', file);
      setSectionFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image de la catégorie.');
    } finally {
      setSectionImageUploading(false);
      event.target.value = '';
    }
  };

  const handleSubcategoryImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setSubcategoryImageUploading(true);
      const uploaded = await uploadFile('product', file);
      setSubcategoryFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image de la sous-catégorie.');
    } finally {
      setSubcategoryImageUploading(false);
      event.target.value = '';
    }
  };

  const handleProductImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      await uploadProductImage(file, (image) => setProductFormData((prev) => ({ ...prev, image })));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image du produit.');
    } finally {
      event.target.value = '';
    }
  };

  const handleProductEditImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setProductEditImageUploading(true);
      const uploaded = await uploadFile('product', file);
      setProductEditFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image du produit.');
    } finally {
      setProductEditImageUploading(false);
      event.target.value = '';
    }
  };

  const handleSupplementImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setSupplementImageUploading(true);
      const uploaded = await uploadFile('supplement', file);
      setSupplementFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image du supplément.');
    } finally {
      setSupplementImageUploading(false);
      event.target.value = '';
    }
  };

  const handleSupplementEditImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setSupplementEditImageUploading(true);
      const uploaded = await uploadFile('supplement', file);
      setSupplementEditFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image du supplément.');
    } finally {
      setSupplementEditImageUploading(false);
      event.target.value = '';
    }
  };

  const handleAutoSupplementImages = async () => {
    try {
      setSupplementAutoImageLoading(true);
      const selectedIds = selectedSupplementIdsForAutoImages.length
        ? selectedSupplementIdsForAutoImages
        : supplements.map((supplement) => supplement.id);
      const response = await fetchWithAuth(`${API_URL}/api/supplements/auto-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overwrite: true,
          supplementIds: selectedIds,
        }),
      });
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.error || 'La recherche automatique des images a échoué.');
      }

      window.alert(
        `${payload.message}\n${payload.resolved || 0} suppléments mis à jour avec une photo.`
      );
      fetchCoreData();
    } catch (error) {
      window.alert(error.message);
    } finally {
      setSupplementAutoImageLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIdsForAutoImages((current) => {
      const normalizedId = String(productId);
      if (current.some((value) => String(value) === normalizedId)) {
        return current.filter((value) => String(value) !== normalizedId);
      }

      return [...current, productId];
    });
  };

  const toggleAllProductSelections = (targetProducts = products) => {
    const targetIds = targetProducts.map((product) => String(product.id));
    setSelectedProductIdsForAutoImages((current) => {
      const selectedInTarget = current.filter((id) => targetIds.includes(String(id)));
      if (selectedInTarget.length === targetIds.length && targetIds.length > 0) {
        return current.filter((id) => !targetIds.includes(String(id)));
      }

      const remaining = current.filter((id) => !targetIds.includes(String(id)));
      return [...remaining, ...targetProducts.map((product) => product.id)];
    });
  };

  const handleProductAvailabilityBulk = async (isAvailable, productIds = []) => {
    if (!productIds.length) {
      window.alert('Sélectionne au moins un produit.');
      return;
    }

    try {
      setProductAvailabilityUpdating(true);
      const response = await fetchWithAuth(`${API_URL}/api/products/availability`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds,
          isAvailable,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour de disponibilité impossible.');
      }

      const updatedById = new Map((payload.products || []).map((product) => [String(product.id), product]));
      setProducts((current) =>
        current.map((product) => (updatedById.has(String(product.id)) ? {
          ...product,
          ...updatedById.get(String(product.id)),
          image: resolveMediaUrl(updatedById.get(String(product.id)).image),
        } : product))
      );
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductAvailabilityUpdating(false);
    }
  };

  const buildProductUpsertPayload = (product, overrides = {}) => ({
    name: String(product.name || ''),
    kitchenName: String(product.kitchenName || ''),
    price: Number(product.price || 0),
    dineInPrice: product.dineInPrice === null || product.dineInPrice === undefined ? '' : product.dineInPrice,
    taxRate: Number(product.taxRate ?? 10),
    sectionId: String(product.sectionId || product.Section?.id || ''),
    subcategoryId: String(product.subcategoryId || product.Subcategory?.id || ''),
    productionZoneId: String(product.productionZoneId || product.ProductionZone?.id || ''),
    isDealOfDay: !!product.isDealOfDay,
    availableFromTime: String(product.availableFromTime || ''),
    availableToTime: String(product.availableToTime || ''),
    supplementIds: Array.isArray(product.Supplements)
      ? product.Supplements.map((supplement) => Number(supplement.id)).filter(Number.isFinite)
      : [],
    image: String(product.image || ''),
    description: String(product.description || ''),
    isAvailable: product.isAvailable !== false,
    isCaisseAvailable: product.isCaisseAvailable !== false,
    isBorneAvailable: product.isBorneAvailable !== false,
    isSpicy: !!product.isSpicy,
    isVegetarian: !!product.isVegetarian,
    isHalal: !!product.isHalal,
    isGlutenFree: !!product.isGlutenFree,
    allergens: Array.isArray(product.allergens) ? product.allergens.map(String) : [],
    ...overrides,
  });

  const getProductInlinePriceValue = (product) => {
    const productId = String(product?.id || '');
    if (!productId) {
      return '';
    }
    if (Object.prototype.hasOwnProperty.call(productInlinePriceDrafts, productId)) {
      return productInlinePriceDrafts[productId];
    }
    return String(product?.price ?? '');
  };

  const handleProductInlinePriceChange = (productId, rawValue) => {
    const key = String(productId || '');
    if (!key) {
      return;
    }
    setProductInlinePriceDrafts((current) => ({
      ...current,
      [key]: rawValue,
    }));
  };

  const getProductInlineTaxRateValue = (product) => {
    const productId = String(product?.id || '');
    if (!productId) {
      return '';
    }
    if (Object.prototype.hasOwnProperty.call(productInlineTaxRateDrafts, productId)) {
      return productInlineTaxRateDrafts[productId];
    }
    return String(product?.taxRate ?? 10);
  };

  const handleProductInlineTaxRateChange = (productId, rawValue) => {
    const key = String(productId || '');
    if (!key) {
      return;
    }
    setProductInlineTaxRateDrafts((current) => ({
      ...current,
      [key]: rawValue,
    }));
  };

  const getProductInlineDineInPriceValue = (product) => {
    const productId = String(product?.id || '');
    if (!productId) {
      return '';
    }
    if (Object.prototype.hasOwnProperty.call(productInlineDineInPriceDrafts, productId)) {
      return productInlineDineInPriceDrafts[productId];
    }
    return product?.dineInPrice === null || product?.dineInPrice === undefined ? '' : String(product.dineInPrice);
  };

  const handleProductInlineDineInPriceChange = (productId, rawValue) => {
    const key = String(productId || '');
    if (!key) {
      return;
    }
    setProductInlineDineInPriceDrafts((current) => ({
      ...current,
      [key]: rawValue,
    }));
  };

  const getProductInlineZoneValue = (product) => {
    const productId = String(product?.id || '');
    if (!productId) {
      return '';
    }
    if (Object.prototype.hasOwnProperty.call(productInlineZoneDrafts, productId)) {
      return String(productInlineZoneDrafts[productId] || '');
    }
    return String(product?.productionZoneId || product?.ProductionZone?.id || '');
  };

  const handleProductInlineZoneChange = (productId, rawValue) => {
    const key = String(productId || '');
    if (!key) {
      return;
    }
    setProductInlineZoneDrafts((current) => ({
      ...current,
      [key]: String(rawValue || ''),
    }));
  };

  const hasPendingInlineZoneChange = (product) => {
    const key = String(product?.id || '');
    if (!key || !Object.prototype.hasOwnProperty.call(productInlineZoneDrafts, key)) {
      return false;
    }
    const currentZone = String(product?.productionZoneId || product?.ProductionZone?.id || '');
    const draftZone = String(productInlineZoneDrafts[key] || '');
    return currentZone !== draftZone;
  };

  const hasPendingInlinePriceChange = (product) => {
    const key = String(product?.id || '');
    if (!key || !Object.prototype.hasOwnProperty.call(productInlinePriceDrafts, key)) {
      return false;
    }

    const rawDraft = String(productInlinePriceDrafts[key] ?? '').replace(',', '.').trim();
    const draftNumber = Number(rawDraft);
    if (!Number.isFinite(draftNumber) || draftNumber < 0) {
      return rawDraft !== String(product?.price ?? '').replace(',', '.').trim();
    }

    const roundedDraft = Number(draftNumber.toFixed(2));
    return Number(product?.price || 0) !== roundedDraft;
  };

  const hasPendingInlineTaxRateChange = (product) => {
    const key = String(product?.id || '');
    if (!key || !Object.prototype.hasOwnProperty.call(productInlineTaxRateDrafts, key)) {
      return false;
    }

    const rawDraft = String(productInlineTaxRateDrafts[key] ?? '').replace(',', '.').trim();
    const draftNumber = Number(rawDraft);
    if (!Number.isFinite(draftNumber) || draftNumber < 0 || draftNumber > 100) {
      return rawDraft !== String(product?.taxRate ?? 10).replace(',', '.').trim();
    }

    const roundedDraft = Number(draftNumber.toFixed(2));
    return Number(product?.taxRate ?? 10) !== roundedDraft;
  };

  const handleProductInlinePriceSave = async (product) => {
    if (!product?.id) {
      return;
    }

    const key = String(product.id);
    const rawPrice = Object.prototype.hasOwnProperty.call(productInlinePriceDrafts, key)
      ? productInlinePriceDrafts[key]
      : String(product.price ?? '');
    const normalizedRawPrice = String(rawPrice || '').replace(',', '.').trim();
    const nextPrice = Number(normalizedRawPrice);
    const rawTaxRate = Object.prototype.hasOwnProperty.call(productInlineTaxRateDrafts, key)
      ? productInlineTaxRateDrafts[key]
      : String(product.taxRate ?? 10);
    const normalizedRawTaxRate = String(rawTaxRate || '').replace(',', '.').trim();
    const nextTaxRate = Number(normalizedRawTaxRate);

    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      window.alert('Prix invalide. Utilise un nombre positif.');
      return;
    }
    if (!Number.isFinite(nextTaxRate) || nextTaxRate < 0 || nextTaxRate > 100) {
      window.alert('TVA invalide. Utilise une valeur entre 0 et 100.');
      return;
    }

    const roundedPrice = Number(nextPrice.toFixed(2));
    const roundedTaxRate = Number(nextTaxRate.toFixed(2));
    const isPriceUnchanged = Number(product.price) === roundedPrice;
    const isTaxRateUnchanged = Number(product.taxRate ?? 10) === roundedTaxRate;

    if (isPriceUnchanged && isTaxRateUnchanged) {
      setProductInlinePriceDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
      setProductInlineTaxRateDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
      return;
    }

    try {
      setProductPriceUpdatingId(key);
      const response = await fetchWithAuth(`${API_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildProductUpsertPayload(product, {
          price: roundedPrice,
          taxRate: roundedTaxRate,
        })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour du prix/TVA impossible.');
      }

      setProducts((current) =>
        current.map((item) => (String(item.id) === key ? {
          ...item,
          ...payload,
          image: resolveMediaUrl(payload.image),
        } : item))
      );

      if (editingProduct?.id === product.id) {
        setProductEditFormData((current) => ({
          ...current,
          price: String(payload.price ?? roundedPrice),
          taxRate: String(payload.taxRate ?? roundedTaxRate),
        }));
      }

      setProductInlinePriceDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
      setProductInlineTaxRateDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductPriceUpdatingId('');
    }
  };

  const handleProductSalesOptionsSave = async (product, overrides = {}) => {
    if (!product?.id) {
      return;
    }

    const key = String(product.id);
    const rawDineInPrice = Object.prototype.hasOwnProperty.call(productInlineDineInPriceDrafts, key)
      ? productInlineDineInPriceDrafts[key]
      : (product.dineInPrice ?? '');
    const normalizedRaw = String(rawDineInPrice || '').replace(',', '.').trim();
    const nextDineInPrice = normalizedRaw === '' ? '' : Number(normalizedRaw);

    if (normalizedRaw !== '' && (!Number.isFinite(nextDineInPrice) || nextDineInPrice < 0)) {
      window.alert('Prix sur place invalide. Utilise un nombre positif ou laisse vide.');
      return;
    }

    try {
      setProductSalesOptionsUpdatingId(key);
      const response = await fetchWithAuth(`${API_URL}/api/products/${product.id}/sales-options`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dineInPrice: normalizedRaw === '' ? '' : Number(nextDineInPrice.toFixed(2)),
          isCaisseAvailable: product.isCaisseAvailable !== false,
          isBorneAvailable: product.isBorneAvailable !== false,
          ...overrides,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour caisse/borne impossible.');
      }

      setProducts((current) =>
        current.map((item) => (String(item.id) === key ? {
          ...item,
          ...payload,
          image: resolveMediaUrl(payload.image),
        } : item))
      );
      if (editingProduct?.id === product.id) {
        setProductEditFormData((current) => ({
          ...current,
          dineInPrice: payload.dineInPrice ?? '',
          isCaisseAvailable: payload.isCaisseAvailable !== false,
          isBorneAvailable: payload.isBorneAvailable !== false,
        }));
      }
      setProductInlineDineInPriceDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductSalesOptionsUpdatingId('');
    }
  };

  const handleProductDealToggle = async (product, isDealOfDay) => {
    if (!product?.id) {
      return;
    }

    try {
      setProductDealUpdatingId(String(product.id));
      const response = await fetchWithAuth(`${API_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildProductUpsertPayload(product, { isDealOfDay })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour du Deal du jour impossible.');
      }

      setProducts((current) =>
        current.map((item) => (String(item.id) === String(product.id) ? {
          ...item,
          ...payload,
          image: resolveMediaUrl(payload.image),
        } : item))
      );

      if (editingProduct?.id === product.id) {
        setProductEditFormData((current) => ({ ...current, isDealOfDay }));
      }
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductDealUpdatingId('');
    }
  };

  const handleProductInlineZoneSave = async (product) => {
    if (!product?.id) {
      return;
    }

    const key = String(product.id);
    const nextZoneId = Object.prototype.hasOwnProperty.call(productInlineZoneDrafts, key)
      ? String(productInlineZoneDrafts[key] || '')
      : String(product.productionZoneId || product.ProductionZone?.id || '');
    const currentZoneId = String(product.productionZoneId || product.ProductionZone?.id || '');

    if (nextZoneId === currentZoneId) {
      setProductInlineZoneDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
      return;
    }

    try {
      setProductZoneUpdatingId(key);
      const response = await fetchWithAuth(`${API_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildProductUpsertPayload(product, { productionZoneId: nextZoneId })),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Mise à jour de la zone impossible.');
      }

      setProducts((current) =>
        current.map((item) => (String(item.id) === key ? {
          ...item,
          ...payload,
          image: resolveMediaUrl(payload.image),
        } : item))
      );

      if (editingProduct?.id === product.id) {
        setProductEditFormData((current) => ({
          ...current,
          productionZoneId: String(payload.productionZoneId || payload.ProductionZone?.id || ''),
        }));
      }

      setProductInlineZoneDrafts((current) => {
        if (!Object.prototype.hasOwnProperty.call(current, key)) {
          return current;
        }
        const nextDrafts = { ...current };
        delete nextDrafts[key];
        return nextDrafts;
      });
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductZoneUpdatingId('');
    }
  };

  const handleAutoProductImages = async ({ removeBackground = false, productIds = null, instruction = '' } = {}) => {
    try {
      setProductAutoImageLoading(true);
      const selectedIds = Array.isArray(productIds) && productIds.length
        ? productIds
        : selectedProductIdsForAutoImages.length
          ? selectedProductIdsForAutoImages
          : products.map((product) => product.id);

      const response = await fetchWithAuth(`${API_URL}/api/products/auto-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          overwrite: true,
          editExisting: removeBackground,
          removeBackground,
          instruction,
          productIds: selectedIds,
        }),
      });
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.error || 'La génération des photos produits a échoué.');
      }

      window.alert(
        `${payload.message}\n${payload.resolved || 0} produit(s) mis à jour avec une photo.`
      );
      fetchCoreData();
    } catch (error) {
      window.alert(error.message);
    } finally {
      setProductAutoImageLoading(false);
    }
  };

  const toggleSupplementSelection = (supplementId) => {
    setSelectedSupplementIdsForAutoImages((current) => {
      const normalizedId = String(supplementId);
      if (current.some((value) => String(value) === normalizedId)) {
        return current.filter((value) => String(value) !== normalizedId);
      }

      return [...current, supplementId];
    });
  };

  const toggleAllSupplementSelections = () => {
    setSelectedSupplementIdsForAutoImages((current) =>
      current.length === supplements.length ? [] : supplements.map((supplement) => supplement.id)
    );
  };

  const handleMenuImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setMenuImageUploading(true);
      const uploaded = await uploadFile('product', file);
      setMenuFormData((prev) => ({ ...prev, image: resolveMediaUrl(uploaded.url) }));
    } catch (error) {
      window.alert('Impossible d’envoyer l’image du menu.');
    } finally {
      setMenuImageUploading(false);
      event.target.value = '';
    }
  };

  const selectedSupplementCount = selectedSupplementIdsForAutoImages.length;
  const allSupplementsSelected = supplements.length > 0 && selectedSupplementCount === supplements.length;
  const selectedFilteredProductCount = filteredCatalogProducts.filter((product) =>
    selectedProductIdsForAutoImages.some((selectedId) => String(selectedId) === String(product.id))
  ).length;
  const allProductsSelected =
    filteredCatalogProducts.length > 0 && selectedFilteredProductCount === filteredCatalogProducts.length;

  const handleAttractMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setAttractUploading(true);
      const uploaded = await uploadFile('attract', file);
      setPrinterSettings((prev) => ({
        ...prev,
        attractBackgroundMedia: resolveMediaUrl(uploaded.url),
        attractBackgroundType: uploaded.mimeType.startsWith('video/') ? 'video' : 'image',
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le média de veille.');
    } finally {
      setAttractUploading(false);
      event.target.value = '';
    }
  };

  const handleClosedAttractMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setAttractUploading(true);
      const uploaded = await uploadFile('attract', file);
      setPrinterSettings((prev) => ({
        ...prev,
        closedAttractBackgroundMedia: resolveMediaUrl(uploaded.url),
        closedAttractBackgroundType: uploaded.mimeType.startsWith('video/') ? 'video' : 'image',
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le fond fermé.');
    } finally {
      setAttractUploading(false);
      event.target.value = '';
    }
  };

  const handleClientBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setClientBackgroundUploading(true);
      const uploaded = await uploadFile('attract', file);
      setPrinterSettings((prev) => ({
        ...prev,
        clientBackgroundMedia: resolveMediaUrl(uploaded.url),
        clientBackgroundType: uploaded.mimeType.startsWith('video/') ? 'video' : 'image',
        clientBackgroundEnabled: true,
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le média de la page borne.');
    } finally {
      setClientBackgroundUploading(false);
      event.target.value = '';
    }
  };

  const handleTicketLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setTicketLogoUploading(true);
      const uploaded = await uploadFile('branding', file);
      setPrinterSettings((prev) => ({
        ...prev,
        ticketLogoMedia: resolveMediaUrl(uploaded.url),
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le logo du ticket.');
    } finally {
      setTicketLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleDisplayLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setDisplayLogoUploading(true);
      const uploaded = await uploadFile('branding', file);
      setPrinterSettings((prev) => ({
        ...prev,
        displayLogoMedia: resolveMediaUrl(uploaded.url),
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le logo d’affichage.');
    } finally {
      setDisplayLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleFidLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setFidLogoUploading(true);
      const uploaded = await uploadFile('branding', file);
      setPrinterSettings((prev) => ({
        ...prev,
        fidLogoMedia: resolveMediaUrl(uploaded.url),
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le logo Ecran fid.');
    } finally {
      setFidLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleCustomerChatbotLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setCustomerChatbotLogoUploading(true);
      const uploaded = await uploadFile('branding', file);
      setPrinterSettings((prev) => ({
        ...prev,
        customerChatbotLogoMedia: resolveMediaUrl(uploaded.url),
      }));
    } catch (error) {
      window.alert('Impossible d’envoyer le logo du chatbot.');
    } finally {
      setCustomerChatbotLogoUploading(false);
      event.target.value = '';
    }
  };

  const handleUberImportSubmit = async (event) => {
    event.preventDefault();

    try {
      setUberImporting(true);
      const response = await fetchWithAuth(`${API_URL}/api/import/ubereats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(uberImportFormData),
      });
      const responseText = await response.text();
      let payload = null;
      try {
        payload = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        payload = null;
      }

      if (!response.ok) {
        const backendError = payload?.error || payload?.message;
        if (backendError) {
          throw new Error(backendError);
        }

        if (responseText && responseText.trim().startsWith('<')) {
          throw new Error(
            `Le serveur a renvoyé une page HTML (HTTP ${response.status}) au lieu d'un JSON. Vérifie le proxy API/nginx.`
          );
        }

        throw new Error(`L'import Uber Eats a échoué (HTTP ${response.status}).`);
      }

      if (!payload || typeof payload !== 'object') {
        throw new Error("Réponse invalide du serveur après l'import Uber Eats.");
      }

      window.alert(
        `${payload.message}\n${payload.productsCreated} produits créés, ${payload.productsUpdated} produits mis à jour.\n${payload.supplementCategoriesCreated || 0} catégories de suppléments créées, ${payload.supplementsCreated || 0} suppléments créés, ${payload.supplementsUpdated || 0} suppléments mis à jour, ${payload.productSupplementsLinked || 0} liens produits/suppléments ajoutés.`
          + `${payload.supplementImagesResolved ? `\n${payload.supplementImagesResolved} photos de suppléments ajoutées automatiquement.` : ''}`
      );
      setUberImportFormData({ url: '' });
      fetchCoreData();
    } catch (error) {
      window.alert(error.message);
    } finally {
      setUberImporting(false);
    }
  };

  const updateWeeklyDaySchedule = (dayKey, patch) => {
    setPrinterSettings((prev) => {
      const currentSchedule = normalizeWeeklySchedule(prev.weeklySchedule, prev);
      return {
        ...prev,
        weeklySchedule: {
          ...currentSchedule,
          [dayKey]: {
            ...(currentSchedule[dayKey] || {
              enabled: true,
              openingTime: '',
              closingTime: '',
              breakStartTime: '',
              breakEndTime: '',
            }),
            ...patch,
          },
        },
      };
    });
  };

  const savePrinterSettings = async (message) => {
    if (loyaltyConflict) throw new Error('Actualisez les réglages avant une nouvelle sauvegarde. Votre brouillon est conservé.');
    const patch = adminSettingsPatch(printerSettings, printerBaselineRef.current);
    let response, printerSettingsData;
    try {
      response = await fetchWithAuth(`${API_URL}/api/printer-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      printerSettingsData = await response.json();
    } catch (error) {
      if (patch.customerAppearanceRevision) {
        setLoyaltyConflict(true);
        throw new Error('Le résultat de la sauvegarde ne peut pas être confirmé. Votre brouillon est conservé. Actualisez les réglages avant de réessayer.');
      }
      throw error;
    }
    if (!response.ok) {
      if (response.status === 409 || (patch.customerAppearanceRevision && response.status >= 500)) {
        setLoyaltyConflict(true);
        throw new Error(response.status === 409 ? 'Ces réglages ont changé dans une autre interface. Votre brouillon est conservé. Actualisez les réglages avant de reprendre vos modifications.' : 'Le résultat de la sauvegarde ne peut pas être confirmé. Votre brouillon est conservé. Actualisez les réglages avant de réessayer.');
      }
      throw new Error(printerSettingsData.error || 'Impossible d’enregistrer les paramètres.');
    }
    if (patch.customerAppearanceRevision && !/^[a-f0-9]{64}$/.test(printerSettingsData?.customerAppearanceRevision || '')) {
      setLoyaltyConflict(true);
      throw new Error('La version de la palette enregistrée ne peut pas être vérifiée. Votre brouillon est conservé. Actualisez les réglages avant de réessayer.');
    }
    acceptPrinterSettings({
      ...printerSettingsData,
      weeklySchedule: normalizeWeeklySchedule(printerSettingsData.weeklySchedule, printerSettingsData),
      kdsScreenMode: ['single', 'dual', 'full'].includes(String(printerSettingsData.kdsScreenMode || '').trim())
        ? String(printerSettingsData.kdsScreenMode).trim()
        : 'single',
      kdsEnabledScreensJson: normalizeKdsScreens(printerSettingsData.kdsEnabledScreensJson),
      kdsScreenZoneMapJson: normalizeKdsScreenZoneMap(printerSettingsData.kdsScreenZoneMapJson),
      kdsWarnThresholdSeconds: Math.max(30, Number(printerSettingsData.kdsWarnThresholdSeconds || 180)),
      kdsLateThresholdSeconds: Math.max(60, Number(printerSettingsData.kdsLateThresholdSeconds || 600)),
      kdsSoundEnabled: printerSettingsData.kdsSoundEnabled !== false,
      fidReviewUrl: printerSettingsData.fidReviewUrl || 'https://beresto.fr',
      fidPrinterId: printerSettingsData.fidPrinterId || '',
      fidLogoMedia: resolveMediaUrl(printerSettingsData.fidLogoMedia),
      fidTicketQrUrl: printerSettingsData.fidTicketQrUrl || '',
      fidDownloadQrText: printerSettingsData.fidDownloadQrText || 'Telecharge ta photo sous 24h ici',
      customerChatbotName: printerSettingsData.customerChatbotName || 'Assistant menu',
      customerChatbotLogoMedia: resolveMediaUrl(printerSettingsData.customerChatbotLogoMedia),
      customerChatbotWelcomeMessage:
        printerSettingsData.customerChatbotWelcomeMessage ||
        'Bonjour, je peux vous conseiller sur la carte, les recettes et les allergènes.',
      uiSurfaceColorHex:
        typeof printerSettingsData.uiSurfaceColor === 'string' &&
        printerSettingsData.uiSurfaceColor.startsWith('#')
          ? printerSettingsData.uiSurfaceColor
          : '#fffaf3',
    });
    window.alert(message);
  };

  const handleDailySettingsSave = async (event) => {
    event.preventDefault();
    try {
      await savePrinterSettings('Planification enregistrée.');
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleAttractSettingsSave = async (event) => {
    event.preventDefault();
    try {
      await savePrinterSettings('Page de veille mise à jour.');
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleDisplaySettingsSave = async (event) => {
    event.preventDefault();
    try {
      await savePrinterSettings('Affichage mis à jour.');
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handlePrinterOptionsSave = async (event) => {
    event.preventDefault();
    try {
      await savePrinterSettings('Options d’impression mises à jour.');
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleDailySummaryPrint = async () => {
    const summary = await fetchDailySummary();
    const targetPrinter = printers.find(
      (printer) => String(printer.id) === String(printerSettings.dailySummaryPrinterId)
    );

    if (!targetPrinter) {
      window.alert('Sélectionne une imprimante pour le récap journalier.');
      return;
    }

    await printDailySummary(targetPrinter, summary);
    const response = await fetchWithAuth(`${API_URL}/api/printer-settings/mark-daily-summary-printed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: summary.date }),
    });
    const printedSettings = await response.json();
    setPrinterSettings(current => ({ ...current, lastDailySummaryPrintDate: printedSettings.lastDailySummaryPrintDate }));
    window.alert('Récap du jour imprimé.');
  };

  const handleTicketPreviewPrint = async () => {
    const selectedZone = productionZones.find(
      (zone) => String(zone.id) === String(printerSettings.clientTicketZoneId || '')
    );
    const selectedZonePrinter =
      selectedZone?.Printer ||
      printers.find((printer) => String(printer.id) === String(selectedZone?.printerId || ''));
    const mainPrinter = printers.find((printer) => !!printer.isMain) || printers[0];
    const targetPrinter = selectedZonePrinter || mainPrinter;

    if (!targetPrinter) {
      window.alert('Aucune imprimante disponible pour le ticket test.');
      return;
    }

    try {
      setTicketPreviewPrinting(true);

      await printOrderTickets(
        [targetPrinter],
        [
          {
            id: 'test-ticket-item',
            name: 'Produit test impression',
            totalPrice: 9.9,
            quantity: 1,
            selectedSupplements: [
              { id: 'test-sup-1', name: 'Supplément test', price: 0.5 },
            ],
          },
        ],
        '10.40',
        9999,
        {
          ticketHeaderText:
            printerSettings.ticketHeaderCardPaidText || printerSettings.ticketHeaderText || 'TICKET TEST',
          ticketHeaderTextSize: printerSettings.ticketHeaderTextSize || 4,
          ticketLogoMedia: printerSettings.ticketLogoMedia || '',
          ticketStoreLine1: printerSettings.ticketStoreLine1 || '',
          ticketStoreLine2: printerSettings.ticketStoreLine2 || '',
          ticketStoreLine3: printerSettings.ticketStoreLine3 || '',
          ticketStoreLine4: printerSettings.ticketStoreLine4 || '',
          ticketStoreLine5: printerSettings.ticketStoreLine5 || '',
          ticketStoreLine6: printerSettings.ticketStoreLine6 || '',
          ticketFooterLine1: printerSettings.ticketFooterLine1 || '',
          ticketFooterLine2: printerSettings.ticketFooterLine2 || '',
          ticketFooterLine3: printerSettings.ticketFooterLine3 || '',
          ticketQrText: printerSettings.ticketQrText || '',
          ticketQrUrl: printerSettings.ticketQrUrl || '',
          customerFirstName: 'Client test',
          paymentMethod: 'cb',
          skipProductionTickets: true,
        }
      );

      window.alert(`Ticket test envoyé sur ${targetPrinter.name}.`);
    } catch (error) {
      window.alert(error.message || 'Impression du ticket test impossible.');
    } finally {
      setTicketPreviewPrinting(false);
    }
  };

  const renderLoadingButtonContent = (idleLabel, loadingLabel = 'En cours...') => (
    <span className="admin-button-loading-content">
      <span className="admin-button-spinner" aria-hidden="true" />
      <span>{loadingLabel || idleLabel}</span>
    </span>
  );

  const renderProductFields = (
    formData,
    setFormData,
    availableSubcategoryOptions,
    imageUploadHandler,
    compact = false
  ) => {
    const selectedSupplementsCount = Array.isArray(formData.supplementIds) ? formData.supplementIds.length : 0;
    return (
    <div className={`admin-product-fields ${compact ? 'admin-product-fields-compact' : ''}`}>
      <div className="admin-form-grid admin-form-grid-2">
        <label className="admin-field">
          <span>Nom</span>
          <input
            type="text"
            placeholder="Nom du produit"
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
        </label>
        <label className="admin-field">
          <span>Nom cuisine (tickets / panel)</span>
          <input
            type="text"
            placeholder="Optionnel: nom interne cuisine"
            value={formData.kitchenName || ''}
            onChange={(event) => setFormData((prev) => ({ ...prev, kitchenName: event.target.value }))}
          />
        </label>
        <label className="admin-field">
          <span>Prix TTC</span>
          <input
            type="number"
            step="0.01"
            placeholder="0.00"
            value={formData.price}
            onChange={(event) => setFormData((prev) => ({ ...prev, price: event.target.value }))}
            required
          />
        </label>
        {accountFeatures.caisseEnabled && (
          <label className="admin-field">
            <span>Prix sur place TTC</span>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="Vide = prix TTC"
              value={formData.dineInPrice ?? ''}
              onChange={(event) => setFormData((prev) => ({ ...prev, dineInPrice: event.target.value }))}
            />
          </label>
        )}
        <label className="admin-field">
          <span>TVA (%)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="10"
            value={formData.taxRate}
            onChange={(event) => setFormData((prev) => ({ ...prev, taxRate: event.target.value }))}
            required
          />
        </label>
      </div>
      <div className="admin-form-grid admin-form-grid-3">
        <label className="admin-field">
          <span>Catégorie</span>
          <select
            value={formData.sectionId}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                sectionId: event.target.value,
                subcategoryId: '',
              }))
            }
            required
          >
            <option value="">Catégorie principale</option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.emoji ? `${section.emoji} ` : ''}
                {section.name}
              </option>
            ))}
          </select>
        </label>
        <label className="admin-field">
          <span>Sous-catégorie</span>
          <select
            value={formData.subcategoryId}
            onChange={(event) => setFormData((prev) => ({ ...prev, subcategoryId: event.target.value }))}
            disabled={!formData.sectionId}
          >
            <option value="">Sous-catégorie facultative</option>
            {availableSubcategoryOptions.map((subcategory) => (
              <option key={subcategory.id} value={subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>
        </label>
        <label className="admin-field">
          <span>Zone de production</span>
          <select
            value={formData.productionZoneId}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, productionZoneId: event.target.value }))
            }
            required
          >
            <option value="">Choisir une zone</option>
            {productionZones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
                {zone.Printer?.name ? ` · ${zone.Printer.name}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="admin-form-grid admin-form-grid-2">
        <label className="admin-field">
          <span>Début du créneau</span>
          <input
            type="time"
            value={formData.availableFromTime}
            onChange={(event) => setFormData((prev) => ({ ...prev, availableFromTime: event.target.value }))}
          />
        </label>
        <label className="admin-field">
          <span>Fin du créneau</span>
          <input
            type="time"
            value={formData.availableToTime}
            onChange={(event) => setFormData((prev) => ({ ...prev, availableToTime: event.target.value }))}
          />
        </label>
      </div>
      <details className="admin-collapsible admin-product-supplement-field">
        <summary>
          Suppléments disponibles
          <small>{selectedSupplementsCount} sélectionné(s)</small>
        </summary>
        <div className="admin-check-grid admin-check-grid-wide">
          {supplementCategories.map((category) => (
            <div className="admin-check-group" key={category.id}>
              <strong>{category.name}</strong>
              <div className="admin-check-list">
                {supplements
                  .filter((supplement) => String(supplement.supplementCategoryId) === String(category.id))
                  .map((supplement) => (
                    <label className="admin-check admin-check-media" key={supplement.id}>
                      <input
                        type="checkbox"
                        checked={formData.supplementIds.includes(String(supplement.id))}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            supplementIds: event.target.checked
                              ? [...prev.supplementIds, String(supplement.id)]
                              : prev.supplementIds.filter((id) => id !== String(supplement.id)),
                          }))
                        }
                      />
                      {supplement.image ? <img src={supplement.image} alt={supplement.name} /> : null}
                      <span>
                        {supplement.name}
                        <small>{Number(supplement.price).toFixed(2)} €</small>
                      </span>
                    </label>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </details>
      <label className="admin-field">
        <span>Image</span>
        <input type="file" accept="image/*" onChange={imageUploadHandler} />
        {(productImageUploading || productEditImageUploading) && <small>Envoi de l’image en cours...</small>}
        {formData.image && (
          <div className="admin-media-preview">
            <img src={formData.image} alt="Aperçu produit" />
          </div>
        )}
      </label>
      <label className="admin-field">
        <span>Description</span>
        <textarea
          placeholder="Description"
          value={formData.description}
          onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
        />
      </label>
      <label className="admin-check admin-check-inline">
        <input
          type="checkbox"
          checked={!!formData.isDealOfDay}
          onChange={(event) => setFormData((prev) => ({ ...prev, isDealOfDay: event.target.checked }))}
        />
        <span>Mettre dans les Deals du jour</span>
      </label>
      <label className="admin-check admin-check-inline">
        <input
          type="checkbox"
          checked={!!formData.isAvailable}
          onChange={(event) => setFormData((prev) => ({ ...prev, isAvailable: event.target.checked }))}
        />
        <span>Produit disponible a la vente</span>
      </label>
      <div className="admin-check-grid">
        {accountFeatures.caisseEnabled && (
          <label className="admin-check">
            <input
              type="checkbox"
              checked={formData.isCaisseAvailable !== false}
              onChange={(event) => setFormData((prev) => ({ ...prev, isCaisseAvailable: event.target.checked }))}
            />
            <span>Visible sur BE Caisse</span>
          </label>
        )}
        {accountFeatures.borneEnabled && (
          <label className="admin-check">
            <input
              type="checkbox"
              checked={formData.isBorneAvailable !== false}
              onChange={(event) => setFormData((prev) => ({ ...prev, isBorneAvailable: event.target.checked }))}
            />
            <span>Visible sur la borne</span>
          </label>
        )}
      </div>
      <div className="admin-field">
        <span>Tags visuels / allergenes</span>
        <div className="admin-check-grid">
          {PRODUCT_VISUAL_TAGS.map((tag) => (
            <label className="admin-check" key={tag.key}>
              <input
                type="checkbox"
                checked={!!formData[tag.key]}
                onChange={(event) => setFormData((prev) => ({ ...prev, [tag.key]: event.target.checked }))}
              />
              <span>{tag.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="admin-field">
        <span>Allergènes réglementaires</span>
        <div className="admin-check-grid admin-check-grid-wide">
          {PRODUCT_ALLERGENS.map((allergen) => (
            <label className="admin-check" key={allergen.id}>
              <input
                type="checkbox"
                checked={Array.isArray(formData.allergens) && formData.allergens.includes(allergen.id)}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    allergens: event.target.checked
                      ? [...new Set([...(prev.allergens || []), allergen.id])]
                      : (prev.allergens || []).filter((id) => id !== allergen.id),
                  }))
                }
              />
              <span>{allergen.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const weeklyScheduleView = normalizeWeeklySchedule(printerSettings.weeklySchedule, printerSettings);
  const kioskAccount = (() => {
    try {
      return JSON.parse(localStorage.getItem('kioskAccount') || '{}');
    } catch (error) {
      return {};
    }
  })();
  const canManagePayments = kioskAccount.role === 'admin';
  const paymentScope = `${kioskAccount.tenantAccountId || kioskAccount.parentAccountId || kioskAccount.id}:${kioskAccount.id}:${kioskAccount.role}`;
  const customerChatbotPublicSlug = String(kioskAccount?.publicSlug || '').trim();
  const customerChatbotUrl = customerChatbotPublicSlug
    ? `${window.location.origin}/chat/${customerChatbotPublicSlug}`
    : '';

  return (
    <div className="admin-page">
      {authChecking ? (
        <div className="panel-empty">Vérification de session...</div>
      ) : (
        <>
      <div className="admin-tabs">
        {ADMIN_TAB_GROUPS.map((group) => {
          const isActive = getAdminTabGroup(activeTab).id === group.id;
          return (
          <button
            key={group.id}
            type="button"
            className={`admin-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => setActiveTab(group.items[0])}
          >
            {group.id}
          </button>
          );
        })}
      </div>
      <div className="admin-tabs admin-subtabs">
        {getAdminTabGroup(activeTab).items.filter((tab) => tab !== 'Paiements' || canManagePayments).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`admin-tab ${activeTab === tab ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'BEGuest' ? 'BEMenu' : tab}
          </button>
        ))}
      </div>

      {loyaltyConflict && <section role="alert" className="admin-panel"><p>Les réglages partagés doivent être relus avant une nouvelle sauvegarde. Actualiser remplacera votre brouillon par les valeurs enregistrées.</p><button type="button" onClick={fetchCoreData}>Actualiser les réglages et remplacer mon brouillon</button></section>}
      <AiAssistantWidget request={fetchWithAuth} scopeKey={`${kioskAccount.tenantAccountId || kioskAccount.parentAccountId || kioskAccount.id}:${kioskAccount.id}:${kioskAccount.role}`} onActionDone={fetchCoreData} />

      {activeTab === 'Paiements' && (canManagePayments
        ? <><StripeConnectSettings request={fetchWithAuth} scopeKey={paymentScope} expectedAccountId={stripeSetupAccount} navigate={navigate} />{stripeSetupAccount === undefined ? <><StripeTerminalSettings request={fetchWithAuth} scopeKey={paymentScope} /><StripeAfterSalesSettings request={fetchWithAuth} scopeKey={paymentScope} expectedAccountId={Number(kioskAccount.tenantAccountId || kioskAccount.parentAccountId || kioskAccount.id)} /><UberDirectSettings request={fetchWithAuth} scopeKey={paymentScope} /></> : null}</>
        : <p role="alert" className="panel-empty">La gestion des paiements est réservée à l’administrateur de l’établissement.</p>)}

      {activeTab === 'Imprimantes' && String(kioskAccount?.role || '').toLowerCase() === 'admin' && <WorkstationManager />}
      {activeTab === 'Imprimantes' && <WorkstationSettings canBind={String(kioskAccount?.role || '').toLowerCase() === 'admin'} />}
      {activeTab === 'Imprimantes' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Configuration</span>
              <h2>{editingPrinter ? 'Modifier une imprimante' : 'Ajouter une imprimante'}</h2>
            </div>
            <form className="admin-form" onSubmit={handlePrinterSubmit}>
              <label className="admin-field">
                <span>Protocole d’impression</span>
                <select
                  value={printerFormData.connectionMode || 'epson_epos'}
                  onChange={(event) =>
                    setPrinterFormData((prev) => ({ ...prev, connectionMode: event.target.value }))
                  }
                >
                  <option value="epson_epos">Epson ePOS / compatible</option>
                  <option value="escpos_tcp">ESC/POS réseau générique</option>
                  <option value="esp32_bridge">ESP32 Bridge (MQTT USB)</option>
                </select>
              </label>
              <label className="admin-field">
                <span>Type d’imprimante</span>
                <select
                  value={resolvePrinterType(printerFormData.printerType)}
                  onChange={(event) =>
                    setPrinterFormData((prev) => ({ ...prev, printerType: resolvePrinterType(event.target.value) }))
                  }
                >
                  {PRINTER_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Nom</span>
                <input
                  type="text"
                  placeholder="Cuisine, bar, caisse..."
                  value={printerFormData.name}
                  onChange={(event) => setPrinterFormData((prev) => ({ ...prev, name: event.target.value }))}
                  required
                />
              </label>
              <label className="admin-field">
                <span>Adresse IP</span>
                <input
                  type="text"
                  placeholder="192.168.1.80"
                  value={printerFormData.printerIp}
                  onChange={(event) => setPrinterFormData((prev) => ({ ...prev, printerIp: event.target.value }))}
                  required={!isBridgePrinterForm}
                />
              </label>
              <label className="admin-check admin-check-inline">
                <input
                  type="checkbox"
                  checked={!!printerFormData.isMain}
                  onChange={(event) => setPrinterFormData((prev) => ({ ...prev, isMain: event.target.checked }))}
                />
                <span>Imprimante principale (Tickets client & rapport)</span>
              </label>
              {isBridgePrinterForm ? (
                <>
                  <div className="admin-form-grid admin-form-grid-2">
                    <label className="admin-field">
                      <span>Bridge ID ESP32</span>
                      <input
                        type="text"
                        placeholder="B01"
                        value={printerFormData.bridgeId || ''}
                        onChange={(event) =>
                          setPrinterFormData((prev) => ({ ...prev, bridgeId: event.target.value.toUpperCase() }))
                        }
                        required
                      />
                    </label>
                  </div>
                  <div className="admin-form-grid admin-form-grid-2">
                    <label className="admin-field">
                      <span>Broker MQTT</span>
                      <input
                        type="text"
                        placeholder="212.227.212.196"
                        value={printerFormData.bridgeBrokerHost || ''}
                        onChange={(event) =>
                          setPrinterFormData((prev) => ({ ...prev, bridgeBrokerHost: event.target.value }))
                        }
                        required
                      />
                    </label>
                    <label className="admin-field">
                      <span>Port MQTT</span>
                      <input
                        type="number"
                        min="1"
                        value={printerFormData.bridgeBrokerPort || '8883'}
                        onChange={(event) =>
                          setPrinterFormData((prev) => ({ ...prev, bridgeBrokerPort: event.target.value }))
                        }
                        required
                      />
                    </label>
                  </div>
                </>
              ) : null}
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  {editingPrinter ? 'Mettre à jour' : 'Ajouter'}
                </button>
                {editingPrinter && (
                  <button className="admin-button admin-button-secondary" type="button" onClick={resetPrinterForm}>
                    Annuler
                  </button>
                )}
              </div>
            </form>
            <div className="admin-form-group" style={{ marginTop: 18 }}>
              <div>
                <h3>Découverte réseau</h3>
                <p>Recherche automatique des imprimantes thermiques sur le réseau local.</p>
              </div>
              <div className="admin-actions">
                <button className="admin-button admin-button-secondary" type="button" onClick={handlePrinterDiscovery}>
                  {printerDiscoveryLoading ? 'Recherche...' : 'Découvrir les imprimantes'}
                </button>
              </div>
              {printerDiscoveryError ? <p className="admin-error">{printerDiscoveryError}</p> : null}
              {printerDiscoveryResults.length > 0 ? (
                <div className="admin-discovery-list">
                  {printerDiscoveryResults.map((candidate) => (
                    <article className="admin-record" key={`${candidate.host}:${candidate.port}`}>
                      <div className="admin-record-top">
                        <div>
                          <h3>{candidate.host}</h3>
                          <p>Port {candidate.port}</p>
                        </div>
                        <span className="admin-chip">{candidate.protocol === 'escpos_tcp' ? 'ESC/POS' : 'ePOS'}</span>
                      </div>
                      <div className="admin-actions">
                        <button
                          className="admin-button admin-button-secondary"
                          type="button"
                          onClick={() => applyDiscoveredPrinter(candidate)}
                        >
                          Utiliser
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Parc</span>
              <h2>Imprimantes</h2>
            </div>
            <div className="admin-card-list">
              {printers.map((printer) => (
                <article className="admin-record" key={printer.id}>
                    <div className="admin-record-top">
                      <div>
                        <h3>{printer.name}</h3>
                        <p>{(printer.printerIp || printer.networkHost || 'N/A')}:{printer.port}</p>
                        {printer.networkHost ? <p>{printer.networkHost}</p> : null}
                        {printer.connectionMode === 'esp32_bridge' && (printer.bridgeId || printer.bridgeMacAddress) ? (
                          <p>Bridge: {printer.bridgeId || printer.bridgeMacAddress}</p>
                        ) : null}
                      </div>
                    <span className="admin-chip">
                      {printer.connectionMode === 'escpos_tcp'
                        ? 'ESC/POS'
                        : printer.connectionMode === 'esp32_bridge'
                          ? 'ESP32 BRIDGE'
                          : 'ePOS'} · {printer.deviceId}
                    </span>
                    {printer.isMain ? <span className="admin-chip">Principale</span> : null}
                  </div>
                  <div className="admin-actions">
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => startPrinterEdit(printer)}>
                      Modifier
                    </button>
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => handlePrinterTest(printer)}>
                      Ticket test
                    </button>
                    <button className="admin-button admin-button-danger" type="button" onClick={() => handlePrinterDelete(printer.id)}>
                      Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Production</span>
              <h2>Zones de production</h2>
            </div>
            <form className="admin-form" onSubmit={handleProductionZoneSubmit}>
              <div className="admin-form-grid admin-form-grid-3">
                <label className="admin-field">
                  <span>Nom</span>
                  <input
                    type="text"
                    placeholder="Cuisine chaude, Bar, Desserts..."
                    value={productionZoneFormData.name}
                    onChange={(event) =>
                      setProductionZoneFormData((prev) => ({ ...prev, name: event.target.value }))
                    }
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Imprimante liée</span>
                  <select
                    value={productionZoneFormData.printerId}
                    onChange={(event) =>
                      setProductionZoneFormData((prev) => ({ ...prev, printerId: event.target.value }))
                    }
                    required
                  >
                    <option value="">Choisir une imprimante</option>
                    {printers.map((printer) => (
                      <option key={printer.id} value={printer.id}>
                        {printer.name}
                        {printer.isMain ? ' · Principale' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>Nombre de tickets</span>
                  <input
                    type="number"
                    min="1"
                    max="9"
                    value={productionZoneFormData.ticketCopies}
                    onChange={(event) =>
                      setProductionZoneFormData((prev) => ({ ...prev, ticketCopies: event.target.value }))
                    }
                    required
                  />
                </label>
              </div>
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  {editingProductionZone ? 'Mettre à jour' : 'Ajouter la zone'}
                </button>
                {editingProductionZone && (
                  <button
                    className="admin-button admin-button-secondary"
                    type="button"
                    onClick={resetProductionZoneForm}
                  >
                    Annuler
                  </button>
                )}
              </div>
            </form>
            <div className="admin-card-list">
              {productionZones.map((zone) => (
                <article className="admin-record" key={zone.id}>
                  <div className="admin-record-top">
                    <div>
                      <h3>{zone.name}</h3>
                      <p>{zone.Printer?.name || 'Aucune imprimante'}</p>
                    </div>
                    <span className="admin-chip">x{zone.ticketCopies || 1}</span>
                  </div>
                  <div className="admin-summary-list" style={{ marginBottom: 10 }}>
                    <div className="admin-summary-row">
                      <span>Lien écran zone</span>
                      <strong>{`${window.location.origin}/cuisine?zone=${zone.id}`}</strong>
                    </div>
                    <div className="admin-actions">
                      <button
                        className="admin-button admin-button-secondary"
                        type="button"
                        onClick={() => {
                          const url = `${window.location.origin}/cuisine?zone=${zone.id}`;
                          if (navigator?.clipboard?.writeText) {
                            navigator.clipboard.writeText(url).catch(() => null);
                          }
                          window.alert(`Lien copié:\n${url}`);
                        }}
                      >
                        Copier le lien zone
                      </button>
                    </div>
                  </div>
                  <div className="admin-actions">
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => startProductionZoneEdit(zone)}>
                      Modifier
                    </button>
                    <button className="admin-button admin-button-danger" type="button" onClick={() => handleProductionZoneDelete(zone.id)}>
                      Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Journalier</span>
              <h2>Récap des commandes du jour</h2>
            </div>
            <div className="admin-split">
              <form className="admin-form" onSubmit={handleDailySettingsSave}>
                <label className="admin-field">
                  <span>Imprimante du récap</span>
                  <select
                    value={printerSettings.dailySummaryPrinterId || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, dailySummaryPrinterId: event.target.value }))
                    }
                  >
                    <option value="">Choisir une imprimante</option>
                    {printers.map((printer) => (
                      <option key={printer.id} value={printer.id}>
                        {printer.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>Heure d’impression automatique</span>
                  <input
                    type="time"
                    value={printerSettings.dailySummaryTime || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, dailySummaryTime: event.target.value }))
                    }
                  />
                </label>
                <div className="admin-actions">
                  <button className="admin-button" type="submit">
                    Enregistrer la planification
                  </button>
                  <button className="admin-button admin-button-secondary" type="button" onClick={handleDailySummaryPrint}>
                    Imprimer le récap du jour
                  </button>
                </div>
              </form>

              {dailySummary && (
                <div className="admin-summary-card">
                  <div className="admin-summary-top">
                    <div>
                      <span className="admin-panel-kicker">Aujourd’hui</span>
                      <h3>{dailySummary.date}</h3>
                    </div>
                    <strong>{dailySummary.totalTtc} €</strong>
                  </div>
                  <div className="admin-summary-stats">
                    <div>
                      <span>Commandes</span>
                      <strong>{dailySummary.ordersCount}</strong>
                    </div>
                    <div>
                      <span>CA TTC</span>
                      <strong>{dailySummary.totalTtc} €</strong>
                    </div>
                    <div>
                      <span>Payé CB</span>
                      <strong>{Number(dailySummary.paidByCardTtc || 0).toFixed(2)} €</strong>
                    </div>
                    <div>
                      <span>Payé espèces</span>
                      <strong>{Number(dailySummary.paidByCashTtc || 0).toFixed(2)} €</strong>
                    </div>
                    <div>
                      <span>Total HT</span>
                      <strong>{Number(dailySummary.totalHt || 0).toFixed(2)} €</strong>
                    </div>
                    <div>
                      <span>Total TVA</span>
                      <strong>{Number(dailySummary.totalTva || 0).toFixed(2)} €</strong>
                    </div>
                  </div>
                  {Array.isArray(dailySummary.vatBreakdown) && dailySummary.vatBreakdown.length > 0 && (
                    <div className="admin-summary-list">
                      {dailySummary.vatBreakdown.map((vatLine) => (
                        <div className="admin-summary-row" key={`vat-${vatLine.rate}`}>
                          <span>TVA {Number(vatLine.rate || 0).toFixed(2)}%</span>
                          <strong>
                            CA {Number(vatLine.totalTtc || 0).toFixed(2)} € · TVA {Number(vatLine.totalTva || 0).toFixed(2)} €
                          </strong>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="admin-summary-list">
                    {ensureArray(dailySummary.items).map((item) => (
                      <div className="admin-summary-row" key={item.name}>
                        <span>{item.name}</span>
                        <strong>{item.quantity} · {Number(item.totalTtc).toFixed(2)} €</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Ticket</span>
              <h2>Options d’impression</h2>
            </div>
            <form className="admin-form" onSubmit={handlePrinterOptionsSave}>
              <div className="admin-form-group">
                <div>
                  <h3>Branding ticket</h3>
                  <p>Logo imprimé en tête de ticket.</p>
                </div>
                <label className="admin-field">
                  <span>Logo en haut du ticket</span>
                  <input type="file" accept="image/*" onChange={handleTicketLogoUpload} />
                  {ticketLogoUploading && <small>Envoi du logo en cours...</small>}
                  {printerSettings.ticketLogoMedia && (
                    <img
                      src={printerSettings.ticketLogoMedia}
                      alt="Logo ticket"
                      style={{ width: 120, height: 120, objectFit: 'contain', marginTop: 8 }}
                    />
                  )}
                </label>
              </div>

              <div className="admin-form-group">
                <div>
                  <h3>Textes d’impression</h3>
                  <p>Messages selon le type de ticket.</p>
                </div>
                <label className="admin-field">
                  <span>Texte ticket pour paiement espèces</span>
                  <input
                    type="text"
                    value={printerSettings.ticketHeaderCashText || printerSettings.ticketHeaderText || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        ticketHeaderCashText: event.target.value,
                      }))
                    }
                    placeholder="Ex: COMMANDE A PAYER"
                  />
                </label>
                <label className="admin-field">
                  <span>Texte ticket pour paiement CB déjà validé</span>
                  <input
                    type="text"
                    value={printerSettings.ticketHeaderCardPaidText || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        ticketHeaderCardPaidText: event.target.value,
                      }))
                    }
                    placeholder="Ex: TICKET CLIENT"
                  />
                </label>
                <label className="admin-field">
                  <span>Texte ticket pour duplicata depuis le panel</span>
                  <input
                    type="text"
                    value={printerSettings.ticketHeaderDuplicateText || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        ticketHeaderDuplicateText: event.target.value,
                      }))
                    }
                    placeholder="Ex: DUPLICATA"
                  />
                </label>
                <label className="admin-field">
                  <span>Taille du texte ticket (1 à 8)</span>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={printerSettings.ticketHeaderTextSize || 4}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        ticketHeaderTextSize: event.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <div className="admin-form-group">
                <div>
                  <h3>Infos restaurant sur ticket</h3>
                  <p>Affichées juste sous le logo (6 lignes max).</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Ligne 1</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine1 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine1: event.target.value }))
                      }
                      placeholder="MAYTHAI"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 2</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine2 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine2: event.target.value }))
                      }
                      placeholder="32 avenue de la République"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 3</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine3 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine3: event.target.value }))
                      }
                      placeholder="75011 Paris"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 4</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine4 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine4: event.target.value }))
                      }
                      placeholder="TVA : ..."
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 5</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine5 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine5: event.target.value }))
                      }
                      placeholder="Téléphone"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 6</span>
                    <input
                      type="text"
                      value={printerSettings.ticketStoreLine6 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketStoreLine6: event.target.value }))
                      }
                      placeholder="Optionnel"
                    />
                  </label>
                </div>
              </div>

              <div className="admin-form-group">
                <div>
                  <h3>QR code de retour</h3>
                  <p>Texte au-dessus et lien à encoder en bas du ticket.</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Texte au-dessus du QR code</span>
                    <input
                      type="text"
                      value={printerSettings.ticketQrText || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketQrText: event.target.value }))
                      }
                      placeholder="Scannez pour commander à nouveau"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Lien du QR code</span>
                    <input
                      type="url"
                      value={printerSettings.ticketQrUrl || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketQrUrl: event.target.value }))
                      }
                      placeholder="https://..."
                    />
                  </label>
                </div>
              </div>

              <div className="admin-form-group">
                <div>
                  <h3>Message de remerciement ticket</h3>
                  <p>Affiché au-dessus du message QR (3 lignes).</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Ligne 1</span>
                    <input
                      type="text"
                      value={printerSettings.ticketFooterLine1 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketFooterLine1: event.target.value }))
                      }
                      placeholder="Merci de votre visite"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 2</span>
                    <input
                      type="text"
                      value={printerSettings.ticketFooterLine2 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketFooterLine2: event.target.value }))
                      }
                      placeholder="À bientôt !"
                    />
                  </label>
                  <label className="admin-field">
                    <span>Ligne 3</span>
                    <input
                      type="text"
                      value={printerSettings.ticketFooterLine3 || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, ticketFooterLine3: event.target.value }))
                      }
                      placeholder="Optionnel"
                    />
                  </label>
                </div>
              </div>
              <div className="admin-form-group">
                <div>
                  <h3>Ticket client</h3>
                  <p>Choisis la zone utilisée pour le ticket client et le nombre de copies à sortir.</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Zone du ticket client</span>
                    <select
                      value={printerSettings.clientTicketZoneId || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          clientTicketZoneId: event.target.value,
                        }))
                      }
                    >
                      <option value="">Choisir une zone</option>
                      {productionZones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                          {zone.Printer?.name ? ` · ${zone.Printer.name}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Nombre de copies client</span>
                    <input
                      type="number"
                      min="1"
                      max="9"
                      value={printerSettings.clientTicketCopies || 1}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          clientTicketCopies: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>
              <div className="admin-form-group">
                <div>
                  <h3>Kitchen Display (écrans cuisine)</h3>
                  <p>Choisis le mode d’affichage et les écrans actifs (1 écran, 2 écrans, ou complet).</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Mode d’écran</span>
                    <select
                      value={printerSettings.kdsScreenMode || 'single'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kdsScreenMode: event.target.value,
                        }))
                      }
                    >
                      <option value="single">Tout sur 1 écran</option>
                      <option value="dual">Workflow sur 2 écrans</option>
                      <option value="full">Complet multi-écrans</option>
                    </select>
                  </label>
                  <label className="admin-field admin-field-inline">
                    <input
                      type="checkbox"
                      checked={printerSettings.kdsSoundEnabled !== false}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kdsSoundEnabled: event.target.checked,
                        }))
                      }
                    />
                    <span>Son à l’arrivée d’une nouvelle commande</span>
                  </label>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  {[
                    { key: 'incoming', label: 'Écran nouvelles commandes' },
                    { key: 'prep', label: 'Écran préparation' },
                    { key: 'ready', label: 'Écran prêt' },
                    { key: 'assembly', label: 'Écran assemblage' },
                    { key: 'dispatch', label: 'Écran dispatch / remise' },
                  ].map((entry) => (
                    <label key={entry.key} className="admin-field admin-field-inline">
                      <input
                        type="checkbox"
                        checked={!!printerSettings.kdsEnabledScreensJson?.[entry.key]}
                        onChange={(event) =>
                          setPrinterSettings((prev) => ({
                            ...prev,
                            kdsEnabledScreensJson: {
                              ...normalizeKdsScreens(prev.kdsEnabledScreensJson),
                              [entry.key]: event.target.checked,
                            },
                          }))
                        }
                      />
                      <span>{entry.label}</span>
                    </label>
                  ))}
                </div>
                <div className="admin-summary-card">
                  <div className="admin-summary-top">
                    <div>
                      <span className="admin-panel-kicker">Raccourcis écrans</span>
                      <h3>Liens automatiques par écran / zone</h3>
                    </div>
                  </div>
                  <div className="admin-summary-list">
                    {[
                      { key: 'incoming', label: 'Nouvelles commandes' },
                      { key: 'prep', label: 'Préparation' },
                      { key: 'ready', label: 'Prêtes' },
                      { key: 'assembly', label: 'Assemblage' },
                      { key: 'dispatch', label: 'Dispatch' },
                    ].map((entry) => {
                      const zoneId = String(printerSettings.kdsScreenZoneMapJson?.[entry.key] || '').trim();
                      const url = `${window.location.origin}/cuisine?screen=${entry.key}${zoneId ? `&zone=${zoneId}` : ''}`;
                      return (
                        <div className="admin-summary-row" key={`kds-url-${entry.key}`}>
                          <span>{entry.label}</span>
                          <strong>{url}</strong>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  {[
                    { key: 'incoming', label: 'Zone ecran nouvelles commandes' },
                    { key: 'prep', label: 'Zone ecran preparation' },
                    { key: 'ready', label: 'Zone ecran pret' },
                    { key: 'assembly', label: 'Zone ecran assemblage' },
                    { key: 'dispatch', label: 'Zone ecran dispatch' },
                  ].map((entry) => (
                    <label key={`zone-${entry.key}`} className="admin-field">
                      <span>{entry.label}</span>
                      <select
                        value={printerSettings.kdsScreenZoneMapJson?.[entry.key] || ''}
                        onChange={(event) =>
                          setPrinterSettings((prev) => ({
                            ...prev,
                            kdsScreenZoneMapJson: {
                              ...normalizeKdsScreenZoneMap(prev.kdsScreenZoneMapJson),
                              [entry.key]: event.target.value,
                            },
                          }))
                        }
                      >
                        <option value="">Toutes zones</option>
                        {productionZones.map((zone) => (
                          <option key={`kds-screen-zone-${entry.key}-${zone.id}`} value={zone.id}>
                            {zone.name}
                            {zone.Printer?.name ? ` · ${zone.Printer.name}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Seuil alerte (orange, en secondes)</span>
                    <input
                      type="number"
                      min="30"
                      max="3600"
                      value={printerSettings.kdsWarnThresholdSeconds ?? 180}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kdsWarnThresholdSeconds: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Seuil retard (rouge, en secondes)</span>
                    <input
                      type="number"
                      min="60"
                      max="7200"
                      value={printerSettings.kdsLateThresholdSeconds ?? 600}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          kdsLateThresholdSeconds: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>
              <button className="admin-button" type="submit">
                Enregistrer les options ticket
              </button>
              <button
                className={`admin-button admin-button-secondary ${ticketPreviewPrinting ? 'is-loading' : ''}`}
                type="button"
                onClick={handleTicketPreviewPrint}
                disabled={ticketPreviewPrinting}
                aria-busy={ticketPreviewPrinting}
              >
                {ticketPreviewPrinting
                  ? renderLoadingButtonContent('Imprimer un ticket test', 'Impression en cours...')
                  : <span>Imprimer un ticket test</span>}
              </button>
            </form>
          </section>

        </div>
      )}

      {activeTab === 'Produits' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Importation</span>
              <h2>Importer un menu Uber Eats</h2>
            </div>
            <form className="admin-form" onSubmit={handleUberImportSubmit}>
              <label className="admin-field">
                <span>URL du restaurant Uber Eats</span>
                <input
                  type="url"
                  value={uberImportFormData.url}
                  onChange={(event) =>
                    setUberImportFormData((prev) => ({ ...prev, url: event.target.value }))
                  }
                  placeholder="https://www.ubereats.com/fr/store/..."
                  required
                />
              </label>
              <p style={{ margin: 0, color: 'var(--kiosk-muted)' }}>
                Le serveur importe les sections, produits, descriptions, prix et photos quand Uber les expose dans la page publique.
              </p>
              <div className="admin-actions">
                <button
                  className={`admin-button ${uberImporting ? 'is-loading' : ''}`}
                  type="submit"
                  disabled={uberImporting}
                  aria-busy={uberImporting}
                >
                  {uberImporting
                    ? renderLoadingButtonContent('Importer le menu', 'Import en cours...')
                    : <span>Importer le menu</span>}
                </button>
              </div>
            </form>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Structure</span>
              <h2>{editingSection ? 'Modifier une catégorie principale' : 'Catégories principales'}</h2>
            </div>
            <form className="admin-form" onSubmit={handleSectionSubmit}>
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Nom</span>
                  <input
                    type="text"
                    placeholder="Nouvelle catégorie"
                    value={sectionFormData.name}
                    onChange={(event) =>
                      setSectionFormData((prev) => ({ ...prev, name: event.target.value }))
                    }
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Emoji</span>
                  <input
                    type="text"
                    placeholder="🍜"
                    value={sectionFormData.emoji}
                    onChange={(event) =>
                      setSectionFormData((prev) => ({ ...prev, emoji: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Ordre d’affichage</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={sectionFormData.displayOrder}
                    onChange={(event) =>
                      setSectionFormData((prev) => ({ ...prev, displayOrder: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Image du bouton catégorie</span>
                  <input type="file" accept="image/*" onChange={handleSectionImageUpload} />
                  {sectionImageUploading ? <small>Envoi de l’image en cours...</small> : null}
                  {sectionFormData.image ? (
                    <div className="admin-media-preview">
                      <img src={sectionFormData.image} alt="Aperçu catégorie" />
                    </div>
                  ) : null}
                </label>
              </div>
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  {editingSection ? 'Mettre à jour la catégorie principale' : 'Ajouter la catégorie principale'}
                </button>
                {editingSection ? (
                  <button className="admin-button admin-button-secondary" type="button" onClick={resetSectionForm}>
                    Annuler
                  </button>
                ) : null}
              </div>
            </form>
            <div className="admin-tag-list admin-tag-list-spaced">
              {sections.map((section) => (
                <span className="admin-chip admin-chip-action" key={section.id}>
                  {section.emoji ? `${section.emoji} ` : ''}
                  {section.name}
                  {` · ordre ${section.displayOrder ?? 0}`}
                  <button type="button" onClick={() => startSectionEdit(section)} title="Modifier">
                    ✎
                  </button>
                </span>
              ))}
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Sous-catégories</span>
              <h2>{editingSubcategory ? 'Modifier une sous-catégorie' : 'Ajouter une sous-catégorie'}</h2>
            </div>
            <form className="admin-form" onSubmit={handleSubcategorySubmit}>
              <label className="admin-field">
                <span>Nom</span>
                <input
                  type="text"
                  placeholder="Salade, Bobun, Sandwich"
                  value={subcategoryFormData.name}
                  onChange={(event) =>
                    setSubcategoryFormData((prev) => ({ ...prev, name: event.target.value }))
                  }
                  required
                />
              </label>
              <label className="admin-field">
                <span>Catégorie principale</span>
                <select
                  value={subcategoryFormData.sectionId}
                  onChange={(event) =>
                    setSubcategoryFormData((prev) => ({ ...prev, sectionId: event.target.value }))
                  }
                  required
                >
                  <option value="">Assigner à</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.emoji ? `${section.emoji} ` : ''}
                      {section.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Ordre d’affichage</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={subcategoryFormData.displayOrder}
                  onChange={(event) =>
                    setSubcategoryFormData((prev) => ({ ...prev, displayOrder: event.target.value }))
                  }
                />
              </label>
              <label className="admin-field">
                <span>Image bandeau + bouton sous-catégorie</span>
                <input type="file" accept="image/*" onChange={handleSubcategoryImageUpload} />
                {subcategoryImageUploading ? <small>Envoi de l’image en cours...</small> : null}
                {subcategoryFormData.image ? (
                  <div className="admin-media-preview">
                    <img src={subcategoryFormData.image} alt="Aperçu sous-catégorie" />
                  </div>
                ) : null}
              </label>
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  {editingSubcategory ? 'Mettre à jour la sous-catégorie' : 'Ajouter la sous-catégorie'}
                </button>
                {editingSubcategory ? (
                  <button className="admin-button admin-button-secondary" type="button" onClick={resetSubcategoryForm}>
                    Annuler
                  </button>
                ) : null}
              </div>
            </form>
            <div className="admin-tag-list admin-tag-list-spaced">
              {subcategories.map((subcategory) => (
                <span className="admin-chip admin-chip-action" key={subcategory.id}>
                  {subcategory.name}
                  {subcategory.Section?.name ? ` (${subcategory.Section.name})` : ''}
                  {` · ordre ${subcategory.displayOrder ?? 0}`}
                  {` · ${subcategoryProductCounts.get(String(subcategory.id)) || 0} item(s)`}
                  <button type="button" onClick={() => startSubcategoryEdit(subcategory)} title="Modifier">
                    ✎
                  </button>
                  <button type="button" onClick={() => handleSubcategoryDelete(subcategory.id)}>×</button>
                </span>
              ))}
            </div>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Suppléments</span>
              <h2>Ajouter une catégorie de supplément</h2>
            </div>
            <form className="admin-form" onSubmit={handleSupplementCategorySubmit}>
              <label className="admin-field">
                <span>Nom</span>
                <input
                  type="text"
                  placeholder="Crudité, Sauce, Topping"
                  value={supplementCategoryFormData.name}
                  onChange={(event) =>
                    setSupplementCategoryFormData((prev) => ({ ...prev, name: event.target.value }))
                  }
                  required
                />
              </label>
              <button className="admin-button" type="submit">Ajouter la catégorie</button>
            </form>
            <div className="admin-tag-list admin-tag-list-spaced">
              {supplementCategories.map((category) => (
                <span className="admin-chip admin-chip-action" key={category.id}>
                  {category.name}
                  <button type="button" onClick={() => handleSupplementCategoryDelete(category.id)}>×</button>
                </span>
              ))}
            </div>
          </section>

          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Carte</span>
              <h2>Ajouter un produit</h2>
            </div>
            <form className="admin-form" onSubmit={handleProductSubmit}>
              {renderProductFields(productFormData, setProductFormData, availableSubcategories, handleProductImageUpload)}
              <div className="admin-actions">
                <button className="admin-button" type="submit">Ajouter</button>
                <button className="admin-button admin-button-secondary" type="button" onClick={resetProductForm}>
                  Réinitialiser
                </button>
              </div>
            </form>
          </section>

          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Suppléments</span>
              <h2>Ajouter un supplément</h2>
            </div>
            <form className="admin-form" onSubmit={handleSupplementSubmit}>
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Nom</span>
                  <input
                    type="text"
                    placeholder="Extra sauce cacahuète"
                    value={supplementFormData.name}
                    onChange={(event) => setSupplementFormData((prev) => ({ ...prev, name: event.target.value }))}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Prix TTC</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={supplementFormData.price}
                    onChange={(event) => setSupplementFormData((prev) => ({ ...prev, price: event.target.value }))}
                    required
                  />
                </label>
              </div>
              <label className="admin-field">
                <span>Catégorie de supplément</span>
                <select
                  value={supplementFormData.supplementCategoryId}
                  onChange={(event) =>
                    setSupplementFormData((prev) => ({ ...prev, supplementCategoryId: event.target.value }))
                  }
                  required
                >
                  <option value="">Choisir une catégorie</option>
                  {supplementCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                <span>Photo du supplément</span>
                <input type="file" accept="image/*" onChange={handleSupplementImageUpload} />
                {supplementImageUploading && <small>Envoi de l’image en cours...</small>}
                {supplementFormData.image && (
                  <div className="admin-media-preview admin-media-preview-small">
                    <img src={supplementFormData.image} alt="Aperçu supplément" />
                  </div>
                )}
              </label>
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  Ajouter
                </button>
                <button className="admin-button admin-button-secondary" type="button" onClick={resetSupplementForm}>
                  Réinitialiser
                </button>
              </div>
            </form>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Catalogue</span>
              <h2>Liste des produits</h2>
              <div className="admin-actions">
                <label className="admin-select-all">
                  <input
                    ref={productSelectAllRef}
                    type="checkbox"
                    checked={allProductsSelected}
                    onChange={() => toggleAllProductSelections(filteredCatalogProducts)}
                    aria-label="Sélectionner tous les produits"
                  />
                  <span>Tout</span>
                </label>
                <label className="admin-field admin-field-inline-text">
                  <span>Catégorie</span>
                  <select
                    value={productCatalogFilters.sectionId}
                    onChange={(event) =>
                      setProductCatalogFilters((prev) => ({
                        ...prev,
                        sectionId: event.target.value,
                        subcategoryId: '',
                      }))
                    }
                  >
                    <option value="">Toutes</option>
                    {sections.map((section) => (
                      <option key={`catalog-section-${section.id}`} value={section.id}>
                        {section.emoji ? `${section.emoji} ` : ''}{section.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field admin-field-inline-text">
                  <span>Sous-catégorie</span>
                  <select
                    value={productCatalogFilters.subcategoryId}
                    onChange={(event) =>
                      setProductCatalogFilters((prev) => ({
                        ...prev,
                        subcategoryId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Toutes</option>
                    {productCatalogSubcategories.map((subcategory) => (
                      <option key={`catalog-subcategory-${subcategory.id}`} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field admin-field-inline-text">
                  <span>Disponibilité</span>
                  <select
                    value={productCatalogFilters.availability}
                    onChange={(event) =>
                      setProductCatalogFilters((prev) => ({
                        ...prev,
                        availability: event.target.value,
                      }))
                    }
                  >
                    <option value="all">Tous</option>
                    <option value="available">Disponibles</option>
                    <option value="unavailable">Indisponibles</option>
                  </select>
                </label>
                <button
                  className={`admin-button admin-button-available ${productAvailabilityUpdating ? 'is-loading' : ''}`}
                  type="button"
                  onClick={() => handleProductAvailabilityBulk(true, selectedProductIdsForAutoImages)}
                  disabled={productAvailabilityUpdating}
                >
                  {productAvailabilityUpdating
                    ? renderLoadingButtonContent('Mettre dispo', 'Mise à jour...')
                    : <span>Rendre dispo sélection</span>}
                </button>
                <button
                  className={`admin-button admin-button-unavailable ${productAvailabilityUpdating ? 'is-loading' : ''}`}
                  type="button"
                  onClick={() => handleProductAvailabilityBulk(false, selectedProductIdsForAutoImages)}
                  disabled={productAvailabilityUpdating}
                >
                  {productAvailabilityUpdating
                    ? renderLoadingButtonContent('Mettre indispo', 'Mise à jour...')
                    : <span>Rendre indispo sélection</span>}
                </button>
                <label className="admin-field admin-field-inline-text">
                  <span>Instruction IA</span>
                  <input
                    type="text"
                    value={productAutoImageInstruction}
                    onChange={(event) => setProductAutoImageInstruction(event.target.value)}
                    placeholder="Ex: fond transparent, zoomé, plus appétissant"
                  />
                </label>
                <button
                  className={`admin-button admin-button-secondary ${productAutoImageLoading ? 'is-loading' : ''}`}
                  type="button"
                  onClick={() =>
                    handleAutoProductImages({
                      instruction: productAutoImageInstruction,
                    })
                  }
                  disabled={productAutoImageLoading}
                  aria-busy={productAutoImageLoading}
                >
                  {productAutoImageLoading ? (
                    renderLoadingButtonContent('Générer les photos', 'Génération en cours...')
                  ) : (
                    <span>
                      {selectedProductIdsForAutoImages.length > 0
                        ? `Générer les photos (${selectedProductIdsForAutoImages.length})`
                        : 'Générer les photos'}
                    </span>
                  )}
                </button>
                <button
                  className={`admin-button admin-button-secondary ${productAutoImageLoading ? 'is-loading' : ''}`}
                  type="button"
                  onClick={() =>
                    handleAutoProductImages({
                      removeBackground: true,
                      instruction: productAutoImageInstruction,
                    })
                  }
                  disabled={productAutoImageLoading}
                  aria-busy={productAutoImageLoading}
                >
                  {productAutoImageLoading ? (
                    renderLoadingButtonContent('Retirer le fond', 'Traitement en cours...')
                  ) : (
                    <span>Retirer le fond</span>
                  )}
                </button>
              </div>
            </div>
            <div className="admin-card-list">
              {filteredCatalogProducts.length === 0 ? (
                <div className="admin-empty-state">Aucun produit pour ces filtres.</div>
              ) : filteredCatalogProducts.map((product) => (
                <article
                  className={`admin-record ${editingProduct?.id === product.id ? 'is-editing' : ''}`}
                  key={product.id}
                  ref={(node) => {
                    if (node) {
                      productCardRefs.current[product.id] = node;
                    } else {
                      delete productCardRefs.current[product.id];
                    }
                  }}
                >
                  <div className="admin-record-top">
                    <div className="admin-record-top-media admin-record-top-media-with-select">
                      <label className="admin-record-select">
                        <input
                          type="checkbox"
                          checked={selectedProductIdsForAutoImages.some(
                            (productId) => String(productId) === String(product.id)
                          )}
                          onChange={() => toggleProductSelection(product.id)}
                          aria-label={`Sélectionner ${product.name}`}
                        />
                      </label>
                      {product.image ? (
                        <img src={product.image} alt={product.name} className="admin-record-thumb" />
                      ) : null}
                      <div>
                        <h3>{product.name}</h3>
                        <p>
                          {product.Section?.name}
                          {product.Subcategory ? ` / ${product.Subcategory.name}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="admin-inline-price-editor">
                      <label className="admin-inline-price-field" htmlFor={`inline-price-${product.id}`}>
                        <input
                          id={`inline-price-${product.id}`}
                          className="admin-inline-price-input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          value={getProductInlinePriceValue(product)}
                          disabled={productPriceUpdatingId === String(product.id)}
                          onChange={(event) => handleProductInlinePriceChange(product.id, event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleProductInlinePriceSave(product);
                            }
                          }}
                          aria-label={`Prix de ${product.name}`}
                        />
                        <span>€</span>
                      </label>
                      <label className="admin-inline-price-field" htmlFor={`inline-tax-${product.id}`}>
                        <input
                          id={`inline-tax-${product.id}`}
                          className="admin-inline-price-input"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          inputMode="decimal"
                          value={getProductInlineTaxRateValue(product)}
                          disabled={productPriceUpdatingId === String(product.id)}
                          onChange={(event) => handleProductInlineTaxRateChange(product.id, event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleProductInlinePriceSave(product);
                            }
                          }}
                          aria-label={`TVA de ${product.name}`}
                        />
                        <span>% TVA</span>
                      </label>
                      <button
                        className={`admin-button admin-button-secondary admin-inline-price-save ${productPriceUpdatingId === String(product.id) ? 'is-loading' : ''} ${(hasPendingInlinePriceChange(product) || hasPendingInlineTaxRateChange(product)) ? 'is-dirty' : ''}`}
                        type="button"
                        disabled={productPriceUpdatingId === String(product.id)}
                        onClick={() => handleProductInlinePriceSave(product)}
                        aria-busy={productPriceUpdatingId === String(product.id)}
                      >
                        {productPriceUpdatingId === String(product.id)
                          ? renderLoadingButtonContent('Valider', 'Validation...')
                          : <span>Valider</span>}
                      </button>
                    </div>
                  </div>
                  <p className="admin-record-copy">{product.description || 'Pas de description'}</p>
                  {(accountFeatures.caisseEnabled || accountFeatures.borneEnabled) && (
                    <div className="admin-sales-options">
                      {accountFeatures.caisseEnabled && (
                        <>
                          <label className="admin-inline-price-field" htmlFor={`inline-dine-in-price-${product.id}`}>
                            <span>Sur place</span>
                            <input
                              id={`inline-dine-in-price-${product.id}`}
                              className="admin-inline-price-input"
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              placeholder={String(product.price ?? '')}
                              value={getProductInlineDineInPriceValue(product)}
                              disabled={productSalesOptionsUpdatingId === String(product.id)}
                              onChange={(event) => handleProductInlineDineInPriceChange(product.id, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  handleProductSalesOptionsSave(product);
                                }
                              }}
                              aria-label={`Prix sur place de ${product.name}`}
                            />
                            <span>€</span>
                          </label>
                          <label className="admin-check admin-check-inline">
                            <input
                              type="checkbox"
                              checked={product.isCaisseAvailable !== false}
                              disabled={productSalesOptionsUpdatingId === String(product.id)}
                              onChange={(event) => handleProductSalesOptionsSave(product, { isCaisseAvailable: event.target.checked })}
                            />
                            <span>Caisse</span>
                          </label>
                        </>
                      )}
                      {accountFeatures.borneEnabled && (
                        <label className="admin-check admin-check-inline">
                          <input
                            type="checkbox"
                            checked={product.isBorneAvailable !== false}
                            disabled={productSalesOptionsUpdatingId === String(product.id)}
                            onChange={(event) => handleProductSalesOptionsSave(product, { isBorneAvailable: event.target.checked })}
                          />
                          <span>Borne</span>
                        </label>
                      )}
                      {accountFeatures.caisseEnabled && (
                        <button
                          className={`admin-button admin-button-secondary admin-inline-price-save ${productSalesOptionsUpdatingId === String(product.id) ? 'is-loading' : ''}`}
                          type="button"
                          disabled={productSalesOptionsUpdatingId === String(product.id)}
                          onClick={() => handleProductSalesOptionsSave(product)}
                        >
                          {productSalesOptionsUpdatingId === String(product.id)
                            ? renderLoadingButtonContent('Valider caisse', 'Validation...')
                            : <span>Valider caisse</span>}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="admin-record-meta">
                    <span>{isWithinAvailabilityWindow(product) ? 'Disponible' : 'Hors créneau'}</span>
                    <span>{` · TVA: ${Number(product.taxRate ?? 10).toFixed(2)}%`}</span>
                    {formatAvailabilityWindow(product) ? <span>{` · ${formatAvailabilityWindow(product)}`}</span> : null}
                    {product.kitchenName && product.kitchenName !== product.name ? (
                      <span>{` · Cuisine: ${product.kitchenName}`}</span>
                    ) : null}
                    <span>
                      Zone: {product.ProductionZone?.name || 'Aucune'}
                      {product.ProductionZone?.Printer?.name ? ` · ${product.ProductionZone.Printer.name}` : ''}
                    </span>
                    {product.isDealOfDay && <span> · Deal du jour</span>}
                    {PRODUCT_VISUAL_TAGS
                      .filter((tag) => product[tag.key])
                      .map((tag) => (
                        <span key={`${product.id}-${tag.key}`}> · {tag.label}</span>
                      ))}
                    {product.Supplements?.length ? <span> · {product.Supplements.length} supplément(s)</span> : null}
                  </div>
                  <div className="admin-actions">
                    <label className="admin-field admin-field-inline-text">
                      <span>Zone d’impression</span>
                      <select
                        value={getProductInlineZoneValue(product)}
                        disabled={productZoneUpdatingId === String(product.id)}
                        onChange={(event) => handleProductInlineZoneChange(product.id, event.target.value)}
                      >
                        <option value="">Aucune</option>
                        {productionZones.map((zone) => (
                          <option key={`inline-zone-${product.id}-${zone.id}`} value={zone.id}>
                            {zone.name}
                            {zone.Printer?.name ? ` · ${zone.Printer.name}` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className={`admin-button admin-button-secondary ${productZoneUpdatingId === String(product.id) ? 'is-loading' : ''} ${hasPendingInlineZoneChange(product) ? 'is-dirty' : ''}`}
                      type="button"
                      disabled={productZoneUpdatingId === String(product.id)}
                      onClick={() => handleProductInlineZoneSave(product)}
                      aria-busy={productZoneUpdatingId === String(product.id)}
                    >
                      {productZoneUpdatingId === String(product.id)
                        ? renderLoadingButtonContent('Appliquer zone', 'Mise à jour...')
                        : <span>Appliquer zone</span>}
                    </button>
                    <label className="admin-check admin-check-inline admin-check-inline-compact">
                      <input
                        type="checkbox"
                        checked={!!product.isDealOfDay}
                        disabled={productDealUpdatingId === String(product.id)}
                        onChange={(event) => handleProductDealToggle(product, event.target.checked)}
                      />
                      <span>Deal du jour</span>
                    </label>
                    <button
                      className={`admin-button ${isWithinAvailabilityWindow(product) ? 'admin-button-unavailable' : 'admin-button-available'}`}
                      type="button"
                      disabled={productAvailabilityUpdating}
                      onClick={() => handleProductAvailabilityBulk(isWithinAvailabilityWindow(product) ? false : true, [product.id])}
                    >
                      {isWithinAvailabilityWindow(product) ? 'Mettre indispo' : 'Mettre dispo'}
                    </button>
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => startProductEdit(product)}>
                      Modifier
                    </button>
                    <button className="admin-button admin-button-danger" type="button" onClick={() => handleProductDelete(product.id)}>
                      Supprimer
                    </button>
                  </div>
                  {editingProduct?.id === product.id && (
                    <div className="admin-inline-editor admin-inline-editor-compact">
                      <div className="admin-panel-head">
                        <span className="admin-panel-kicker">Edition en cours</span>
                        <h4>Modifier ce produit</h4>
                      </div>
                      <form className="admin-form" onSubmit={handleProductEditSubmit}>
                        {renderProductFields(
                          productEditFormData,
                          setProductEditFormData,
                          availableEditingSubcategories,
                          handleProductEditImageUpload,
                          true
                        )}
                        <div className="admin-field admin-field-compact">
                          <span>Instruction IA photo</span>
                          <textarea
                            placeholder="Ex: détourer le plat, fond transparent, plus zoomé"
                            value={productEditAiInstruction}
                            onChange={(event) => setProductEditAiInstruction(event.target.value)}
                          />
                        </div>
                        <div className="admin-actions">
                          <button
                            className={`admin-button admin-button-secondary ${productAutoImageLoading ? 'is-loading' : ''}`}
                            type="button"
                            onClick={() =>
                              handleAutoProductImages({
                                productIds: [product.id],
                                instruction: productEditAiInstruction,
                              })
                            }
                            disabled={productAutoImageLoading}
                            aria-busy={productAutoImageLoading}
                          >
                            {productAutoImageLoading ? (
                              renderLoadingButtonContent('Générer la photo', 'Génération...')
                            ) : (
                              <span>Générer la photo</span>
                            )}
                          </button>
                          <button
                            className={`admin-button admin-button-secondary ${productAutoImageLoading ? 'is-loading' : ''}`}
                            type="button"
                            onClick={() =>
                              handleAutoProductImages({
                                productIds: [product.id],
                                removeBackground: true,
                                instruction: productEditAiInstruction,
                              })
                            }
                            disabled={productAutoImageLoading}
                            aria-busy={productAutoImageLoading}
                          >
                            {productAutoImageLoading ? (
                              renderLoadingButtonContent('Retirer le fond', 'Traitement...')
                            ) : (
                              <span>Retirer le fond</span>
                            )}
                          </button>
                        </div>
                        <div className="admin-actions">
                          <button className="admin-button" type="submit">
                            Enregistrer
                          </button>
                          <button
                            className="admin-button admin-button-secondary"
                            type="button"
                            onClick={resetProductEditForm}
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Extras</span>
              <h2>Liste des suppléments</h2>
              <div className="admin-actions">
                <label className="admin-select-all">
                  <input
                    ref={supplementSelectAllRef}
                    type="checkbox"
                    checked={allSupplementsSelected}
                    onChange={toggleAllSupplementSelections}
                    aria-label="Sélectionner tous les suppléments"
                  />
                  <span>Tout</span>
                </label>
                <button
                  className={`admin-button admin-button-secondary ${supplementAutoImageLoading ? 'is-loading' : ''}`}
                  type="button"
                  onClick={handleAutoSupplementImages}
                  disabled={supplementAutoImageLoading}
                  aria-busy={supplementAutoImageLoading}
                >
                  {supplementAutoImageLoading ? (
                    renderLoadingButtonContent('Régénérer les photos automatiquement', 'Génération en cours...')
                  ) : (
                    <span>
                      {selectedSupplementCount > 0
                        ? `Générer les photos (${selectedSupplementCount})`
                        : 'Régénérer les photos automatiquement'}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="admin-card-list">
              {supplements.map((supplement) => (
                <article
                  className={`admin-record ${editingSupplement?.id === supplement.id ? 'is-editing' : ''}`}
                  key={supplement.id}
                  ref={(node) => {
                    if (node) {
                      supplementCardRefs.current[supplement.id] = node;
                    } else {
                      delete supplementCardRefs.current[supplement.id];
                    }
                  }}
                >
                  <div className="admin-record-top">
                    <div className="admin-record-top-media admin-record-top-media-with-select">
                      <label className="admin-record-select">
                        <input
                          type="checkbox"
                          checked={selectedSupplementIdsForAutoImages.some(
                            (supplementId) => String(supplementId) === String(supplement.id)
                          )}
                          onChange={() => toggleSupplementSelection(supplement.id)}
                          aria-label={`Sélectionner ${supplement.name}`}
                        />
                      </label>
                      {supplement.image ? (
                        <img src={supplement.image} alt={supplement.name} className="admin-record-thumb" />
                      ) : null}
                      <div>
                        <h3>{supplement.name}</h3>
                        <p>{supplement.SupplementCategory?.name || 'Sans catégorie'}</p>
                      </div>
                    </div>
                    <strong className="admin-price">{Number(supplement.price).toFixed(2)} €</strong>
                  </div>
                  <div className="admin-actions">
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => startSupplementEdit(supplement)}>
                      Modifier
                    </button>
                    <button className="admin-button admin-button-danger" type="button" onClick={() => handleSupplementDelete(supplement.id)}>
                      Supprimer
                    </button>
                  </div>
                  {editingSupplement?.id === supplement.id && (
                    <div className="admin-inline-editor">
                      <div className="admin-panel-head">
                        <span className="admin-panel-kicker">Edition en cours</span>
                        <h4>Modifier ce supplément</h4>
                      </div>
                      <form className="admin-form" onSubmit={handleSupplementEditSubmit}>
                        <div className="admin-form-grid admin-form-grid-2">
                          <label className="admin-field">
                            <span>Nom</span>
                            <input
                              type="text"
                              placeholder="Extra sauce cacahuète"
                              value={supplementEditFormData.name}
                              onChange={(event) =>
                                setSupplementEditFormData((prev) => ({ ...prev, name: event.target.value }))
                              }
                              required
                            />
                          </label>
                          <label className="admin-field">
                            <span>Prix TTC</span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={supplementEditFormData.price}
                              onChange={(event) =>
                                setSupplementEditFormData((prev) => ({ ...prev, price: event.target.value }))
                              }
                              required
                            />
                          </label>
                        </div>
                        <label className="admin-field">
                          <span>Catégorie de supplément</span>
                          <select
                            value={supplementEditFormData.supplementCategoryId}
                            onChange={(event) =>
                              setSupplementEditFormData((prev) => ({
                                ...prev,
                                supplementCategoryId: event.target.value,
                              }))
                            }
                            required
                          >
                            <option value="">Choisir une catégorie</option>
                            {supplementCategories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="admin-field">
                          <span>Photo du supplément</span>
                          <input type="file" accept="image/*" onChange={handleSupplementEditImageUpload} />
                          {supplementEditImageUploading && <small>Envoi de l’image en cours...</small>}
                          {supplementEditFormData.image && (
                            <div className="admin-media-preview admin-media-preview-small">
                              <img src={supplementEditFormData.image} alt="Aperçu supplément" />
                            </div>
                          )}
                        </label>
                        <div className="admin-actions">
                          <button className="admin-button" type="submit">
                            Enregistrer
                          </button>
                          <button
                            className="admin-button admin-button-secondary"
                            type="button"
                            onClick={resetSupplementEditForm}
                          >
                            Annuler
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'Menus' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Composition</span>
              <h2>{editingMenu ? 'Modifier un menu' : 'Ajouter un menu'}</h2>
            </div>
            <form className="admin-form" onSubmit={handleMenuSubmit}>
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Nom</span>
                  <input
                    type="text"
                    placeholder="Nom du menu"
                    value={menuFormData.name}
                    onChange={(event) => setMenuFormData((prev) => ({ ...prev, name: event.target.value }))}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Prix</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Prix"
                    value={menuFormData.price}
                    onChange={(event) => setMenuFormData((prev) => ({ ...prev, price: event.target.value }))}
                    required
                  />
                </label>
              </div>
              <label className="admin-field">
                <span>Description</span>
                <textarea
                  placeholder="Description"
                  value={menuFormData.description}
                  onChange={(event) => setMenuFormData((prev) => ({ ...prev, description: event.target.value }))}
                  required
                />
              </label>
              <label className="admin-field">
                <span>Image du menu</span>
                <input type="file" accept="image/*" onChange={handleMenuImageUpload} />
                {menuImageUploading && <small>Envoi de l’image en cours...</small>}
                {menuFormData.image && (
                  <div className="admin-media-preview">
                    <img src={menuFormData.image} alt="Aperçu menu" />
                  </div>
                )}
              </label>
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Début du créneau</span>
                  <input
                    type="time"
                    value={menuFormData.availableFromTime}
                    onChange={(event) => setMenuFormData((prev) => ({ ...prev, availableFromTime: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  <span>Fin du créneau</span>
                  <input
                    type="time"
                    value={menuFormData.availableToTime}
                    onChange={(event) => setMenuFormData((prev) => ({ ...prev, availableToTime: event.target.value }))}
                  />
                </label>
              </div>
              <div className="admin-field">
                <span>Sections du menu</span>
                <div className="admin-check-grid">
                  {sections.map((section) => (
                    <div className="admin-check-group" key={section.id}>
                      <label className="admin-check">
                        <input
                          type="checkbox"
                          checked={menuFormData.selectedSections.some((item) => String(item.id) === String(section.id))}
                          onChange={(event) =>
                            setMenuFormData((prev) => ({
                              ...prev,
                              selectedSections: event.target.checked
                                ? [
                                    ...prev.selectedSections,
                                    { id: String(section.id), requiredQuantity: '1' },
                                  ]
                                : prev.selectedSections.filter((item) => String(item.id) !== String(section.id)),
                            }))
                          }
                        />
                        <span>
                          {section.emoji ? `${section.emoji} ` : ''}
                          {section.name}
                        </span>
                      </label>
                      {menuFormData.selectedSections.some((item) => String(item.id) === String(section.id)) && (
                        <label className="admin-field" style={{ marginTop: 8 }}>
                          <span>Quantité requise</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={
                              menuFormData.selectedSections.find((item) => String(item.id) === String(section.id))
                                ?.requiredQuantity || '1'
                            }
                            onChange={(event) =>
                              setMenuFormData((prev) => ({
                                ...prev,
                                selectedSections: prev.selectedSections.map((item) =>
                                  String(item.id) === String(section.id)
                                    ? { ...item, requiredQuantity: event.target.value }
                                    : item
                                ),
                              }))
                            }
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="admin-field">
                <span>Produits supplémentaires possibles</span>
                <select
                  value=""
                  onChange={(event) => {
                    if (!event.target.value) {
                      return;
                    }
                    if (menuFormData.selectedProducts.some((item) => String(item.id) === String(event.target.value))) {
                      return;
                    }
                    setMenuFormData((prev) => ({
                      ...prev,
                      selectedProducts: [...prev.selectedProducts, { id: event.target.value, additionalPrice: 0 }],
                    }));
                  }}
                >
                  <option value="">Choisir un produit</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                <div className="admin-inline-list">
                  {menuFormData.selectedProducts.map((item, index) => (
                    <div className="admin-inline-item" key={`${item.id}-${index}`}>
                      <span>{products.find((product) => product.id === parseInt(item.id, 10))?.name}</span>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Prix supp."
                        value={item.additionalPrice || ''}
                        onChange={(event) => {
                          const nextProducts = [...menuFormData.selectedProducts];
                          nextProducts[index].additionalPrice = event.target.value;
                          setMenuFormData((prev) => ({ ...prev, selectedProducts: nextProducts }));
                        }}
                      />
                      <button
                        className="admin-button admin-button-secondary"
                        type="button"
                        onClick={() =>
                          setMenuFormData((prev) => ({
                            ...prev,
                            selectedProducts: prev.selectedProducts.filter((_, itemIndex) => itemIndex !== index),
                          }))
                        }
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="admin-actions">
                <button className="admin-button" type="submit">
                  {editingMenu ? 'Mettre à jour' : 'Ajouter le menu'}
                </button>
                {editingMenu && (
                  <button className="admin-button admin-button-secondary" type="button" onClick={resetMenuForm}>
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Offre</span>
              <h2>Menus enregistrés</h2>
            </div>
            <div className="admin-card-list">
              {menus.map((menu) => (
                <article className="admin-record" key={menu.id}>
                  <div className="admin-record-top">
                    <div className="admin-record-top-media">
                      {menu.image ? (
                        <img src={menu.image} alt={menu.name} className="admin-record-thumb" />
                      ) : null}
                      <div>
                        <h3>{menu.name}</h3>
                        <p>{menu.description}</p>
                      </div>
                    </div>
                    <strong className="admin-price">{menu.price} €</strong>
                  </div>
                  <div className="admin-record-meta">
                    <span>{isWithinAvailabilityWindow(menu) ? 'Disponible' : 'Hors créneau'}</span>
                    {formatAvailabilityWindow(menu) ? <span>{` · ${formatAvailabilityWindow(menu)}`}</span> : null}
                    {menu.Sections?.length ? (
                      <span>
                        {menu.Sections
                          .map((section) => `${section.name} x${getSectionRequiredQuantity(section)}`)
                          .join(' · ')}
                      </span>
                    ) : null}
                    {menu.Products?.length ? <span> · {menu.Products.length} produit(s) optionnel(s)</span> : null}
                  </div>
                  <div className="admin-actions">
                    <button className="admin-button admin-button-secondary" type="button" onClick={() => startMenuEdit(menu)}>
                      Modifier
                    </button>
                    <button className="admin-button admin-button-danger" type="button" onClick={() => handleMenuDelete(menu.id)}>
                      Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'Historique' && (
        <div className="admin-layout">
          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Recherche</span>
              <h2>Filtres</h2>
            </div>
            <div className="admin-form">
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Date de début</span>
                  <input
                    type="date"
                    value={historyFilters.dateFrom}
                    onChange={(event) => setHistoryFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  <span>Date de fin</span>
                  <input
                    type="date"
                    value={historyFilters.dateTo}
                    onChange={(event) => setHistoryFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
                  />
                </label>
              </div>
              <div className="admin-form-grid admin-form-grid-2">
                <label className="admin-field">
                  <span>Produit</span>
                  <select
                    value={historyFilters.productId}
                    onChange={(event) => setHistoryFilters((prev) => ({ ...prev, productId: event.target.value }))}
                  >
                    <option value="">Tous les produits</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>Recherche libre</span>
                  <input
                    type="text"
                    placeholder="Nom produit"
                    value={historyFilters.search}
                    onChange={(event) => setHistoryFilters((prev) => ({ ...prev, search: event.target.value }))}
                  />
                </label>
              </div>
              <button
                className="admin-button"
                type="button"
                onClick={() => {
                  const searchParams = new URLSearchParams();
                  Object.entries(historyFilters).forEach(([key, value]) => value && searchParams.set(key, value));
                  downloadCsv(`${API_URL}/api/orders/export.csv?${searchParams.toString()}`);
                }}
              >
                Export CSV
              </button>
            </div>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Suivi</span>
              <h2>Historique des commandes</h2>
            </div>
            <div className="admin-card-list">
              {orders.map((order) => (
                <article className="admin-record" key={order.id}>
                  <div className="admin-record-top">
                    <div>
                      <h3>Ticket #{order.ticketNumber}</h3>
                      <p>{formatDateTime(order.orderedAt)}</p>
                    </div>
                    <strong className="admin-price">{Number(order.totalTtc).toFixed(2)} €</strong>
                  </div>
                  <div className="admin-history-list">
                    {order.OrderItems.map((item) => (
                      <div className="admin-history-row admin-history-row-stack" key={item.id}>
                        <div className="admin-record-top">
                          <span>{item.name}</span>
                          <strong>{item.quantity} · {Number(item.totalPrice).toFixed(2)} €</strong>
                        </div>
                        {Array.isArray(item.selections) && item.selections.length > 0 && (
                          <div className="admin-history-sublist">
                            {item.selections.map((selection, index) => (
                              <span key={`${item.id}-${index}`}>
                                {selection.kind === 'supplement'
                                  ? `+ ${selection.name}`
                                  : `${selection.sectionName}: ${selection.product?.name}`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'Fidelisation' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Clients</span>
              <h2>Fidelisation</h2>
            </div>
            <BefidAddonEntry brand={brandForLocation(location, window.location.hostname)} returnTo="admin" />
            <form className="admin-form" onSubmit={handlePrinterOptionsSave}>
              <div className="admin-form-group">
                <div>
                  <h3>Paramètres SMS et promos</h3>
                  <p>Messages, validation du compte, émetteur et cadeaux automatiques.</p>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Nom de l’émetteur (3 à 11 caractères)</span>
                    <input
                      type="text"
                      minLength="3"
                      maxLength="11"
                      value={printerSettings.loyaltySmsSenderName || ''}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, loyaltySmsSenderName: event.target.value }))
                      }
                      placeholder="CLONE"
                    />
                  </label>
                </div>
                <label className="admin-field">
                  <span>Message d’ouverture de compte</span>
                  <textarea
                    rows="3"
                    value={printerSettings.loyaltyWelcomeMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltyWelcomeMessage: event.target.value }))
                    }
                    placeholder="Bienvenue..."
                  />
                </label>
                <label className="admin-field admin-field-inline">
                  <input
                    type="checkbox"
                    checked={!!printerSettings.loyaltySendWelcomeSms}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltySendWelcomeSms: event.target.checked }))
                    }
                  />
                  <span>Envoyer automatiquement un SMS à l’ouverture de compte</span>
                </label>
                <label className="admin-field">
                  <span>Message avec lien de validation</span>
                  <textarea
                    rows="3"
                    value={printerSettings.loyaltyValidationMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltyValidationMessage: event.target.value }))
                    }
                    placeholder="Valide ton compte ici : {link}"
                  />
                </label>
                <label className="admin-field">
                  <span>Base du lien de validation (ex: https://ton-domaine.fr)</span>
                  <input
                    type="url"
                    value={printerSettings.loyaltyValidationBaseUrl || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltyValidationBaseUrl: event.target.value }))
                    }
                    placeholder="https://ton-site.fr"
                  />
                </label>
                <label className="admin-field">
                  <span>Message de remerciement</span>
                  <textarea
                    rows="2"
                    value={printerSettings.loyaltyThankYouMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltyThankYouMessage: event.target.value }))
                    }
                    placeholder="Merci pour ta confiance !"
                  />
                  <small>
                    Variables: {'{firstname}'}, {'{lastname}'}, {'{phone}'}, {'{code}'}, {'{welcome_code}'}, {'{signup_code}'}, {'{birthday_code}'}, {'{gift}'}, {'{welcome_gift}'}, {'{signup_gift}'}, {'{cadeau_bienvenue}'}, {'{birthday_gift}'}, {'{cadeau_anniversaire}'}
                  </small>
                </label>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Récompense ouverture - type</span>
                    <select
                      value={printerSettings.loyaltySignupRewardType || 'discount_euro'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, loyaltySignupRewardType: event.target.value }))
                      }
                    >
                      <option value="discount_euro">Remise en euros</option>
                      <option value="discount_percent">Remise en pourcentage</option>
                      <option value="product">Produit offert</option>
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Récompense ouverture - valeur</span>
                    <input
                      type="number"
                      step="0.01"
                      value={printerSettings.loyaltySignupRewardValue ?? 0}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, loyaltySignupRewardValue: event.target.value }))
                      }
                      placeholder="0"
                    />
                  </label>
                </div>
                <label className="admin-field">
                  <span>Produit offert à l’ouverture</span>
                  <select
                    value={printerSettings.loyaltySignupRewardProductId || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltySignupRewardProductId: event.target.value }))
                    }
                  >
                    <option value="">Aucun</option>
                    {products.map((product) => (
                      <option key={`signup-${product.id}`} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Récompense anniversaire - type</span>
                    <select
                      value={printerSettings.loyaltyBirthdayRewardType || 'discount_euro'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, loyaltyBirthdayRewardType: event.target.value }))
                      }
                    >
                      <option value="discount_euro">Remise en euros</option>
                      <option value="discount_percent">Remise en pourcentage</option>
                      <option value="product">Produit offert</option>
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Récompense anniversaire - valeur</span>
                    <input
                      type="number"
                      step="0.01"
                      value={printerSettings.loyaltyBirthdayRewardValue ?? 0}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, loyaltyBirthdayRewardValue: event.target.value }))
                      }
                      placeholder="0"
                    />
                  </label>
                </div>
                <label className="admin-field">
                  <span>Produit offert à l’anniversaire</span>
                  <select
                    value={printerSettings.loyaltyBirthdayRewardProductId || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, loyaltyBirthdayRewardProductId: event.target.value }))
                    }
                  >
                    <option value="">Aucun</option>
                    {products.map((product) => (
                      <option key={`birthday-${product.id}`} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button className="admin-button" type="submit">
                Enregistrer la fidélisation
              </button>
            </form>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Campagne</span>
              <h2>Campagne SMS</h2>
            </div>
            <form
              className="admin-form"
              onSubmit={(event) => {
                event.preventDefault();
                handleCampaignSend();
              }}
            >
              <div className="admin-form-group">
                <div>
                  <h3>Campagne SMS</h3>
                  <p>Cible les clients avec filtres Inclure/Exclure en ET/OU, puis envoie un SMS en masse.</p>
                </div>

                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Logique entre critères</span>
                    <select
                      value={campaignCriteria.joinOperator}
                      onChange={(event) =>
                        setCampaignCriteria((current) => ({
                          ...current,
                          joinOperator: event.target.value === 'or' ? 'or' : 'and',
                        }))
                      }
                    >
                      <option value="and">ET</option>
                      <option value="or">OU</option>
                    </select>
                  </label>
                  <div className="admin-summary-note">
                    <strong>{campaignPreview.targetedCustomers || 0}</strong> compte(s) ciblé(s) / {campaignPreview.totalCustomers || 0}
                  </div>
                </div>

                <div className="admin-card-list">
                  {campaignCriteria.rules.map((rule) => {
                    const isNumeric = CAMPAIGN_NUMERIC_FIELDS.has(rule.field);
                    const isBoolean = !isNumeric;
                    return (
                      <article className="admin-record" key={rule.id}>
                        <div className="admin-form-grid admin-form-grid-4">
                          <label className="admin-field">
                            <span>Mode</span>
                            <select
                              value={rule.mode}
                              onChange={(event) => handleCampaignRuleChange(rule.id, { mode: event.target.value })}
                            >
                              <option value="include">Inclure</option>
                              <option value="exclude">Exclure</option>
                            </select>
                          </label>
                          <label className="admin-field">
                            <span>Critère</span>
                            <select
                              value={rule.field}
                              onChange={(event) => {
                                const nextField = event.target.value;
                                handleCampaignRuleChange(rule.id, {
                                  field: nextField,
                                  operator: CAMPAIGN_NUMERIC_FIELDS.has(nextField) ? 'gte' : 'is_true',
                                  value: CAMPAIGN_NUMERIC_FIELDS.has(nextField) ? 1 : 1,
                                });
                              }}
                            >
                              {CAMPAIGN_RULE_FIELDS.map((field) => (
                                <option key={field.value} value={field.value}>
                                  {field.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="admin-field">
                            <span>Opérateur</span>
                            <select
                              value={rule.operator}
                              onChange={(event) => handleCampaignRuleChange(rule.id, { operator: event.target.value })}
                            >
                              {isNumeric ? (
                                <>
                                  <option value="gte">≥</option>
                                  <option value="gt">&gt;</option>
                                  <option value="lte">≤</option>
                                  <option value="lt">&lt;</option>
                                  <option value="eq">=</option>
                                </>
                              ) : (
                                <>
                                  <option value="is_true">Oui</option>
                                  <option value="is_false">Non</option>
                                </>
                              )}
                            </select>
                          </label>
                          <label className="admin-field">
                            <span>Valeur</span>
                            {isBoolean ? (
                              <select
                                value={rule.value ? '1' : '0'}
                                onChange={(event) => handleCampaignRuleChange(rule.id, { value: event.target.value === '1' ? 1 : 0 })}
                              >
                                <option value="1">Vrai</option>
                                <option value="0">Faux</option>
                              </select>
                            ) : (
                              <input
                                type="number"
                                step="0.01"
                                value={rule.value}
                                onChange={(event) => handleCampaignRuleChange(rule.id, { value: event.target.value })}
                              />
                            )}
                          </label>
                        </div>
                        <div className="admin-summary-actions">
                          <button
                            className="admin-button admin-button-secondary"
                            type="button"
                            onClick={() => handleCampaignRemoveRule(rule.id)}
                          >
                            Supprimer ce critère
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                <div className="admin-summary-actions">
                  <button className="admin-button admin-button-secondary" type="button" onClick={handleCampaignAddRule}>
                    Ajouter un critère
                  </button>
                  <button
                    className={`admin-button admin-button-secondary ${campaignPreviewLoading ? 'is-loading' : ''}`}
                    type="button"
                    onClick={handleCampaignPreview}
                    disabled={campaignPreviewLoading}
                    aria-busy={campaignPreviewLoading}
                  >
                    {campaignPreviewLoading
                      ? renderLoadingButtonContent('Prévisualiser', 'Calcul en cours...')
                      : <span>Prévisualiser le ciblage</span>}
                  </button>
                </div>

                <label className="admin-field">
                  <span>Message SMS (variables: {'{firstname}'}, {'{lastname}'}, {'{phone}'}, {'{code}'})</span>
                  <textarea
                    rows="3"
                    value={campaignMessage}
                    onChange={(event) => setCampaignMessage(event.target.value)}
                    placeholder="Bonjour {firstname}, ton code: {code}"
                  />
                </label>

                <label className="admin-field admin-field-inline">
                  <input
                    type="checkbox"
                    checked={!!campaignReward.enabled}
                    onChange={(event) =>
                      setCampaignReward((current) => ({ ...current, enabled: event.target.checked }))
                    }
                  />
                  <span>Ajouter un cadeau / bon d’achat à la campagne</span>
                </label>

                {campaignReward.enabled && (
                  <>
                    <div className="admin-form-grid admin-form-grid-3">
                      <label className="admin-field">
                        <span>Type d’offre</span>
                        <select
                          value={campaignReward.rewardType}
                          onChange={(event) =>
                            setCampaignReward((current) => ({ ...current, rewardType: event.target.value }))
                          }
                        >
                          <option value="discount_euro">Bon d’achat en euros</option>
                          <option value="discount_percent">Bon d’achat en %</option>
                          <option value="product">Produit offert</option>
                        </select>
                      </label>
                      <label className="admin-field">
                        <span>Libellé offre</span>
                        <input
                          type="text"
                          value={campaignReward.label}
                          onChange={(event) =>
                            setCampaignReward((current) => ({ ...current, label: event.target.value }))
                          }
                          placeholder="Offre campagne SMS"
                        />
                      </label>
                      <label className="admin-field">
                        <span>Expiration (jours)</span>
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={campaignReward.expiresDays}
                          onChange={(event) =>
                            setCampaignReward((current) => ({ ...current, expiresDays: event.target.value }))
                          }
                        />
                      </label>
                    </div>

                    {(campaignReward.rewardType === 'discount_euro' || campaignReward.rewardType === 'discount_percent') && (
                      <label className="admin-field">
                        <span>Valeur du bon</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={campaignReward.rewardValue}
                          onChange={(event) =>
                            setCampaignReward((current) => ({ ...current, rewardValue: event.target.value }))
                          }
                        />
                      </label>
                    )}

                    {campaignReward.rewardType === 'product' && (
                      <>
                        <label className="admin-field">
                          <span>Cible cadeau</span>
                          <select
                            value={campaignReward.targetType}
                            onChange={(event) =>
                              setCampaignReward((current) => ({ ...current, targetType: event.target.value }))
                            }
                          >
                            <option value="product">Produit précis</option>
                            <option value="section">Choix parmi une catégorie</option>
                            <option value="subcategory">Choix parmi une sous-catégorie</option>
                          </select>
                        </label>

                        {campaignReward.targetType === 'product' && (
                          <label className="admin-field">
                            <span>Produit offert</span>
                            <select
                              value={campaignReward.productId}
                              onChange={(event) =>
                                setCampaignReward((current) => ({ ...current, productId: event.target.value }))
                              }
                            >
                              <option value="">Choisir un produit</option>
                              {products.map((product) => (
                                <option key={`campaign-product-${product.id}`} value={product.id}>
                                  {product.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}

                        {campaignReward.targetType === 'section' && (
                          <label className="admin-field">
                            <span>Catégorie</span>
                            <select
                              value={campaignReward.sectionId}
                              onChange={(event) =>
                                setCampaignReward((current) => ({ ...current, sectionId: event.target.value }))
                              }
                            >
                              <option value="">Choisir une catégorie</option>
                              {sections.map((section) => (
                                <option key={`campaign-section-${section.id}`} value={section.id}>
                                  {section.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}

                        {campaignReward.targetType === 'subcategory' && (
                          <label className="admin-field">
                            <span>Sous-catégorie</span>
                            <select
                              value={campaignReward.subcategoryId}
                              onChange={(event) =>
                                setCampaignReward((current) => ({ ...current, subcategoryId: event.target.value }))
                              }
                            >
                              <option value="">Choisir une sous-catégorie</option>
                              {subcategories.map((subcategory) => (
                                <option key={`campaign-subcategory-${subcategory.id}`} value={subcategory.id}>
                                  {subcategory.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>

              <button
                className={`admin-button ${campaignSendLoading ? 'is-loading' : ''}`}
                type="submit"
                disabled={campaignSendLoading}
                aria-busy={campaignSendLoading}
              >
                {campaignSendLoading
                  ? renderLoadingButtonContent('Envoyer la campagne', 'Envoi en cours...')
                  : <span>Envoyer la campagne SMS</span>}
              </button>
            </form>
          </section>

          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Comptes</span>
              <h2>Comptes fidélité</h2>
            </div>
            <div className="admin-form-group">
              <div>
                <h3>Comptes clients</h3>
                <p>Filtre les comptes puis clique un numéro pour ouvrir ses actions au même endroit.</p>
              </div>
              <div className="admin-form-grid admin-form-grid-4">
                <label className="admin-field">
                  <span>Recherche client (téléphone, nom, email)</span>
                  <input
                    type="text"
                    placeholder="06..., prénom, email..."
                    value={customerFilters.search}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, search: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Validation du compte</span>
                  <select
                    value={customerFilters.verified}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, verified: event.target.value }))
                    }
                  >
                    <option value="all">Tous</option>
                    <option value="verified">Comptes validés</option>
                    <option value="unverified">Comptes non validés</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>Profil de commandes</span>
                  <select
                    value={customerFilters.orderProfile}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, orderProfile: event.target.value }))
                    }
                  >
                    <option value="all">Tous</option>
                    <option value="single">Une seule commande</option>
                    <option value="multiple">Plusieurs commandes</option>
                  </select>
                </label>
                <label className="admin-field">
                  <span>État des codes promo</span>
                  <select
                    value={customerFilters.codeStatus}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, codeStatus: event.target.value }))
                    }
                  >
                    <option value="all">Tous</option>
                    <option value="has_unused">Avec code non utilisé</option>
                    <option value="without_unused">Sans code non utilisé</option>
                  </select>
                </label>
              </div>
              <div className="admin-form-grid admin-form-grid-4">
                <label className="admin-field">
                  <span>Nb commandes min</span>
                  <input
                    type="number"
                    min="0"
                    value={customerFilters.minOrders}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, minOrders: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Nb commandes max</span>
                  <input
                    type="number"
                    min="0"
                    value={customerFilters.maxOrders}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, maxOrders: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Dépense min (€)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={customerFilters.minSpent}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, minSpent: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Dépense max (€)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={customerFilters.maxSpent}
                    onChange={(event) =>
                      setCustomerFilters((prev) => ({ ...prev, maxSpent: event.target.value }))
                    }
                  />
                </label>
              </div>
              <div className="admin-summary-actions">
                <div className="admin-summary-note">
                  <strong>{filteredCustomers.length}</strong> compte(s) affiché(s)
                </div>
                <button
                  className="admin-button admin-button-secondary"
                  type="button"
                  onClick={() => {
                    const searchParams = new URLSearchParams();
                    if (customerFilters.search) {
                      searchParams.set('search', customerFilters.search);
                    }
                    downloadCsv(`${API_URL}/api/customers/export.csv?${searchParams.toString()}`);
                  }}
                >
                  Export CSV
                </button>
              </div>

              <div className="admin-card-list">
                {filteredCustomers.length === 0 && (
                  <div className="admin-empty-state">Aucun compte ne correspond aux filtres.</div>
                )}

                {filteredCustomers.map((customer) => {
                  const isSelected = String(selectedCustomer?.id || '') === String(customer.id);
                  const customerDetail = isSelected ? selectedCustomer : null;
                  const promoCodes = Array.isArray(customerDetail?.PromoCodes)
                    ? customerDetail.PromoCodes
                    : [];
                  const recentOrders = Array.isArray(customerDetail?.Orders)
                    ? customerDetail.Orders.slice(0, 5)
                    : [];

                  return (
                    <article className={`admin-record ${isSelected ? 'is-editing' : ''}`} key={customer.id}>
                      <button
                        type="button"
                        className={`admin-record-button ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => handleSelectCustomer(customer.id)}
                      >
                        <div className="admin-record-top">
                          <div>
                            <h3>{customer.phone}</h3>
                            <p>
                              {customer.totalOrders || 0} commande(s) · {Number(customer.totalSpent || 0).toFixed(2)} €
                            </p>
                          </div>
                          <strong className="admin-price">{customer.points || 0} pts</strong>
                        </div>
                        <div className="admin-tag-list admin-tag-list-spaced">
                          <span className="admin-chip">
                            {customer.loyaltyVerifiedAt ? 'Compte validé' : 'Compte non validé'}
                          </span>
                          <span className="admin-chip">Codes non utilisés: {Number(customer.promoCodesUnused || 0)}</span>
                          <span className="admin-chip">Codes utilisés: {Number(customer.promoCodesUsed || 0)}</span>
                        </div>
                      </button>

                      {isSelected && (
                        <div className="admin-inline-editor">
                          <div className="admin-summary-stats">
                            <div>
                              <span>Dernière commande</span>
                              <strong>{customerDetail?.lastOrderAt ? formatDateTime(customerDetail.lastOrderAt) : '-'}</strong>
                            </div>
                            <div>
                              <span>Date d’inscription</span>
                              <strong>{customerDetail?.createdAt ? formatDateTime(customerDetail.createdAt) : '-'}</strong>
                            </div>
                            <div>
                              <span>Compte validé le</span>
                              <strong>{customerDetail?.loyaltyVerifiedAt ? formatDateTime(customerDetail.loyaltyVerifiedAt) : '-'}</strong>
                            </div>
                            <div>
                              <span>Lien validation envoyé</span>
                              <strong>{customerDetail?.loyaltyValidationSentAt ? formatDateTime(customerDetail.loyaltyValidationSentAt) : '-'}</strong>
                            </div>
                          </div>

                          <div className="admin-form-group">
                            <div>
                              <h3>Profil client</h3>
                              <p>Modifie le compte, renvoie un lien de validation, ou supprime ce client.</p>
                            </div>
                            <div className="admin-form-grid admin-form-grid-4">
                              <label className="admin-field">
                                <span>Prénom</span>
                                <input
                                  type="text"
                                  value={customerInlineFormData.firstName}
                                  onChange={(event) =>
                                    setCustomerInlineFormData((prev) => ({ ...prev, firstName: event.target.value }))
                                  }
                                />
                              </label>
                              <label className="admin-field">
                                <span>Nom</span>
                                <input
                                  type="text"
                                  value={customerInlineFormData.lastName}
                                  onChange={(event) =>
                                    setCustomerInlineFormData((prev) => ({ ...prev, lastName: event.target.value }))
                                  }
                                />
                              </label>
                              <label className="admin-field">
                                <span>Date de naissance</span>
                                <input
                                  type="date"
                                  value={customerInlineFormData.birthDate}
                                  onChange={(event) =>
                                    setCustomerInlineFormData((prev) => ({ ...prev, birthDate: event.target.value }))
                                  }
                                />
                              </label>
                              <label className="admin-field">
                                <span>Email</span>
                                <input
                                  type="email"
                                  value={customerInlineFormData.email}
                                  onChange={(event) =>
                                    setCustomerInlineFormData((prev) => ({ ...prev, email: event.target.value }))
                                  }
                                />
                              </label>
                            </div>
                            <div className="admin-actions">
                              <button
                                className="admin-button admin-button-secondary"
                                type="button"
                                onClick={handleSendCustomerValidation}
                              >
                                Renvoyer le lien de validation
                              </button>
                              <button className="admin-button" type="button" onClick={handleCustomerInlineSave}>
                                Enregistrer le compte
                              </button>
                              <button
                                className={`admin-button admin-button-danger ${customerDeleteLoading ? 'is-loading' : ''}`}
                                type="button"
                                onClick={handleCustomerDelete}
                                disabled={customerDeleteLoading}
                                aria-busy={customerDeleteLoading}
                              >
                                {customerDeleteLoading
                                  ? renderLoadingButtonContent('Supprimer le compte', 'Suppression...')
                                  : <span>Supprimer le compte</span>}
                              </button>
                            </div>
                          </div>

                          <div className="admin-form-group">
                            <div>
                              <h3>SMS direct</h3>
                              <p>Envoie un message ponctuel au client sélectionné.</p>
                            </div>
                            <label className="admin-field">
                              <span>Message (variables: {'{firstname}'}, {'{lastname}'}, {'{phone}'})</span>
                              <textarea
                                rows="3"
                                value={customerDirectMessage}
                                onChange={(event) => setCustomerDirectMessage(event.target.value)}
                              />
                            </label>
                            <button
                              className={`admin-button ${customerDirectMessageSending ? 'is-loading' : ''}`}
                              type="button"
                              onClick={handleSendCustomerMessage}
                              disabled={customerDirectMessageSending}
                              aria-busy={customerDirectMessageSending}
                            >
                              {customerDirectMessageSending
                                ? renderLoadingButtonContent('Envoyer le SMS', 'Envoi...')
                                : <span>Envoyer le SMS</span>}
                            </button>
                          </div>

                          <div className="admin-form-group">
                            <div>
                              <h3>Codes promo et cadeaux</h3>
                              <p>Crée un cadeau automatique ou un code manuel pour ce client.</p>
                            </div>
                            <div className="admin-form-grid admin-form-grid-2">
                              <label className="admin-field">
                                <span>Cadeau automatique</span>
                                <select
                                  value={customerPromoSource}
                                  onChange={(event) => setCustomerPromoSource(event.target.value)}
                                >
                                  <option value="signup">Ouverture de compte</option>
                                  <option value="birthday">Anniversaire</option>
                                </select>
                              </label>
                              <button
                                className={`admin-button ${customerPromoLoading ? 'is-loading' : ''}`}
                                type="button"
                                onClick={handleGenerateCustomerPromo}
                                disabled={customerPromoLoading}
                                aria-busy={customerPromoLoading}
                              >
                                {customerPromoLoading
                                  ? renderLoadingButtonContent('Créer le code auto', 'Création...')
                                  : <span>Créer le code auto</span>}
                              </button>
                            </div>
                            <div className="admin-form-grid admin-form-grid-3">
                              <label className="admin-field">
                                <span>Type d’offre</span>
                                <select
                                  value={customerManualPromo.rewardType}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({
                                      ...current,
                                      rewardType: event.target.value,
                                      productId: '',
                                      sectionId: '',
                                      subcategoryId: '',
                                    }))
                                  }
                                >
                                  <option value="discount_euro">Bon d’achat en euros</option>
                                  <option value="discount_percent">Bon d’achat en %</option>
                                  <option value="product">Produit offert</option>
                                  <option value="product_choice">Produit offert au choix</option>
                                </select>
                              </label>
                              <label className="admin-field">
                                <span>Libellé</span>
                                <input
                                  type="text"
                                  value={customerManualPromo.label}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({ ...current, label: event.target.value }))
                                  }
                                />
                              </label>
                              <label className="admin-field">
                                <span>Expiration (jours)</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="365"
                                  value={customerManualPromo.expiresDays}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({
                                      ...current,
                                      expiresDays: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                            </div>

                            {(customerManualPromo.rewardType === 'discount_euro'
                              || customerManualPromo.rewardType === 'discount_percent') && (
                              <label className="admin-field">
                                <span>Valeur du bon</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={customerManualPromo.rewardValue}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({
                                      ...current,
                                      rewardValue: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                            )}

                            {customerManualPromo.rewardType === 'product' && (
                              <label className="admin-field">
                                <span>Produit offert</span>
                                <select
                                  value={customerManualPromo.productId}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({
                                      ...current,
                                      productId: event.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Choisir un produit</option>
                                  {products.map((product) => (
                                    <option key={`manual-product-${product.id}`} value={product.id}>
                                      {product.name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}

                            {customerManualPromo.rewardType === 'product_choice' && (
                              <div className="admin-form-grid admin-form-grid-2">
                                <label className="admin-field">
                                  <span>Choix limité à une catégorie</span>
                                  <select
                                    value={customerManualPromo.sectionId}
                                    onChange={(event) =>
                                      setCustomerManualPromo((current) => ({
                                        ...current,
                                        sectionId: event.target.value,
                                        subcategoryId: '',
                                      }))
                                    }
                                  >
                                    <option value="">Aucune</option>
                                    {sections.map((section) => (
                                      <option key={`manual-section-${section.id}`} value={section.id}>
                                        {section.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                                <label className="admin-field">
                                  <span>Ou limitée à une sous-catégorie</span>
                                  <select
                                    value={customerManualPromo.subcategoryId}
                                    onChange={(event) =>
                                      setCustomerManualPromo((current) => ({
                                        ...current,
                                        sectionId: '',
                                        subcategoryId: event.target.value,
                                      }))
                                    }
                                  >
                                    <option value="">Aucune</option>
                                    {customerManualPromoSubcategories.map((subcategory) => (
                                      <option key={`manual-subcategory-${subcategory.id}`} value={subcategory.id}>
                                        {subcategory.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              </div>
                            )}

                            <label className="admin-field admin-field-inline">
                              <input
                                type="checkbox"
                                checked={!!customerManualPromo.sendSms}
                                onChange={(event) =>
                                  setCustomerManualPromo((current) => ({
                                    ...current,
                                    sendSms: event.target.checked,
                                  }))
                                }
                              />
                              <span>Envoyer le code promo par SMS automatiquement</span>
                            </label>
                            {customerManualPromo.sendSms && (
                              <label className="admin-field">
                                <span>Message SMS (variable {'{code}'})</span>
                                <textarea
                                  rows="2"
                                  value={customerManualPromo.message}
                                  onChange={(event) =>
                                    setCustomerManualPromo((current) => ({
                                      ...current,
                                      message: event.target.value,
                                    }))
                                  }
                                />
                              </label>
                            )}

                            <button
                              className={`admin-button ${customerManualPromoLoading ? 'is-loading' : ''}`}
                              type="button"
                              onClick={handleCreateCustomerManualPromo}
                              disabled={customerManualPromoLoading}
                              aria-busy={customerManualPromoLoading}
                            >
                              {customerManualPromoLoading
                                ? renderLoadingButtonContent('Créer un code personnalisé', 'Création...')
                                : <span>Créer un code personnalisé</span>}
                            </button>
                          </div>

                          <div className="admin-form-group">
                            <div>
                              <h3>Codes associés au compte</h3>
                              <p>Affiche les codes et leur statut d’utilisation.</p>
                            </div>
                            {!Array.isArray(customerDetail?.PromoCodes) && (
                              <div className="admin-empty-state">Chargement des codes...</div>
                            )}
                            {Array.isArray(customerDetail?.PromoCodes) && customerDetail.PromoCodes.length === 0 && (
                              <div className="admin-empty-state">Aucun code promo associé.</div>
                            )}
                            {promoCodes.length > 0 && (
                              <div className="admin-code-list">
                                {promoCodes.map((promoCode) => (
                                  <div className="admin-code-row" key={promoCode.id}>
                                    <div className="admin-code-row-head">
                                      <strong>{promoCode.code}</strong>
                                      <span className={`admin-chip ${promoCode.usedAt ? 'admin-chip-used' : 'admin-chip-unused'}`}>
                                        {promoCode.usedAt ? `Utilisé le ${formatDateTime(promoCode.usedAt)}` : 'Non utilisé'}
                                      </span>
                                    </div>
                                    <div className="admin-history-sublist">
                                      <span>{formatPromoReward(promoCode)}</span>
                                      {promoCode.expiresAt
                                        ? <span>Expire le {formatDateTime(promoCode.expiresAt)}</span>
                                        : <span>Sans expiration</span>}
                                      {promoCode.Order ? <span>Utilisé sur ticket #{promoCode.Order.ticketNumber}</span> : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="admin-form-group">
                            <div>
                              <h3>Dernières commandes du compte</h3>
                              <p>Aperçu des 5 derniers tickets du client.</p>
                            </div>
                            {recentOrders.length === 0 && (
                              <div className="admin-empty-state">Aucune commande enregistrée.</div>
                            )}
                            {recentOrders.length > 0 && (
                              <div className="admin-card-list">
                                {recentOrders.map((order) => (
                                  <article className="admin-record" key={order.id}>
                                    <div className="admin-record-top">
                                      <div>
                                        <h3>Ticket #{order.ticketNumber}</h3>
                                        <p>{formatDateTime(order.orderedAt)}</p>
                                      </div>
                                      <strong className="admin-price">{Number(order.totalTtc || 0).toFixed(2)} €</strong>
                                    </div>
                                    <div className="admin-history-sublist">
                                      <span>{order.paymentStatus === 'paid' ? 'Payée' : 'Paiement en attente'}</span>
                                      {order.loyaltyPointsUsed ? (
                                        <span>
                                          {order.loyaltyPointsUsed} pt(s) utilisés · -{Number(order.loyaltyDiscountAmount || 0).toFixed(2)} €
                                        </span>
                                      ) : null}
                                      {order.loyaltyPointsEarned ? <span>{order.loyaltyPointsEarned} pt(s) gagnés</span> : null}
                                    </div>
                                  </article>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'Chatbot' && (
        <div className="admin-layout admin-chatbot-layout">
          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Assistant client</span>
              <h2>Chatbot QR</h2>
            </div>
            <form className="admin-form" onSubmit={handleDisplaySettingsSave}>
              <div className="admin-form-group">
                <div>
                  <h3>Identité de conversation</h3>
                  <p>Nom, logo et message d’accueil visibles par les clients qui scannent le QR code.</p>
                </div>
                <label className="admin-field">
                  <span>Nom affiché dans la conversation</span>
                  <input
                    type="text"
                    maxLength="80"
                    value={printerSettings.customerChatbotName || 'Assistant menu'}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        customerChatbotName: event.target.value,
                      }))
                    }
                    placeholder="Assistant Maythai"
                  />
                </label>
                <label className="admin-field">
                  <span>Message d’accueil</span>
                  <textarea
                    rows="3"
                    maxLength="500"
                    value={printerSettings.customerChatbotWelcomeMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        customerChatbotWelcomeMessage: event.target.value,
                      }))
                    }
                    placeholder="Bonjour, je peux vous conseiller sur la carte..."
                  />
                </label>
                <label className="admin-field">
                  <span>Logo affiché en haut du chat</span>
                  <input type="file" accept="image/*" onChange={handleCustomerChatbotLogoUpload} />
                  {customerChatbotLogoUploading && <small>Envoi du logo en cours...</small>}
                  <div className="admin-display-logo-preview">
                    <img
                      src={printerSettings.customerChatbotLogoMedia || printerSettings.displayLogoMedia || FALLBACK_BRAND_LOGO_URL}
                      alt="Logo chatbot"
                    />
                  </div>
                </label>
                <label className="admin-field">
                  <span>URL publique à encoder dans le QR code</span>
                  <input type="text" value={customerChatbotUrl || 'Activation MASTER requise'} readOnly />
                </label>
                <div className="admin-field">
                  <span>QR code client</span>
                  <ChatbotQrCard url={customerChatbotUrl} restaurantName={kioskAccount?.tradeName || kioskAccount?.name} />
                </div>
              </div>
              <button className="admin-button" type="submit">
                Enregistrer le chatbot
              </button>
            </form>
          </section>
          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Aperçu</span>
              <h2>Conversation client</h2>
            </div>
            <div className="customer-chat-preview">
              <div className="customer-chat-preview-header">
                <img
                  src={printerSettings.customerChatbotLogoMedia || printerSettings.displayLogoMedia || FALLBACK_BRAND_LOGO_URL}
                  alt=""
                />
                <strong>{printerSettings.customerChatbotName || 'Assistant menu'}</strong>
              </div>
              <div className="customer-chat-preview-body">
                <div className="customer-chat-preview-bubble bot">
                  {printerSettings.customerChatbotWelcomeMessage || 'Bonjour, je peux vous conseiller sur la carte.'}
                </div>
                <div className="customer-chat-preview-bubble user">Je veux un plat sans gluten.</div>
                <div className="customer-chat-preview-bubble bot">
                  Je peux vous orienter vers les produits marqués sans gluten, mais en cas d’allergie confirmez toujours avec l’équipe avant de commander.
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {activeTab === 'Affichage' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Interface borne</span>
              <h2>Affichage</h2>
            </div>
            <form className="admin-form" onSubmit={handleDisplaySettingsSave}>
              <div className="admin-form-group">
                <div>
                  <h3>Messages et timing</h3>
                  <p>Réglages de validation et d’automatisation.</p>
                </div>
                <label className="admin-field">
                  <span>Texte après validation paiement espèces</span>
                  <input
                    type="text"
                    value={printerSettings.checkoutSuccessCashMessage || printerSettings.checkoutSuccessMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        checkoutSuccessCashMessage: event.target.value,
                      }))
                    }
                    placeholder="Merci pour votre commande."
                  />
                </label>
                <label className="admin-field">
                  <span>Texte après validation paiement CB</span>
                  <input
                    type="text"
                    value={printerSettings.checkoutSuccessCardMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        checkoutSuccessCardMessage: event.target.value,
                      }))
                    }
                    placeholder="Merci pour votre commande."
                  />
                </label>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Taille du texte après validation</span>
                    <input
                      type="number"
                      min="24"
                      max="160"
                      value={printerSettings.checkoutSuccessMessageSize || 54}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          checkoutSuccessMessageSize: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Temps d’affichage après validation (secondes)</span>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={printerSettings.checkoutSuccessMessageDurationSeconds || 2}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          checkoutSuccessMessageDurationSeconds: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Minutes avant passage automatique à prête</span>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={printerSettings.autoReadyAfterMinutes ?? 15}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          autoReadyAfterMinutes: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Minutes avant passage automatique à terminée</span>
                    <input
                      type="number"
                      min="0"
                      max="240"
                      value={printerSettings.autoCompleteAfterMinutes ?? 15}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          autoCompleteAfterMinutes: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>
              <div className="admin-form-group">
                <div>
                  <h3>Palette visuelle</h3>
                  <p>Chaque couleur indique la zone exacte de l’interface client.</p>
                </div>
                <div className="admin-form-grid admin-form-grid-3">
                  <label className="admin-field">
                    <span>Fond global (arrière-plan de page)</span>
                    <input
                      type="color"
                      value={printerSettings.uiBackgroundColor || '#f7f0e4'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, uiBackgroundColor: event.target.value }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Panneaux/cartes (produits, modales, panier)</span>
                    <input
                      type="color"
                      value={printerSettings.uiSurfaceColorHex || '#fffaf3'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({
                          ...prev,
                          uiSurfaceColorHex: event.target.value,
                          uiSurfaceColor: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Texte secondaire (descriptions, sous-infos)</span>
                    <input
                      type="color"
                      value={printerSettings.uiMutedColor || '#7d6757'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, uiMutedColor: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div className="admin-form-grid admin-form-grid-3">
                  <label className="admin-field">
                    <span>Primaire (boutons principaux, prix, actions)</span>
                    <input
                      type="color"
                      value={printerSettings.uiPrimaryColor || '#08383a'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, uiPrimaryColor: event.target.value }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Accent (badges, pastilles, barre panier)</span>
                    <input
                      type="color"
                      value={printerSettings.uiAccentColor || '#ccb08c'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, uiAccentColor: event.target.value }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Texte principal (titres, labels, boutons)</span>
                    <input
                      type="color"
                      value={printerSettings.uiTextColor || '#2b180f'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, uiTextColor: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <div className="admin-summary-note">
                  Exemples: couleur accent = fond de la zone panier flottante, primaire = boutons “Ajouter / Valider”, panneaux = fond des cartes produits et du panier.
                </div>
              </div>
              <div className="admin-form-group">
                <div>
                  <h3>Fond de la borne</h3>
                  <p>Active ou coupe le fond média derrière la page de commande. Si aucun média n’est envoyé, la borne reprend le fond de veille.</p>
                </div>
                <label className="admin-field admin-field-inline">
                  <input
                    type="checkbox"
                    checked={!!printerSettings.clientBackgroundEnabled}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        clientBackgroundEnabled: event.target.checked,
                      }))
                    }
                  />
                  <span>Activer un fond dédié à la page borne</span>
                </label>
                <label className="admin-field">
                  <span>Média de fond de la borne</span>
                  <input type="file" accept="image/*,video/*" onChange={handleClientBackgroundUpload} />
                  {clientBackgroundUploading && <small>Envoi du média en cours...</small>}
                  {printerSettings.clientBackgroundMedia ? (
                    <div style={{ marginTop: 8 }}>
                      {printerSettings.clientBackgroundType === 'video' ? (
                        <video
                          src={printerSettings.clientBackgroundMedia}
                          muted
                          loop
                          playsInline
                          autoPlay
                          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 16 }}
                        />
                      ) : (
                        <img
                          src={printerSettings.clientBackgroundMedia}
                          alt="Fond borne"
                          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 16 }}
                        />
                      )}
                    </div>
                  ) : null}
                </label>
                <label className="admin-field">
                  <span>Logo affiché sur la veille et la borne</span>
                  <input type="file" accept="image/*" onChange={handleDisplayLogoUpload} />
                  {displayLogoUploading && <small>Envoi du logo en cours...</small>}
                  <div className="admin-display-logo-preview">
                    <img
                      src={printerSettings.displayLogoMedia || FALLBACK_BRAND_LOGO_URL}
                      alt="Logo affichage"
                    />
                  </div>
                </label>
              </div>
              <div className="admin-form-grid admin-form-grid-4">
                <label className="admin-field">
                  <span>Taille texte catégories</span>
                  <input
                    type="number"
                    min="14"
                    max="40"
                    value={printerSettings.uiCategoryTextSize || 18}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, uiCategoryTextSize: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Taille nom des produits</span>
                  <input
                    type="number"
                    min="14"
                    max="40"
                    value={printerSettings.uiProductNameTextSize || 18}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        uiProductNameTextSize: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Taille titres</span>
                  <input
                    type="number"
                    min="18"
                    max="56"
                    value={printerSettings.uiHeadingTextSize || 28}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, uiHeadingTextSize: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Taille texte courant</span>
                  <input
                    type="number"
                    min="14"
                    max="32"
                    value={printerSettings.uiBodyTextSize || 17}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, uiBodyTextSize: event.target.value }))
                    }
                  />
                </label>
              </div>
              <button className="admin-button" type="submit">Enregistrer l’affichage</button>
            </form>
          </section>
        </div>
      )}

      {activeTab === 'Ecran fid' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-wide">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Clients</span>
              <h2>Ecran fid</h2>
            </div>
            <BefidAddonEntry brand={brandForLocation(location, window.location.hostname)} returnTo="admin" page="tablet" />
            <form className="admin-form" onSubmit={handlePrinterOptionsSave}>
              <div className="admin-form-group">
                <div>
                  <h3>Flux QR et impression photo</h3>
                  <p>Le scan du QR sur /fid ouvre ce lien puis déclenche la capture sur borne.</p>
                </div>
                <label className="admin-field">
                  <span>Lien après scan (avis / landing)</span>
                  <input
                    type="text"
                    value={printerSettings.fidReviewUrl || 'https://beresto.fr'}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, fidReviewUrl: event.target.value }))
                    }
                    placeholder="https://beresto.fr"
                  />
                </label>
                <label className="admin-field">
                  <span>Imprimante des tickets photo</span>
                  <select
                    value={printerSettings.fidPrinterId || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, fidPrinterId: event.target.value }))
                    }
                  >
                    <option value="">Imprimante principale</option>
                    {printers.map((printer) => (
                      <option key={`fid-printer-${printer.id}`} value={printer.id}>
                        {printer.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="admin-field">
                  <span>Logo sur ticket et page de téléchargement</span>
                  <input type="file" accept="image/*" onChange={handleFidLogoUpload} />
                  {fidLogoUploading && <small>Envoi du logo Ecran fid en cours...</small>}
                  {printerSettings.fidLogoMedia ? (
                    <div className="admin-display-logo-preview">
                      <img src={printerSettings.fidLogoMedia} alt="Logo Ecran fid" />
                    </div>
                  ) : null}
                </label>
                <label className="admin-field">
                  <span>QR principal sur ticket (optionnel)</span>
                  <input
                    type="text"
                    value={printerSettings.fidTicketQrUrl || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, fidTicketQrUrl: event.target.value }))
                    }
                    placeholder="https://beresto.fr"
                  />
                </label>
                <label className="admin-field">
                  <span>Texte du QR téléchargement</span>
                  <input
                    type="text"
                    value={printerSettings.fidDownloadQrText || 'Telecharge ta photo sous 24h ici'}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, fidDownloadQrText: event.target.value }))
                    }
                  />
                </label>
                <div className="admin-summary-note">
                  Interface client: <a href="/fid" target="_blank" rel="noreferrer">ouvrir /fid</a>
                </div>
              </div>
              <button className="admin-button" type="submit">
                Enregistrer Ecran fid
              </button>
            </form>
          </section>
        </div>
      )}

      {activeTab === 'Veille' && (
        <div className="admin-layout">
          <section className="admin-panel admin-panel-form">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Accueil borne</span>
              <h2>Page de veille</h2>
            </div>
            <form className="admin-form" onSubmit={handleAttractSettingsSave}>
              <KioskExperienceSettings settings={printerSettings} products={products} onChange={patch => setPrinterSettings(current => ({ ...current, ...patch }))} />
              <div className="admin-form-group">
                <div>
                  <h3>Message et visuel</h3>
                  <p>Contenu affiché quand la borne est en veille ou fermée.</p>
                </div>
                <label className="admin-field">
                  <span>Message</span>
                  <input
                    type="text"
                    value={printerSettings.attractMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, attractMessage: event.target.value }))
                    }
                    placeholder="CLIQUER POUR COMMANDER"
                  />
                </label>
                <div className="admin-form-grid admin-form-grid-2">
                  <label className="admin-field">
                    <span>Couleur du texte</span>
                    <input
                      type="color"
                      value={printerSettings.attractTextColor || '#fff8ef'}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, attractTextColor: event.target.value }))
                      }
                    />
                  </label>
                  <label className="admin-field">
                    <span>Taille du texte</span>
                    <input
                      type="number"
                      min="24"
                      max="180"
                      value={printerSettings.attractFontSize || 72}
                      onChange={(event) =>
                        setPrinterSettings((prev) => ({ ...prev, attractFontSize: event.target.value }))
                      }
                    />
                  </label>
                </div>
                <label className="admin-field">
                  <span>Retour à la veille après inactivité (secondes)</span>
                  <input
                    type="number"
                    min="5"
                    max="600"
                    value={printerSettings.inactivityTimeoutSeconds || 20}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({
                        ...prev,
                        inactivityTimeoutSeconds: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="admin-field">
                  <span>Message affiché quand c’est fermé</span>
                  <input
                    type="text"
                    value={printerSettings.closedAttractMessage || ''}
                    onChange={(event) =>
                      setPrinterSettings((prev) => ({ ...prev, closedAttractMessage: event.target.value }))
                    }
                    placeholder="Le restaurant est actuellement fermé."
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <div>
                  <h3>Horaires et fond</h3>
                  <p>Ouverture, coupure et médias de fond.</p>
                </div>
                <div className="admin-form-group">
                  <div>
                    <h3>Planning hebdomadaire</h3>
                    <p>Définis les horaires d’ouverture/fermeture pour chaque jour.</p>
                  </div>
                  {WEEKLY_DAYS.map((day) => {
                    const daySchedule = weeklyScheduleView[day.key] || {
                      enabled: true,
                      openingTime: '',
                      closingTime: '',
                      breakStartTime: '',
                      breakEndTime: '',
                    };
                    const disabled = !daySchedule.enabled;
                    return (
                      <div className="admin-form-grid admin-form-grid-5" key={`weekly-${day.key}`}>
                        <label className="admin-field admin-field-inline">
                          <input
                            type="checkbox"
                            checked={!!daySchedule.enabled}
                            onChange={(event) => updateWeeklyDaySchedule(day.key, { enabled: event.target.checked })}
                          />
                          <span>{day.label}</span>
                        </label>
                        <label className="admin-field">
                          <span>Ouverture</span>
                          <input
                            type="time"
                            value={daySchedule.openingTime || ''}
                            disabled={disabled}
                            onChange={(event) => updateWeeklyDaySchedule(day.key, { openingTime: event.target.value })}
                          />
                        </label>
                        <label className="admin-field">
                          <span>Fermeture</span>
                          <input
                            type="time"
                            value={daySchedule.closingTime || ''}
                            disabled={disabled}
                            onChange={(event) => updateWeeklyDaySchedule(day.key, { closingTime: event.target.value })}
                          />
                        </label>
                        <label className="admin-field">
                          <span>Début coupure</span>
                          <input
                            type="time"
                            value={daySchedule.breakStartTime || ''}
                            disabled={disabled}
                            onChange={(event) => updateWeeklyDaySchedule(day.key, { breakStartTime: event.target.value })}
                          />
                        </label>
                        <label className="admin-field">
                          <span>Fin coupure</span>
                          <input
                            type="time"
                            value={daySchedule.breakEndTime || ''}
                            disabled={disabled}
                            onChange={(event) => updateWeeklyDaySchedule(day.key, { breakEndTime: event.target.value })}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <label className="admin-field">
                  <span>Média de fond</span>
                  <input type="file" accept="image/*,video/*" onChange={handleAttractMediaUpload} />
                  {attractUploading && <small>Envoi du média en cours...</small>}
                </label>
                <label className="admin-field">
                  <span>Média de fond quand le resto est fermé</span>
                  <input type="file" accept="image/*,video/*" onChange={handleClosedAttractMediaUpload} />
                  {attractUploading && <small>Envoi du média fermé en cours...</small>}
                  {printerSettings.closedAttractBackgroundMedia && (
                    <div style={{ marginTop: 8 }}>
                      {printerSettings.closedAttractBackgroundType === 'video' ? (
                        <video
                          src={printerSettings.closedAttractBackgroundMedia}
                          muted
                          loop
                          playsInline
                          autoPlay
                          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 16 }}
                        />
                      ) : (
                        <img
                          src={printerSettings.closedAttractBackgroundMedia}
                          alt="Fond fermé"
                          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 16 }}
                        />
                      )}
                    </div>
                  )}
                </label>
              </div>
              <div className="admin-actions">
                <button className="admin-button" type="submit">Enregistrer la veille</button>
                <a className="admin-button admin-button-secondary admin-link-button" href="/" target="_blank" rel="noreferrer">
                  Ouvrir la page
                </a>
              </div>
            </form>
          </section>

          <section className="admin-panel">
            <div className="admin-panel-head">
              <span className="admin-panel-kicker">Prévisualisation</span>
              <h2>Écran de veille</h2>
            </div>
            <div className="admin-attract-preview">
              {printerSettings.attractBackgroundMedia ? (
                printerSettings.attractBackgroundType === 'video' ? (
                  <video
                    className="admin-attract-media"
                    src={printerSettings.attractBackgroundMedia}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    className="admin-attract-media"
                    src={printerSettings.attractBackgroundMedia}
                    alt="Fond veille"
                  />
                )
              ) : (
                <div className="admin-attract-fallback" />
              )}
              <div className="admin-attract-overlay" />
              <div
                className="admin-attract-message"
                style={{
                  color: printerSettings.attractTextColor || '#fff8ef',
                  fontSize: `${printerSettings.attractFontSize || 72}px`,
                }}
              >
                {printerSettings.attractMessage || 'CLIQUER POUR COMMANDER'}
              </div>
            </div>
          </section>
        </div>
      )}
      {activeTab === 'BEGuest' && <section className="admin-panel"><BemenuSettings allowed={accountFeatures.beguestEnabled === true} fetcher={fetchWithAuth} /></section>}
        </>
      )}
    </div>
  );
}

export default AdminDashboard;
